import { noteBootId, notifyRestoredUnsaved } from '../utils/fieldSave';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { verboseLog } from '../utils/logging';
import { bomBasisFromHeaders, type BomBasis } from './bomBasis';
import {
  connectionIdentityLookup,
  isServiceDropLine,
  normalizeLineType,
  poleIdentityLookup,
} from './lineClassification';

// API URLs - production builds use /api, development uses localhost
// CRA replaces process.env.NODE_ENV with "production" or "development" at build time
const API_BASE_URL = process.env.REACT_APP_ADAPTER_URL || '/api';
const API_FALLBACK_URL = '/api';

/**
 * Get the API base URL for direct fetch calls.
 * Always returns '/api' for production builds.
 */
export function getApiBaseUrl(): string {
  return process.env.REACT_APP_ADAPTER_URL || '/api';
}

// =============================================================================
// AUTH / EMPLOYEE PROFILE TYPES
// =============================================================================

/** HR portal employee profile returned by /auth/login and /auth/me.
 *  All fields optional except employee_id — HR may omit fields, and the
 *  /lookup fallback path returns only name/email/role (no department). */
export interface ReferenceLayerInfo {
  key: string;
  country: string;
  id: string;
  name: string;
  source: string;
  updated_at: string;
  bbox: [number, number, number, number] | null;
  counts: Record<string, number>;
}

export interface AuthEmployeeProfile {
  employee_id?: string;
  name?: string;
  email?: string;
  role?: string;
  department?: string;
  position_title?: string;
}

export interface EffectivePrivilege {
  level: 'A' | 'B' | 'C' | 'D' | 'NONE' | string;
  actions: string[];
  scopeCountries?: string[];
  scopeOrganizations?: string[];
  roleCrudOwners?: string[];
}

export interface AuthSessionResponse {
  user: string;
  mode: 'disabled' | 'simple';
  employee?: AuthEmployeeProfile | null;
  effectivePrivilege?: EffectivePrivilege | null;
  privilegeVersion?: string | null;
}

// =============================================================================
// BOM PRICING PICKER TYPES
// =============================================================================

/** A user-selected pricing strategy for a single BOM part. */
export interface BomPricingOverride {
  /** "average" | "lowest" | "highest" | "source" | "manual" */
  strategy: 'average' | 'lowest' | 'highest' | 'source' | 'manual';
  /** When strategy === "source", the cost record id to use. */
  source_id?: string;
  /** When strategy === "manual", the hand-keyed unit price (USD). */
  manual_usd?: number;
  /** Informational, default USD. */
  currency?: string;
}

/** One row returned from /project/bom/preview-parts. */
export interface BomPreviewPart {
  part_id: string;
  name: string;
  category: string;
  unit: string;
  quantity: number;
  current_strategy?: string;
  current_source_label?: string;
  current_unit_price_usd?: number;
}

/** A single individual source price (PR/invoice). */
export interface BomPartPriceSource {
  id: string;
  price_usd: number;
  label: string;
  date?: string;
  country?: string;
  vendor?: string;
  source_id?: string;
  source_type?: string;
  quantity?: number;
}

/** Per-part menu of price options returned from /project/bom/price-options. */
export interface BomPartPriceOptions {
  part_id: string;
  name: string;
  category: string;
  default_strategy: 'average' | 'catalog' | 'country_price';
  /** Project destination country code (e.g. BJ), when known. */
  country?: string | null;
  /** Country whose invoices produced `stats`; null means all countries. */
  stats_country?: string | null;
  /** Price typed on the catalog part for the project's country. */
  country_price_usd?: number | null;
  datapoints: number;
  stats: {
    average_usd: number | null;
    lowest_usd: number | null;
    highest_usd: number | null;
    median_usd: number | null;
  } | null;
  sources: BomPartPriceSource[];
  catalog_price_usd: number;
  currency: string;
}

/** A single line in the BOM pricing appendix (provenance per part). */
export interface BomPricingAppendixEntry {
  part_id: string;
  name: string;
  quantity: number;
  unit_price_usd: number;
  strategy: string;
  source_label: string;
  datapoints: number;
  is_override: boolean;
  raw_record_id: string | null;
  notes: string | null;
}

export interface UndoRedoInfo {
  can_undo: boolean;
  can_redo: boolean;
  stack_size: number;  // Backward compatibility
  undo_stack_size: number;
  redo_stack_size: number;
  operations: Array<{ operation: string; timestamp: string }>;
  redo_operations: Array<{ operation: string; timestamp: string }>;
}

export interface VisualizationPole {
  coords: [number, number];
  props: Record<string, any>;
}

export interface VisualizationLine {
  p1: [number, number];
  p2: [number, number];
  props: Record<string, any>;
}

export interface VisualizationConnection {
  coords: [number, number];
  props: Record<string, any>;
}

export interface VisualizationGeneration {
  coords: [number, number];
  props: Record<string, any>;
}

export interface VisualizationPayload {
  poles: VisualizationPole[];
  lines: VisualizationLine[];
  connections: VisualizationConnection[];
  generations: VisualizationGeneration[];
  meta: {
    total: { poles: number; lines: number; connections: number; generations: number };
    subnets: string[];
    viewport: { width: number; height: number; zoom: number; panX: number; panY: number };
  };
  styles: Record<string, any>;
  poleVoltages?: Record<string, number>; // Voltage values for poles when viewMode is 'vdrop' (legacy 1ph-equivalent model)
  poleVoltages3ph?: Record<string, number>; // Worst-phase voltage per pole for the real 3-phase scenario
  polePhaseVoltages?: Record<string, { A: number | null; B: number | null; C: number | null }>; // Per-phase voltages (3ph scenario)
  connectionPhases?: Record<string, string>; // Assigned service phase per connection ('A'|'B'|'C'|'ABC')
  vdropCompare?: VdropCompare; // Side-by-side summary of the two scenarios
  dataQuality?: VdropDataQuality; // Corrupt-data screening results (vdrop mode)
}

export interface VdropDataQualityFlag {
  node1: string;
  node2: string;
  lineType: string;
  storedLengthM: number;
  geometricLengthM: number | null;
  reason: 'length_mismatch' | 'length_ceiling' | string;
}

export interface VdropUnsizedLine {
  node1: string;
  node2: string;
}

export interface VdropDataQuality {
  flaggedLines: VdropDataQualityFlag[];
  excludedPoleIds: string[]; // poles masked invalid in both scenario maps
  unsizedLines?: VdropUnsizedLine[]; // LV lines with no assigned conductor (fell back to default)
  unsizedCount?: number;
  missingTransformer?: boolean; // MV+LV network with no transformer feeding LV
  servedConnections?: number; // customers on a dropline or Pole_ID
  totalConnections?: number;
  unservedConnections?: number; // survey points that do not draw current
}

export interface VdropScenarioStats {
  minVoltage: number | null;
  worstPole: string | null;
  polesBelowThreshold: number;
  poleCount: number;
}

export interface VdropCompare {
  threshold: number;
  singlePhase: VdropScenarioStats;
  threePhase: VdropScenarioStats;
  feeders?: Array<{
    root: string;
    current_by_phase: { A: number; B: number; C: number };
    neutral_current: number;
  }>;
}

export interface NetworkData {
  poles: Array<{ geometry: { type: 'Point'; coordinates: [number, number] }; properties: Record<string, any> }>;
  connections: Array<{ geometry: { type: 'Point'; coordinates: [number, number] }; properties: Record<string, any> }>;
  lines: [Array<{ geometry: { type: 'LineString'; coordinates: [number, number][] }; properties: Record<string, any> }>, Array<{ geometry: { type: 'LineString'; coordinates: [number, number][] }; properties: Record<string, any> }>];
  generations: Array<{ geometry: { type: 'Point'; coordinates: [number, number] }; properties: Record<string, any> }>;
  transformers?: Array<{ geometry: { type: 'Point'; coordinates: [number, number] }; properties: Record<string, any> }>;
  ptbs?: Array<{ geometry: { type: 'Point'; coordinates: [number, number] }; properties: Record<string, any> }>;
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
  voltageSettings?: { nominal_lv_voltage: number; nominal_mv_voltage: number; cosphi_by_customer_type?: Record<string, number> } | null;
}

export interface UpdateElementRequest {
  projectId: string;
  elementType: 'pole' | 'connection' | 'line' | 'generation' | 'transformer' | 'ptb';
  id?: string;
  node1?: string;
  node2?: string;
  updates: Record<string, any>;
  viewport?: { width: number; height: number; zoom: number; panX: number; panY: number };
  overrideUnplantedPoles?: boolean;
  plantAdjacentPoles?: boolean;
}

export interface GenerationPhoto {
  filename: string;
  original_name?: string;
  content_type?: string;
  uploaded_at?: number;
  size?: number;
  caption?: string | null;
}

// Construction Gantt types
export interface GanttTask {
  id: string;
  label: string;
  phase?: string;
  start: number;
  end: number;
  drivers?: string[];      // measured progress drivers; empty/absent = manual
  predict_wp?: string[];   // uGridPREDICT work-package ids (WP1-WP8)
  progress?: number | null; // 0-100 manual override; null/absent = auto from drivers
  hidden?: boolean;
  sort?: number;
  predecessors?: string[]; // finish-to-start dependency links (task ids)
  owner?: string;          // responsible party / "Direction"
  notes?: string;          // free-text annotation (deliverable, evidence…)
  wbs?: string;            // WBS code label (e.g. "7.6.10")
  critical?: boolean;      // computed server-side: on the dependency critical path
  design_links?: string[]; // semantic design-tool tags attached server-side by label
                           // rules: "8760" | "sizing" | "layout" | "map" | "bom"
}

export interface GanttPhase {
  id: string;
  label: string;
  start: number;
  end: number;
  tasks: GanttTask[];
}

export interface GanttMilestone {
  id: string;
  label: string;
  week: number;
  phase?: string;        // owning phase label — diamond renders on that WP row
}

export interface GanttSchedule {
  name: string;
  duration_weeks: number;
  content_weeks?: number; // duration extended to fit all bars/milestones
  start_date: string | null;
  phases: GanttPhase[];
  milestones: GanttMilestone[];
  drivers_available?: string[];
  predict_work_packages?: string[];
}

export interface GanttDriver {
  installed: number;
  total: number;
  pct: number;
  source?: string;  // variant registry key that satisfied an existence driver
}

export interface GanttResponse {
  project: string;
  name: string;
  schedule: GanttSchedule;
  progress: Record<string, GanttDriver>;
  latest_version: string | null;
  gensite_ids?: string[];  // generation element ids — gensite driver deep-links
  today_week: number | null;
}

export interface GanttOverrides {
  start_date?: string | null;
  duration_weeks?: number;
  milestones?: GanttMilestone[];
}

export interface GanttSaveResponse {
  success: boolean;
  project: string;
  name: string;
  schedule: GanttSchedule;
  today_week: number | null;
}

export interface GanttImportResult {
  tasks: number;
  phases: number;
  milestones: number;
  links: number;
  dropped_links: number;
  start_date: string | null;
  duration_weeks: number;
}

export interface GanttImportResponse {
  success: boolean;
  import: GanttImportResult;
  schedule: GanttSchedule;
}

// 8760 Load Profile Types (for LoadProfile8760Panel)
export interface Run8760Params {
  simulation_year: number;
  target_tariff: number;
  target_currency: string;
  pe_hh: number;
  pe_sme: number;
  pe_cli: number;
  pe_sch: number;
  pe_chu: number;
  granularity: number;
  allowable_error: number;
  sample_week: number;
  enable_opd_scaling: boolean;
  ave_opd_visits: number;
  staff_onsite: number;
  apply_inflation: boolean;
  inflation_rates: Record<string, number>;
  trend_mode: 'none' | 'data' | 'custom' | 'tenure_transform';
  custom_trend?: any;
  // tenure_transform mode params -- only meaningful when trend_mode === 'tenure_transform'.
  // ``target_tenure_months``: project the load to this tenure month per source.
  // ``out_of_bounds_acknowledged``: lets the engine extrapolate beyond the per-source
  //   fit range; without this, out-of-range sources contribute multiplier=1.0.
  target_tenure_months?: number;
  out_of_bounds_acknowledged?: boolean;
}

export interface Run8760Result {
  run_id: string;
  run_name?: string;
  success: boolean;
  message?: string;
  stats: {
    peak_kw: number;
    annual_kwh: number;
    load_factor: number;
  };
  warnings: string[];
}

export interface Run8760Summary {
  run_id: string;
  run_name?: string;
  timestamp?: string;
  created_at?: string;
  connection_count?: number;
  /** Connections actually simulated, keyed by 8760 profile (HH1, SME, …). */
  counts?: Record<string, number>;
  adjusted_counts?: Record<string, number>;
  /** GIS type codes the engine refused (for example OTHER). */
  unsupported_types?: string[];
  /** Profile -> { cdf source id: weight percent } used for this run. */
  cdf_sources_resolved?: Record<string, Record<string, number>>;
  scenario_adjustments?: Record<string, number>;
  stats?: {
    annual_kwh?: number;
    peak_kw?: number;
    total_kwh?: number;
    total_connections?: number;
  };
  params?: Run8760Params;
  summary?: {
    total_connections: number;
    total_kwh: number;
    peak_kw: number;
  };
}

/** Human-readable auto-name for a run that has no user-given name. */
export function formatRunDescriptor(run: Run8760Summary): string {
  const ts = run.timestamp || run.created_at;
  const date = ts
    ? new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : '';
  const conns = run.connection_count || run.stats?.total_connections || run.summary?.total_connections;
  const kwh = run.stats?.annual_kwh || run.stats?.total_kwh || run.summary?.total_kwh;
  const parts: string[] = [];
  if (date) parts.push(date);
  if (conns) parts.push(`${conns.toLocaleString()} connections`);
  if (kwh) parts.push(kwh >= 1000 ? `${(kwh / 1000).toFixed(1)} MWh/yr` : `${kwh.toFixed(0)} kWh/yr`);
  return parts.length > 0 ? parts.join(' — ') : run.run_id;
}

export interface Run8760Details extends Run8760Summary {
  connections: Array<{
    id: string;
    type: string;
    kwh: number;
    peak_kw: number;
  }>;
  hourly_totals?: number[];
}

export interface CdfSource {
  id: string;
  name: string;
  country?: string;
  region: string;
  building_types: string[];
  is_builtin: boolean;
  /** Free-text describing what the dataset represents (origin, who collected
   *  it, methodology). Surfaced in the source picker tooltip + summary. */
  description?: string;
  /** On-disk filename in `web/adapter/profile_8760/data/` (`.mat` legacy
   *  seed) or `data/cdf_library/` (`.npz` modern). Used by the UI to label
   *  legacy seeds with a "Legacy .mat" chip. */
  file?: string;
  /** Human-readable collection-window string, e.g. "2015-01 to 2017-12".
   *  Falls back to `metadata.raw_data_refresh.refreshed_at` for live sources. */
  collection_period?: string;
  metadata?: {
    data_weight_mb?: number;
    n_records?: number;
    n_customers?: number;
    sample_interval_mins?: number;
    /** Free-text origin hint, e.g. "CC API meter-export", "1PDB
     *  tblmeterdata1 via CC API", "SMP platform smart meters", "NRS". */
    data_source?: string;
    /** Cleaning steps applied to the underlying meter data, e.g. site-outage
     *  removal, stale-zero pruning. Useful for the picker tooltip so users
     *  understand why two sources from the same country can disagree. */
    cleaning_applied?: string;
    /** Tariff context note for sources whose pricing is locked to the
     *  collection period (Hobhouse 1.35 ZAR/kWh @ 2015-2017 etc.). */
    tariff_note?: string;
    base_tariff_zar?: number;
    base_tariff_currency?: string;
    /** Block emitted by the CC-API rebuild pipeline. Presence implies the
     *  source is CC-API-derived rather than a built-in seed. */
    raw_data_refresh?: {
      refreshed_at?: string;
      refresh_kind?: string;
      cc_base_url?: string;
      customer_type?: string;
      sample_interval_mins?: number;
      cleaning_enabled?: boolean;
    };
  };
}

export interface CdfLibrary {
  sources: CdfSource[];
  sources_by_type?: Record<string, CdfSource[]>;
  default_weights: Record<string, Record<string, number>>;
  default_weights_by_country?: Record<string, Record<string, Record<string, number>>>;
  /** Echo of ``country`` query used to build ``default_weights`` (if any). */
  cdf_library_country?: string | null;
  global_settings?: {
    default_inflation_rates?: Record<string, number>;
    exchange_rates?: Record<string, number>;
    supported_currencies?: string[];
  };
}

/** One row from GET /admin/cdf-sources (full registry; tariff fields live under metadata). */
export interface CdfRegistryAdminRow {
  id: string;
  name: string;
  description?: string;
  file?: string;
  country?: string | null;
  region?: string;
  building_types?: string[];
  collection_period?: string;
  is_builtin?: boolean;
  metadata?: Record<string, unknown>;
  verification?: unknown;
}

export interface AdminCdfRegistryResponse {
  sources: CdfRegistryAdminRow[];
  default_weights?: Record<string, Record<string, number>>;
  default_weights_by_country?: Record<string, Record<string, Record<string, number>>>;
  global_settings?: Record<string, unknown>;
  last_updated?: string;
}

export interface YearLoadData {
  hours: number[];
  load_kw: number[];
  temperature?: number[];
}

export interface CustomerTypeInfo {
  code: string;
  name: string;
  profile: string | null;
  supported: boolean;
}

export interface CustomerTypesResponse {
  supported: CustomerTypeInfo[];
  unsupported: CustomerTypeInfo[];
}

/**
 * Canonical 8760 customer-type rows (engine PROFILE_TO_BLDG_TYPE + common GIS/CRM labels).
 * Merged into GET /8760/customer-types so HH/HH1… never disappear if the server list is sparse;
 * used when the request fails entirely.
 */
export const FALLBACK_8760_CUSTOMER_TYPES: CustomerTypesResponse = {
  supported: [
    { code: 'HH', name: 'Household (aggregate)', profile: 'HH', supported: true },
    { code: 'HH1', name: 'Household Type 1', profile: 'HH1', supported: true },
    { code: 'HH2', name: 'Household Type 2', profile: 'HH2', supported: true },
    { code: 'HH3', name: 'Household Type 3', profile: 'HH3', supported: true },
    { code: 'SME', name: 'Small Business', profile: 'SME', supported: true },
    { code: 'COM', name: 'Commercial', profile: 'SME', supported: true },
    { code: 'PUE', name: 'Productive Use', profile: 'PUE', supported: true },
    { code: 'SCP', name: 'Primary School', profile: 'SCP', supported: true },
    { code: 'SCH', name: 'Secondary School', profile: 'SCH', supported: true },
    { code: 'HC', name: 'Health Center', profile: 'CLI', supported: true },
    { code: 'HCF', name: 'Health Care Facility', profile: 'CLI', supported: true },
    { code: 'CLI', name: 'Clinic', profile: 'CLI', supported: true },
    { code: 'REL', name: 'Religious', profile: 'CHU', supported: true },
    { code: 'CHU', name: 'Church', profile: 'CHU', supported: true },
    { code: 'OTH', name: 'Other', profile: 'Other', supported: true },
    { code: 'OTHER', name: 'Other', profile: 'Other', supported: true },
    { code: 'Other', name: 'Other', profile: 'Other', supported: true },
  ],
  unsupported: [
    { code: 'IND', name: 'Industrial', profile: null, supported: false },
    { code: 'GOV', name: 'Government', profile: null, supported: false },
    { code: 'AGR', name: 'Agricultural', profile: null, supported: false },
    { code: 'POL', name: 'Police Station', profile: null, supported: false },
  ],
};

function merge8760CustomerTypesResponse(data: unknown): CustomerTypesResponse {
  const raw = data as { supported?: unknown[]; unsupported?: unknown[] } | null | undefined;
  const sup = Array.isArray(raw?.supported) ? raw.supported : [];
  const unsup = Array.isArray(raw?.unsupported) ? raw.unsupported : [];

  const normalizeRow = (s: any): CustomerTypeInfo => ({
    code: String(s?.code ?? '')
      .trim()
      .toUpperCase(),
    name: (String(s?.name ?? s?.code ?? '').trim() || String(s?.code ?? '')).trim(),
    profile:
      s?.profile != null && String(s.profile).trim() !== ''
        ? String(s.profile).trim().toUpperCase()
        : null,
    supported: s?.supported !== false,
  });

  let supported = sup.map(normalizeRow).filter((r) => r.code);
  const codes = new Set(supported.map((r) => r.code));
  for (const fb of FALLBACK_8760_CUSTOMER_TYPES.supported) {
    if (!codes.has(fb.code)) {
      supported.push({ ...fb });
      codes.add(fb.code);
    }
  }
  if (supported.length === 0) {
    supported = FALLBACK_8760_CUSTOMER_TYPES.supported.map((r) => ({ ...r }));
  }
  const unsupported = unsup.map(normalizeRow).filter((r) => r.code);
  return { supported, unsupported };
}

// Note ledger entry - notes are stored as an array of dated entries
export interface NoteEntry {
  id: string;           // UUID
  timestamp: string;    // ISO 8601 format
  author: string;       // User ID or "system" or "bulk_operation"
  content: string;      // The note text
  edited_at?: string;   // ISO 8601 format, if edited
}

// Design state of a project version (W3). Absent on the server means draft.
export type DesignState = 'draft' | 'frozen' | 'approved' | 'superseded';
export type DesignStateAction = 'freeze' | 'approve' | 'supersede';

export interface DesignStateHistoryEntry {
  state: DesignState;
  action?: DesignStateAction;
  from_state?: DesignState;
  by?: string | null;
  by_name?: string | null;
  at?: string | null;     // ISO 8601 with offset (CAT)
  note?: string | null;
  request_id?: string;
  snapshot_sha256?: string;
  auto?: boolean;
  superseded_by?: string;
}

export interface DesignStateAuditEntry {
  ts: string;
  project_id: string;
  version_id: string;
  action: string;
  from_state?: DesignState | null;
  to_state?: DesignState | null;
  by?: string | null;
  by_name?: string | null;
  note?: string | null;
  outcome: 'applied' | 'refused' | 'failed' | 'tampered';
  status_code: number;
  auto?: boolean;
  error?: { code?: string; message?: string } | null;
}

// Public transformer to allow callers to derive NetworkData while also accessing raw payload styles/meta
export function transformPayloadToNetworkData(payload: VisualizationPayload): NetworkData {
  // Debug: Log payload structure
  if (!payload.lines || !Array.isArray(payload.lines)) {
    console.warn('🔋 TRANSFORM: payload.lines is not an array:', {
      lines: payload.lines,
      linesType: typeof payload.lines,
      isArray: Array.isArray(payload.lines)
    });
  }
  
  // DEBUG: Show backend line info if available
  if ((payload as any).meta?.debug_lines_by_type) {
    console.log('📊 BACKEND DEBUG: lines_by_type from server:', (payload as any).meta.debug_lines_by_type);
  }
  if ((payload as any).meta?.debug_lines_raw) {
    console.log('📊 BACKEND DEBUG: raw line counts:', (payload as any).meta.debug_lines_raw);
  }
  
  // Filter lines: MV/LV go to netLines (lines[0]), Service Drops go to dropLines (lines[1])
  // Topology: pole↔customer lines stay in lines[1] even if Type was mis-tagged LV.
  // Serving-pole Pole_ID is not a customer identity — see lineClassification.ts.
  const connIds = connectionIdentityLookup(payload.connections || []);
  const poleIds = poleIdentityLookup(payload.poles || []);
  const netLines =
    payload.lines?.filter((l) => !isServiceDropLine(l, connIds, poleIds)) || [];
  const dropLines =
    payload.lines?.filter((l) => isServiceDropLine(l, connIds, poleIds)) || [];
  
  console.log('🔋 TRANSFORM: Line filtering:', {
    totalLines: payload.lines?.length || 0,
    netLines: netLines.length,
    dropLines: dropLines.length
  });
  
  // DEBUG: If we have dropLines, log the first one's details
  if (dropLines.length > 0) {
    const sample = dropLines[dropLines.length - 1]; // Last dropline (most recently added)
    console.log('🔋 TRANSFORM: Last dropLine:', {
      node1: sample.props?.node1,
      node2: sample.props?.node2,
      type: sample.props?.type,
      Type: sample.props?.Type,
      p1: sample.p1,
      p2: sample.p2
    });
  }

  const poles = payload.poles.map((p) => ({ geometry: { type: 'Point' as const, coordinates: p.coords }, properties: p.props }));
  const connections = payload.connections.map((c) => ({ geometry: { type: 'Point' as const, coordinates: c.coords }, properties: c.props }));
  const generations = payload.generations.map((g) => ({ geometry: { type: 'Point' as const, coordinates: g.coords }, properties: g.props }));

  const lines0 = netLines.map((l) => {
    // Preserve original type from props, checking both lowercase and uppercase
    const originalType = l.props?.type ?? l.props?.Type ?? l.props?.line_type ?? l.props?.Line_Type;
    const normalized = normalizeLineType(originalType);
    // Default to LV if type cannot be determined (most lines in this network are LV)
    const effectiveType = normalized === 'MV' ? 'MV' : normalized === 'LV' ? 'LV' : (normalized || 'LV');
    // CRITICAL: Always set both 'type' and 'Type' to ensure getLineType can find it
    return {
      geometry: { type: 'LineString' as const, coordinates: [l.p1, l.p2] },
      properties: { 
        ...l.props, 
        type: effectiveType, 
        Type: effectiveType,
        line_type: effectiveType,  // Also set line_type for compatibility
        Line_Type: effectiveType,  // Also set Line_Type for compatibility
        subnetId: (l as any).subnetId 
      },
    };
  });
  const lines1 = dropLines.map((l) => {
    const normalized = normalizeLineType(l.props?.type ?? l.props?.Type) || 'Airdac';
    return {
      geometry: { type: 'LineString' as const, coordinates: [l.p1, l.p2] },
      properties: { ...l.props, type: normalized, Type: normalized, subnetId: (l as any).subnetId },
    };
  });

  // Transform transformers to expected format
  const rawTransformers = (payload as any).transformers || [];
  const transformers = rawTransformers.map((trf: any) => {
    const props = { ...trf.props };
    // Ensure GPS coordinates are in properties for map view
    const gpsX = trf.props?.gpsX || trf.props?.GPS_X;
    const gpsY = trf.props?.gpsY || trf.props?.GPS_Y;
    if (gpsX !== undefined && gpsX !== null) {
      props.GPS_X = gpsX;
      props.gpsX = gpsX;
    }
    if (gpsY !== undefined && gpsY !== null) {
      props.GPS_Y = gpsY;
      props.gpsY = gpsY;
    }
    return {
      geometry: { type: 'Point' as const, coordinates: trf.coords as [number, number] },
      properties: props,
    };
  });
  
  if (transformers.length > 0) {
    console.log('🔌 TRANSFORM: Found', transformers.length, 'transformers in payload');
  }

  // Transform PTBs to expected format
  const rawPtbs = (payload as any).ptbs || [];
  const ptbs = rawPtbs.map((ptb: any) => {
    // Handle both GeoJSON format (from backend) and legacy format
    const coords = ptb.geometry?.coordinates || ptb.coords;
    const props = { ...(ptb.properties || ptb.props || {}) };
    // Map view reads GPS from properties via getCoordinates(); keep both
    // camel and shapefile names so installed PTBs render like poles.
    if (coords && coords.length >= 2) {
      if (props.gpsX == null) props.gpsX = coords[0];
      if (props.gpsY == null) props.gpsY = coords[1];
      if (props.GPS_X == null) props.GPS_X = coords[0];
      if (props.GPS_Y == null) props.GPS_Y = coords[1];
    }
    return {
      geometry: { type: 'Point' as const, coordinates: coords as [number, number] },
      properties: props,
    };
  });
  
  if (ptbs.length > 0) {
    console.log('📦 TRANSFORM: Found', ptbs.length, 'PTBs in payload');
  }

  const allPts: Array<[number, number]> = [
    ...poles.map((p) => p.geometry.coordinates),
    ...connections.map((c) => c.geometry.coordinates),
    ...generations.map((g) => g.geometry.coordinates),
    ...transformers.map((t: any) => t.geometry.coordinates),
    ...ptbs.map((p: any) => p.geometry.coordinates),
    ...lines0.flatMap((l) => l.geometry.coordinates),
    ...lines1.flatMap((l) => l.geometry.coordinates),
  ];
  // Bounds are retained but when using adapter pixel coords, they should be used only for info, not for re-scaling
  const xs = allPts.map((p) => p[0]);
  const ys = allPts.map((p) => p[1]);
  const bounds = {
    minX: Math.min(...xs, 0),
    minY: Math.min(...ys, 0),
    maxX: Math.max(...xs, 1),
    maxY: Math.max(...ys, 1),
  };

  const voltageSettings = (payload as any).meta?.voltage_settings ?? null;
  return { poles, connections, lines: [lines0, lines1], generations, transformers, ptbs, bounds, voltageSettings };
}

export interface CommissioningReport {
  source: 'upload' | 'legacy_link';
  report_id?: string;
  generation_id?: string;
  filename?: string;
  content_type?: string;
  size?: number;
  uploaded_by?: string;
  uploaded_by_name?: string;
  uploaded_at?: string;
  upload_date?: string;
  signed: boolean | null;
  signed_by?: string;
  signed_date?: string;
  notes?: string;
  download_url?: string;
  url?: string;
  /** Digital sign-off (reports uploaded before the feature are 'historical'). */
  sign_off_status?: 'signed' | 'unsigned' | 'historical';
  sign_off_label?: string;
  sign_offs?: CommissioningSignOff[];
}

export interface CommissioningSignOff {
  action: 'uploaded' | 'approved' | 'as_built';
  employee_id: string;
  employee_name?: string;
  employee_email?: string;
  /** Stored in UTC, e.g. 2026-10-14T08:30:05Z */
  signed_at_utc: string;
  /** Server rendering in Africa/Lusaka, e.g. 2026-10-14 10:30 CAT (+02:00) */
  signed_at_display?: string;
  note?: string;
  version_id?: string;
}

export interface CommissioningReportStatus {
  project: string;
  generation_id: string;
  current: CommissioningReport | null;
  history: CommissioningReport[];
  legacy_link: string | null;
  accepted_extensions: string[];
  max_bytes: number;
}

export { bomBasisFromHeaders, describeBomBasis } from './bomBasis';
export type { BomBasis } from './bomBasis';

class ApiService {
  private api: AxiosInstance = axios.create({ baseURL: API_BASE_URL, timeout: 150000, withCredentials: true });
  private backendVersion: string | null = null;
  private versionChecked: boolean = false;

  constructor() {
    // Check backend version on initialization
    this.checkBackendVersion();
  }

  private async checkBackendVersion(): Promise<void> {
    if (this.versionChecked) return;
    this.versionChecked = true;
    
    // First try health check to see if backend is up
    try {
      const healthUrl = `${this.api.defaults.baseURL || API_BASE_URL}/health`;
      await axios.get(healthUrl, { timeout: 3000, withCredentials: true });
      console.log('🔧 BACKEND: Health check passed');
    } catch (healthErr: any) {
      console.warn('🔧 BACKEND: Health check failed, backend may not be running', {
        base_url: this.api.defaults.baseURL || API_BASE_URL,
        error: healthErr?.message
      });
      // Try fallback URL
      try {
        const fallbackHealthUrl = `${API_FALLBACK_URL}/health`;
        await axios.get(fallbackHealthUrl, { timeout: 3000, withCredentials: true });
        this.api = axios.create({ baseURL: API_FALLBACK_URL, timeout: 150000, withCredentials: true });
        console.log('🔧 BACKEND: Fallback health check passed');
      } catch (fallbackHealthErr: any) {
        console.error('🔧 BACKEND: Both primary and fallback health checks failed', {
          primary: this.api.defaults.baseURL || API_BASE_URL,
          fallback: API_FALLBACK_URL,
          error: fallbackHealthErr?.message
        });
        return; // Don't try version if health check fails
      }
    }
    
    try {
      const versionUrl = `${this.api.defaults.baseURL || API_BASE_URL}/version`;
      const response = await axios.get(versionUrl, { timeout: 5000, withCredentials: true });
      const versionInfo = response.data;
      this.backendVersion = versionInfo.backend_version || versionInfo.app_version || 'unknown';
      
      console.log('🔧 BACKEND VERSION:', {
        backend_version: versionInfo.backend_version || versionInfo.app_version || 'unknown',
        app_version: versionInfo.app_version,
        python_version: versionInfo.python_version,
        startup_time: versionInfo.startup_time,
        base_url: this.api.defaults.baseURL || API_BASE_URL
      });
    } catch (err: any) {
      // If version endpoint fails, try fallback URL
      try {
        const fallbackVersionUrl = `${API_FALLBACK_URL}/version`;
        const response = await axios.get(fallbackVersionUrl, { timeout: 5000, withCredentials: true });
        const versionInfo = response.data;
        this.backendVersion = versionInfo.backend_version || versionInfo.app_version || 'unknown';
        this.api = axios.create({ baseURL: API_FALLBACK_URL, timeout: 150000, withCredentials: true });
        
        console.log('🔧 BACKEND VERSION (fallback):', {
          backend_version: versionInfo.backend_version || versionInfo.app_version || 'unknown',
          app_version: versionInfo.app_version,
          python_version: versionInfo.python_version,
          startup_time: versionInfo.startup_time,
          base_url: API_FALLBACK_URL
        });
      } catch (fallbackErr: any) {
        console.warn('🔧 BACKEND VERSION: Could not fetch version info', {
          error: err?.message || fallbackErr?.message,
          base_url: this.api.defaults.baseURL || API_BASE_URL,
          fallback_url: API_FALLBACK_URL
        });
      }
    }
  }

  private async withFallback<T>(fn: (client: AxiosInstance) => Promise<T>): Promise<T> {
    try {
      const res = await fn(this.api);
      noteBootId((res as any)?.headers);
      return res;
    } catch (err: any) {
      noteBootId(err?.response?.headers);
      const denial = err?.response?.status === 403 && typeof err?.response?.data?.detail === 'object'
        ? err.response.data.detail
        : null;
      if (denial?.code === 'privilege_denied') {
        const owners = Array.isArray(denial.role_crud_owners)
          ? denial.role_crud_owners.map(String).join(', ')
          : 'your country HR team or Nexus/IS&T';
        err.message = [
          denial.message,
          denial.required?.action ? `Required action: ${denial.required.action}.` : '',
          `Who can change it: ${owners}.`,
          denial.resolution,
        ].filter(Boolean).join(' ');
      }
      // If network error (no response) or timeout, try fallback URL
      const isNetworkError = !err?.response;
      const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout');
      const currentBaseURL = this.api.defaults.baseURL || API_BASE_URL;
      const isNotFallback = !currentBaseURL.includes(API_FALLBACK_URL);
      
      if ((isNetworkError || isTimeout) && isNotFallback) {
        console.log('🔧 API: Primary URL failed, trying fallback...', {
          error: err?.message,
          code: err?.code,
          primary: this.api.defaults.baseURL || API_BASE_URL,
          fallback: API_FALLBACK_URL
        });
        try {
        const alt = axios.create({ baseURL: API_FALLBACK_URL, timeout: 150000, withCredentials: true });
        const result = await fn(alt);
        this.api = alt; // switch for subsequent calls
          console.log('🔧 API: Fallback URL successful');
        return result;
        } catch (fallbackErr: any) {
          console.error('🔧 API: Fallback URL also failed', {
            error: fallbackErr?.message,
            code: fallbackErr?.code
          });
          throw fallbackErr; // Throw the fallback error
        }
      }
      throw err;
    }
  }

  async loginSimple(
    employeeNumber: string,
    password: string,
  ): Promise<AuthSessionResponse & { status: string }> {
    const { data } = await this.withFallback((client) =>
      client.post('/auth/login', { employeeNumber, password }),
    );
    return data as AuthSessionResponse & { status: string };
  }

  async me(): Promise<AuthSessionResponse | null> {
    try {
      // Reduced timeout to 5 seconds - if backend is up, it should respond quickly
      // If it times out, we'll try fallback or show a clear error
      const { data } = await this.withFallback((client) => client.get('/auth/me', { timeout: 5000 }));
      return data as AuthSessionResponse;
    } catch (err: any) {
      // Handle timeout specifically
      if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
        console.error('🔧 AUTH: Backend timeout - backend may not be running', {
          base_url: this.api.defaults.baseURL || API_BASE_URL,
          fallback_url: API_FALLBACK_URL,
          error: err?.message
        });
        // Try fallback URL if primary timed out
        if ((this.api.defaults.baseURL || API_BASE_URL) !== API_FALLBACK_URL) {
          try {
            console.log('🔧 AUTH: Trying fallback URL...');
            const fallbackClient = axios.create({ baseURL: API_FALLBACK_URL, timeout: 5000, withCredentials: true });
            const { data } = await fallbackClient.get('/auth/me');
            this.api = fallbackClient; // Switch to fallback for future calls
            console.log('🔧 AUTH: Fallback URL successful');
            return data as AuthSessionResponse;
          } catch (fallbackErr: any) {
            console.error('🔧 AUTH: Fallback URL also failed', {
              error: fallbackErr?.message,
              code: fallbackErr?.code
            });
            console.error('🔧 BACKEND NOT RUNNING: To start the backend, run:', {
              command: 'cd web/adapter && ADAPTER_ALLOW_LOCAL_PATHS=1 ADAPTER_AUTH_MODE=disabled python main.py',
              or_use_script: './web/restart-all.sh',
              expected_port: '8017',
              check_status: 'lsof -i :8017'
            });
            // Return null to show login screen instead of throwing
            return null;
          }
        }
        // Return null to show login screen instead of throwing
        return null;
      }
      if (err?.response?.status === 401) return null;
      throw err;
    }
  }

  async logout(): Promise<void> {
    await this.withFallback((client) => client.post('/auth/logout'));
  }

  async uploadProject(files: FileList | File[]): Promise<{ projectId: string }> {
    const form = new FormData();
    const arr = Array.from(files as FileList | File[]);
    arr.forEach((f) => form.append('files', f));
    const { data } = await this.withFallback((client) =>
      client.post('/project/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } })
    );
    return { projectId: (data as any).projectId as string };
  }

  async openLocalProject(path: string): Promise<{ projectId: string }> {
    const { data } = await this.withFallback((client) =>
      client.post('/project/open-local', { path })
    );
    return { projectId: (data as any).projectId as string };
  }

  async getNetworkData(projectId: string, viewport: { width: number; height: number; zoom?: number; panX?: number; panY?: number }): Promise<NetworkData> {
    const params = new URLSearchParams({
      projectId,
      width: String(viewport.width),
      height: String(viewport.height),
      zoom: String(viewport.zoom ?? 1),
      panX: String(viewport.panX ?? 0),
      panY: String(viewport.panY ?? 0),
    });
    const { data } = await this.withFallback((client) =>
      client.get<VisualizationPayload>(`/visualization?${params.toString()}`)
    );
    return this.toNetworkData(data as VisualizationPayload);
  }

  async getVisualization(projectId: string, viewport: { width: number; height: number; zoom?: number; panX?: number; panY?: number }): Promise<NetworkData> {
    const params = new URLSearchParams({
      projectId,
      width: String(viewport.width),
      height: String(viewport.height),
      zoom: String(viewport.zoom ?? 1),
      panX: String(viewport.panX ?? 0),
      panY: String(viewport.panY ?? 0),
    });
    const { data } = await this.withFallback((client) =>
      client.get<VisualizationPayload>(`/visualization?${params.toString()}`)
    );
    return this.toNetworkData(data as VisualizationPayload);
  }

  // Fetch raw adapter payload (includes styles and meta) for parity rendering
  async getVisualizationRaw(projectId: string, viewport: { width: number; height: number; zoom?: number; panX?: number; panY?: number }, bustCache: boolean = false, viewMode: string = 'individual'): Promise<VisualizationPayload> {
    const params = new URLSearchParams({
      projectId,
      width: String(viewport.width),
      height: String(viewport.height),
      zoom: String(viewport.zoom ?? 1),
      panX: String(viewport.panX ?? 0),
      panY: String(viewport.panY ?? 0),
      viewMode: viewMode,
    });
    
    // Add cache buster for post-update refreshes
    if (bustCache) {
      params.append('_t', Date.now().toString());
    }
    
    const vizTimeout = viewMode === 'vdrop' ? 300000 : 150000;
    const { data } = await this.withFallback((client) =>
      client.get<VisualizationPayload>(`/visualization?${params.toString()}`, { timeout: vizTimeout })
    );
    return data as VisualizationPayload;
  }

  async updateProjectElement(req: UpdateElementRequest): Promise<VisualizationPayload> {
    const { data } = await this.withFallback((client) =>
      client.post('/project/update', req)
    );
    return data as VisualizationPayload;
  }

  async createProjectElement(
    projectId: string, 
    elementType: 'pole' | 'line' | 'connection' | 'generation' | 'transformer' | 'ptb', 
    elementData: Record<string, any>,
    viewport: { width: number; height: number; zoom?: number; panX?: number; panY?: number }
  ): Promise<VisualizationPayload> {
    console.log('🔋 API: createProjectElement called', { projectId, elementType, elementData, viewport });
    // Debug: Log specific fields for line creation
    if (elementType === 'line') {
      console.log('🔗 LINE CREATE: node1=' + elementData?.node1 + ', node2=' + elementData?.node2 + ', type=' + elementData?.type);
    }
    try {
      const { data } = await this.withFallback((client) =>
        client.post('/project/create-element', {
          project_id: projectId,
          element_type: elementType,
          element_data: elementData,
          viewport
        }, {
          timeout: 180000 // 3 minute timeout (backend has 120s timeout for visualization)
        })
      );
      console.log('🔋 API: createProjectElement response received', { hasData: !!data, dataKeys: data ? Object.keys(data) : [] });
      
      // Check if backend returned an error response
      if (data && (data as any).status === 'error') {
        console.error('❌ API: Backend returned error:', {
          message: (data as any).message,
          error: (data as any).error,
          elementType: (data as any).element_type,
          elementData: (data as any).element_data,
          traceback: (data as any).traceback
        });
        throw new Error((data as any).message || 'Element creation failed');
      }
      
      // Log debug info from line creation if available
      if (elementType === 'line' && data && (data as any).meta?.create_debug) {
        console.log('🔍 LINE CREATE DEBUG from backend:', (data as any).meta.create_debug);
      }
      
      return data as VisualizationPayload;
    } catch (error: any) {
      console.error('🔋 API: createProjectElement error', { 
        message: error?.message, 
        response: error?.response?.data, 
        status: error?.response?.status,
        timeout: error?.code === 'ECONNABORTED'
      });
      throw error;
    }
  }

  saveProjectInPlace = async (
    projectId: string,
    userId?: string,
    comment?: string,
    forceReason?: string,
    saveRequestId?: string,
  ): Promise<any> => {
    const body: { projectId: string; userId?: string; comment?: string; force?: boolean; forceReason?: string; saveRequestId?: string } = {
      projectId,
      userId,
    };
    if (saveRequestId) body.saveRequestId = saveRequestId;
    const trimmed = comment?.trim();
    if (trimmed) body.comment = trimmed;
    if (forceReason?.trim()) {
      body.force = true;
      body.forceReason = forceReason.trim();
    }
    const { data } = await this.withFallback((client) =>
      client.post('/project/save-in-place', body)
    );
    return data;
  }

  exportFullProject = async (projectId: string): Promise<any> => {
    const { data } = await this.withFallback((client) =>
      client.post('/project/export-full', { projectId })
    );
    return data;
  }

  bulkDeleteElements = async (
    projectId: string,
    elements: Array<{ type: string; id: string; position: { x: number; y: number } }>,
    viewport?: { width: number; height: number; zoom: number; panX: number; panY: number }
  ) => {
    const { data } = await this.withFallback((client) =>
      client.post('/project/bulk-delete', { projectId, elements, viewport })
    );
    return data;
  }

  /**
   * Undo the last modifying operation on a project.
   * Returns updated visualization payload with undo info.
   */
  undoLastOperation = async (
    projectId: string,
    viewport?: { width: number; height: number; zoom: number; panX: number; panY: number }
  ): Promise<VisualizationPayload & { success: boolean; undone_operation?: string; message: string; undo_info: UndoRedoInfo }> => {
    const { data } = await this.withFallback((client) =>
      client.post('/project/undo', { project_id: projectId, viewport })
    );
    return data;
  }

  /**
   * Redo the last undone operation on a project.
   * Returns updated visualization payload with undo info.
   */
  redoLastOperation = async (
    projectId: string,
    viewport?: { width: number; height: number; zoom: number; panX: number; panY: number }
  ): Promise<VisualizationPayload & { success: boolean; redone_operation?: string; message: string; undo_info: UndoRedoInfo }> => {
    const { data } = await this.withFallback((client) =>
      client.post('/project/redo', { project_id: projectId, viewport })
    );
    return data;
  }

  /**
   * Get information about available undo and redo operations.
   */
  getUndoInfo = async (
    projectId: string
  ): Promise<UndoRedoInfo> => {
    const { data } = await this.withFallback((client) =>
      client.get(`/project/undo-info?project_id=${projectId}`)
    );
    return data;
  }

  bulkAssignSubnet = async (
    projectId: string,
    elements: Array<{ type: string; id: string; position: { x: number; y: number } }>,
    subnet: string,
    viewport?: { width: number; height: number; zoom: number; panX: number; panY: number }
  ) => {
    const { data } = await this.withFallback((client) =>
      client.post('/project/bulk-assign-subnet', { projectId, elements, subnet, viewport })
    );
    return data;
  }

  bulkUpdateProperties = async (
    projectId: string,
    elements: Array<{ type: string; id: string; position: { x: number; y: number } }>,
    properties: Record<string, any>,
    viewport?: { width: number; height: number; zoom: number; panX: number; panY: number },
    choice?: { overrideUnplantedPoles?: boolean; plantAdjacentPoles?: boolean }
  ) => {
    const { data } = await this.withFallback((client) =>
      client.post('/project/bulk-update-properties', {
        projectId, elements, properties, viewport, ...(choice || {}),
      })
    );
    return data;
  }

  batchConnectionUpdate = async (
    projectId: string,
    updates: Record<string, Record<string, any>>
  ): Promise<{
    status: string;
    updated: number;
    not_found: string[];
    total_requested: number;
  }> => {
    const { data } = await this.withFallback((client) =>
      client.post('/project/batch-connection-update', { projectId, updates })
    );
    return data;
  }

  deleteProjectElement = async (
    projectId: string,
    elementType: 'pole' | 'line' | 'connection' | 'generation' | 'transformer' | 'ptb',
    elementCoords: [number, number],
    viewport: { width: number; height: number; zoom?: number; panX?: number; panY?: number },
    elementId?: string
  ): Promise<VisualizationPayload> => {
    verboseLog('DELETE ELEMENT ID:', elementId);
    // /v5 (not /api/v5): axios baseURL is already /api, and nginx strips that
    // prefix. The old `${API_BASE_URL}/api/v5/...` URL only reached FastAPI
    // because the backend also registered /api/v5; /v5 is the real route.
    const { data: raw } = await this.withFallback((client) =>
      client.post('/v5/delete-network-element', {
        project_id: projectId,
        element_type: elementType,
        element_coords: elementCoords,
        element_id: elementId,
        viewport
      })
    );
    const data = raw as VisualizationPayload;
    // Backend may answer 200 with an error-shaped payload (status: 'error' or
    // meta.error). Applying it would replace the rendered network with the
    // empty arrays it carries — throw instead so callers keep current state.
    const errMsg = (data as any)?.status === 'error'
      ? ((data as any)?.message || (data as any)?.meta?.error || 'Delete failed')
      : (data as any)?.meta?.error;
    if (errMsg) {
      throw new Error(String(errMsg));
    }
    return data;
  };

  /**
   * Recalculate all line lengths in a project from GPS coordinates.
   * Uses Haversine formula for accurate distance calculation.
   */
  async recalculateLineLengths(
    projectId: string,
    viewport: { width: number; height: number; zoom?: number; panX?: number; panY?: number }
  ): Promise<{ status: string; message: string; linesUpdated: number; payload?: VisualizationPayload }> {
    const { data } = await this.withFallback((client) =>
      client.post('/api/v5/recalculate-line-lengths', {
        project_id: projectId,
        viewport
      })
    );
    return data;
  }

  /**
   * Update subnetwork properties (rename subnet code, bulk update conductor).
   * Updates all elements belonging to the specified subnet.
   */
  async updateSubnetwork(
    projectId: string,
    subnetId: string,
    updates: { newSubnetCode?: string; conductor?: string }
  ): Promise<{ status: string; message: string; updated_count: number }> {
    const { data } = await this.withFallback((client) =>
      client.post('/api/v5/update-subnetwork', {
        project_id: projectId,
        subnet_id: subnetId,
        ...updates
      })
    );
    return data;
  }

  async exportProject(projectId: string, includeLogs: boolean = true): Promise<{ blob: Blob; filename: string }> {
    const response = await this.withFallback<AxiosResponse<Blob>>((client) =>
      client.post('/project/export', { project_id: projectId, include_logs: includeLogs }, { responseType: 'blob' })
    );
    const cd = String(response.headers?.['content-disposition'] || '')
    const m = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const fname = decodeURIComponent((m?.[1] || m?.[2] || `uGridExport-${Date.now()}.zip`).trim());
    return { blob: response.data, filename: fname };
  }

  async exportProjectKml(projectId: string): Promise<{ blob: Blob; filename: string }> {
    const response = await this.withFallback<AxiosResponse<Blob>>((client) =>
      client.get('/project/export-kml', { params: { projectId }, responseType: 'blob' })
    );
    const cd = String(response.headers?.['content-disposition'] || '')
    const m = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const fname = decodeURIComponent((m?.[1] || m?.[2] || `uGridKml-${Date.now()}.zip`).trim());
    return { blob: response.data, filename: fname };
  }

  async exportManifest(projectId: string, includeLogs: boolean = true): Promise<{ exportRoot: string; entries: { path: string; contentB64: string }[] }> {
    const { data } = await this.withFallback((client) =>
      client.get('/project/export-manifest', { params: { projectId, includeLogs } })
    );
    return data as { exportRoot: string; entries: { path: string; contentB64: string }[] };
  }

  async getTableData(
    projectId: string,
    elementType: 'pole' | 'connection' | 'line' | 'generation' | 'conductor' | 'transformer' | 'ptb',
    page: number = 1,
    pageSize: number = 100,
    sortBy?: string | null,
    sortOrder: 'asc' | 'desc' = 'asc',
    search?: string | null
  ): Promise<{ columns: string[]; rows: Record<string, any>[]; total: number; page: number; pageSize: number }> {
    const params: any = { projectId, elementType, page, pageSize, sortOrder };
    if (sortBy) {
      params.sortBy = sortBy;
    }
    if (search && search.trim()) {
      params.search = search.trim();
    }
    const { data } = await this.withFallback((client) =>
      client.get('/project/table-data', { params })
    );
    return data;
  }

  async getTableSchema(
    projectId: string,
    elementType: 'pole' | 'connection' | 'line' | 'generation' | 'conductor' | 'transformer' | 'ptb'
  ): Promise<{ columns: Array<{ name: string; type: string; editable: boolean; options?: any[] }> }> {
    const { data } = await this.withFallback((client) =>
      client.get('/project/table-schema', { params: { projectId, elementType } })
    );
    return data;
  }

  async updateTableRow(
    projectId: string,
    elementType: 'pole' | 'connection' | 'line' | 'generation' | 'conductor' | 'transformer' | 'ptb',
    rowId: string,
    updates: Record<string, any>
  ): Promise<VisualizationPayload> {
    const { data } = await this.withFallback((client) =>
      client.post('/project/table-update', { projectId, elementType, rowId, updates })
    );
    return data;
  }

  async downloadConnectionsExcel(projectId: string): Promise<{ blob: Blob; filename: string }> {
    const { data, headers } = await this.withFallback((client) =>
      client.get('/project/connections/excel', {
        params: { projectId },
        responseType: 'blob',
      })
    );
    const disposition = headers?.['content-disposition'] || headers?.['Content-Disposition'] || '';
    const match = String(disposition).match(/filename="([^"]+)"/);
    return {
      blob: data,
      filename: match?.[1] || `${projectId}_connections.xlsx`,
    };
  }

  async previewConnectionsExcel(
    projectId: string,
    file: File
  ): Promise<{
    new_count: number;
    overwrite_count: number;
    skipped_count: number;
    skipped: Array<{ row?: number; customer_id?: string; reason?: string }>;
  }> {
    const formData = new FormData();
    formData.append('projectId', projectId);
    formData.append('file', file);
    const { data } = await this.withFallback((client) =>
      client.post('/project/connections/excel-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    );
    return data;
  }

  async applyConnectionsExcel(
    projectId: string,
    file: File
  ): Promise<{
    success: boolean;
    new_count: number;
    overwrite_count: number;
    skipped_count: number;
    skipped: Array<{ row?: number; customer_id?: string; reason?: string }>;
  }> {
    const formData = new FormData();
    formData.append('projectId', projectId);
    formData.append('file', file);
    const { data } = await this.withFallback((client) =>
      client.post('/project/connections/excel-apply', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    );
    return data;
  }

  /**
   * Get list of available conductors with their electrical properties.
   * Used for cable size selection dropdowns and displaying conductor specifications.
   */
  async getConductors(
    voltageClass?: 'LV' | 'MV',
    conductorType?: 'Airdac' | 'ABC' | 'ACSR' | 'Copper'
  ): Promise<{
    conductors: Array<{
      id: string;
      name: string;
      type: string;
      size_mm2: number;
      cores: number;
      phases: number;
      resistance_ohm_km: number;
      voltage_drop_constant: number;
      ampacity: number;
      cost_per_m: number;
      voltage_class: 'LV' | 'MV';
      neutral_mm2?: number;
    }>;
    default_network_conductor: string;
    default_dropline_conductor: string;
    total: number;
  }> {
    const params: Record<string, string> = {};
    if (voltageClass) params.voltage_class = voltageClass;
    if (conductorType) params.conductor_type = conductorType;
    
    const { data } = await this.withFallback((client) =>
      client.get('/conductors', { params })
    );
    return data;
  }

  private toNetworkData(payload: VisualizationPayload): NetworkData {
    return transformPayloadToNetworkData(payload);
  }

  // =============================================================================
  // PARTS API
  // =============================================================================

  /**
   * Get all parts for a project (system + project-specific).
   */
  async getParts(projectId: string): Promise<{
    parts: Array<{
      id: string;
      name: string;
      type: string;
      part_number: string;
      specification: string;
      category: string;
      manufacturer: string;
      unit: string;
      price_local: number;
      currency: string;
      price_usd: number;
      source: 'system' | 'project';
    }>;
    count: number;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get('/project/parts', { params: { projectId } })
    );
    return data;
  }

  /**
   * Create a new project-specific part.
   */
  async createPart(projectId: string, part: {
    id: string;
    name: string;
    part_number?: string;
    specification?: string;
    category?: string;
    manufacturer?: string;
    unit?: string;
    price_local?: number;
    currency?: string;
    price_usd?: number;
  }): Promise<{ success: boolean; part: any }> {
    const { data } = await this.withFallback((client) =>
      client.post('/project/parts', { projectId, ...part })
    );
    return data;
  }

  /**
   * Update an existing part.
   */
  async updatePart(projectId: string, partId: string, updates: Record<string, any>): Promise<{ success: boolean; part: any }> {
    const { data } = await this.withFallback((client) =>
      client.put('/project/parts', { projectId, id: partId, updates })
    );
    return data;
  }

  /**
   * Delete a project-specific part.
   */
  async deletePart(projectId: string, partId: string): Promise<{ success: boolean; deleted: any }> {
    const { data } = await this.withFallback((client) =>
      client.delete('/project/parts', { data: { projectId, id: partId } })
    );
    return data;
  }

  // =============================================================================
  // ASSEMBLIES API
  // =============================================================================

  /**
   * Get all assemblies for a project (system + project-specific).
   */
  async getAssemblies(projectId: string): Promise<{
    assemblies: Array<{
      id: string;
      name: string;
      type: string;
      description: string;
      pole_type: string;
      angle_class: string[];
      items: Array<{ part_id: string; quantity: number }>;
      source: 'system' | 'project';
    }>;
    count: number;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get('/project/assemblies', { params: { projectId } })
    );
    return data;
  }

  /**
   * Create a new project-specific assembly.
   */
  async createAssembly(projectId: string, assembly: {
    id: string;
    name: string;
    description?: string;
    pole_type?: string;
    angle_class?: string[];
    items?: Array<{ part_id: string; quantity: number }>;
  }): Promise<{ success: boolean; assembly: any }> {
    const { data } = await this.withFallback((client) =>
      client.post('/project/assemblies', { projectId, ...assembly })
    );
    return data;
  }

  /**
   * Update an existing assembly.
   */
  async updateAssembly(projectId: string, assemblyId: string, updates: Record<string, any>): Promise<{ success: boolean; assembly: any }> {
    const { data } = await this.withFallback((client) =>
      client.put('/project/assemblies', { projectId, id: assemblyId, updates })
    );
    return data;
  }

  /**
   * Delete a project-specific assembly.
   */
  async deleteAssembly(projectId: string, assemblyId: string): Promise<{ success: boolean; deleted: any }> {
    const { data } = await this.withFallback((client) =>
      client.delete('/project/assemblies', { data: { projectId, id: assemblyId } })
    );
    return data;
  }

  // =============================================================================
  // ENGINEERING DRAWINGS API
  // =============================================================================

  /**
   * Upload an engineering drawing for an assembly type.
   */
  async uploadAssemblyDrawing(projectId: string, assemblyId: string, file: File): Promise<{
    success: boolean;
    filename: string;
    content_type: string;
    size: number;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const { data } = await this.withFallback((client) =>
      client.post('/project/upload/assembly-drawing', formData, {
        params: { projectId, assemblyId },
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    );
    return data;
  }

  /**
   * Get the download URL for an assembly drawing.
   */
  getAssemblyDrawingUrl(projectId: string, assemblyId: string): string {
    return `${API_BASE_URL}/project/download/assembly-drawing?projectId=${encodeURIComponent(projectId)}&assemblyId=${encodeURIComponent(assemblyId)}`;
  }

  /**
   * Delete an engineering drawing for an assembly type.
   */
  async deleteAssemblyDrawing(projectId: string, assemblyId: string): Promise<{ success: boolean; deleted: string | null }> {
    const { data } = await this.withFallback((client) =>
      client.delete('/project/upload/assembly-drawing', { params: { projectId, assemblyId } })
    );
    return data;
  }

  async getCommissioningReport(projectName: string, generationId: string): Promise<CommissioningReportStatus> {
    const { data } = await this.withFallback((client) =>
      client.get(`/projects/${encodeURIComponent(projectName)}/generations/${encodeURIComponent(generationId)}/commissioning-report`)
    );
    return data;
  }

  async uploadCommissioningReport(
    projectName: string,
    generationId: string,
    file: File,
    fields: { signed: boolean; signedBy?: string; signedDate?: string; notes?: string },
  ): Promise<{ success: boolean; report: CommissioningReport; replaced_legacy_link: boolean }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('signed', fields.signed ? 'true' : 'false');
    if (fields.signedBy) formData.append('signed_by', fields.signedBy);
    if (fields.signedDate) formData.append('signed_date', fields.signedDate);
    if (fields.notes) formData.append('notes', fields.notes);
    const { data } = await this.withFallback((client) =>
      client.post(
        `/projects/${encodeURIComponent(projectName)}/generations/${encodeURIComponent(generationId)}/commissioning-report`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
    );
    return data;
  }

  async approveCommissioningReport(projectName: string, generationId: string, reportId: string, note?: string):
    Promise<{ success: boolean; report: CommissioningReport }> {
    const { data } = await this.withFallback((client) =>
      client.post(
        `/projects/${encodeURIComponent(projectName)}/generations/${encodeURIComponent(generationId)}/commissioning-report/${encodeURIComponent(reportId)}/approve`,
        { note: note || null },
      )
    );
    return data;
  }

  commissioningReportDownloadUrl(report: CommissioningReport): string | null {
    if (report.source === 'legacy_link') return report.url || null;
    if (!report.download_url) return null;
    return `${API_BASE_URL}${report.download_url.replace(/^\/api/, '')}`;
  }

  async tagVersionAsBuilt(projectName: string, versionId: string, note: string): Promise<any> {
    const { data } = await this.withFallback((client) =>
      client.post(`/projects/${encodeURIComponent(projectName)}/versions/${encodeURIComponent(versionId)}/as-built`, { note })
    );
    return data;
  }

  async clearVersionAsBuilt(projectName: string, versionId: string, note: string): Promise<any> {
    const { data } = await this.withFallback((client) =>
      client.delete(`/projects/${encodeURIComponent(projectName)}/versions/${encodeURIComponent(versionId)}/as-built`, { data: { note } })
    );
    return data;
  }

  /**
   * Upload an engineering drawing for a generation element.
   */
  async uploadGenerationDrawing(projectId: string, generationId: string, file: File): Promise<{
    success: boolean;
    filename: string;
    content_type: string;
    size: number;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const { data } = await this.withFallback((client) =>
      client.post('/project/upload/generation-drawing', formData, {
        params: { projectId, generationId },
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    );
    return data;
  }

  /**
   * Get the download URL for a generation drawing.
   */
  getGenerationDrawingUrl(projectId: string, generationId: string): string {
    return `${API_BASE_URL}/project/download/generation-drawing?projectId=${encodeURIComponent(projectId)}&generationId=${encodeURIComponent(generationId)}`;
  }

  /**
   * Delete an engineering drawing for a generation element.
   */
  async deleteGenerationDrawing(projectId: string, generationId: string): Promise<{ success: boolean; deleted: string | null }> {
    const { data } = await this.withFallback((client) =>
      client.delete('/project/upload/generation-drawing', { params: { projectId, generationId } })
    );
    return data;
  }

  /**
   * Get list of all generation drawings for a project.
   */
  async getGenerationDrawings(projectId: string): Promise<{
    drawings: Record<string, { filename: string; content_type: string; uploaded_at: number }>;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get('/project/generation-drawings', { params: { projectId } })
    );
    return data;
  }

  /**
   * Get the spatial layout for a generation element.
   */
  async getGenerationLayout(projectId: string, generationId: string): Promise<{ layout: any | null; center?: [number, number] | null }> {
    try {
      const { data } = await this.withFallback((client) =>
        client.get('/project/generation-layout', { params: { projectId, generationId } })
      );
      return data;
    } catch (err: any) {
      // A generation with no layout yet is a typed 404, not an error for the editor.
      const detail = err?.response?.status === 404 ? err.response.data?.detail : null;
      if (detail?.code === 'GENERATION_LAYOUT_MISSING') {
        return { layout: null, center: detail.center ?? null };
      }
      throw err;
    }
  }

  /**
   * Save the spatial layout for a generation element.
   */
  async saveGenerationLayout(projectId: string, generationId: string, layout: any): Promise<{ success: boolean }> {
    const { data } = await this.withFallback((client) =>
      client.put('/project/generation-layout', { layout }, { params: { projectId, generationId } })
    );
    return data;
  }

  async getGenerationElectricalDesign(
    projectId: string,
    generationId: string,
  ): Promise<{ design: GenerationElectricalDesign | null }> {
    try {
      const { data } = await this.withFallback((client) =>
        client.get('/project/generation-electrical-design', {
          params: { projectId, generationId },
        })
      );
      return data;
    } catch (err: any) {
      const detail = err?.response?.status === 404 ? err.response.data?.detail : null;
      if (detail?.code === 'GENERATION_DESIGN_MISSING') return { design: null };
      throw err;
    }
  }

  async buildGenerationElectricalDesign(
    projectId: string,
    generationId: string,
    layout: any,
    params: {
      inverterId: string;
      minDesignTempC: number;
      maxCellTempC: number;
      voltageMarginPct?: number;
      stringLengthPolicy?: string;
    },
  ): Promise<{ design: GenerationElectricalDesign }> {
    const { data } = await this.withFallback((client) =>
      client.post('/project/generation-electrical-design', {
        layout,
        ...params,
      }, { params: { projectId, generationId } })
    );
    return data;
  }

  /**
   * Auto-generate solar array placement within the generation site boundary.
   */
  async autoLayoutArrays(projectId: string, generationId: string, layout: any, params: {
    module_id?: string;
    target_kwp?: number;
    gcr?: number;
    tilt_deg?: number;
    azimuth_deg?: number;
    row_spacing_m?: number;
    tracker_preset?: string;
    fence_offset_m?: number;
  } = {}): Promise<any> {
    const { data } = await this.withFallback((client) =>
      client.post('/project/generation-auto-layout', { layout, params }, { params: { projectId, generationId } })
    );
    return data;
  }

  /**
   * Compute DC cable routes and combiner box placements from the generation layout.
   */
  async routeGenerationCables(projectId: string, generationId: string, layout: any, options?: {
    strings_per_combiner?: number;
    electricalDesign?: GenerationElectricalDesign;
  }): Promise<{
    routing: any;
    geojson: any;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post('/project/generation-cable-routing', {
        layout,
        strings_per_combiner: options?.strings_per_combiner ?? 16,
        electricalDesign: options?.electricalDesign
          ? { design_id: options.electricalDesign.design_id }
          : undefined,
      }, { params: { projectId, generationId } })
    );
    return data;
  }

  /**
   * Generate an SLD (Single-Line Diagram) PPTX for a generation site layout.
   * Returns the raw response so callers can trigger a browser download.
   */
  async generateGenerationSLD(
    projectId: string,
    generationId: string,
    features: any[],
    siteName: string,
    panelWp?: number,
    panelModel?: string,
    electricalDesign?: GenerationElectricalDesign,
  ): Promise<Response> {
    const base = getApiBaseUrl();
    return fetch(
      `${base}/project/generation-layout-sld?projectId=${encodeURIComponent(projectId)}&generationId=${encodeURIComponent(generationId)}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          features,
          siteName,
          panelWp,
          panelModel,
          electricalDesign: electricalDesign
            ? { design_id: electricalDesign.design_id }
            : undefined,
        }),
      },
    );
  }

  /**
   * Analyse inter-row shading for solar arrays and return GCR/pitch recommendations.
   */
  async analyzeGenerationShading(
    projectId: string,
    generationId: string,
    payload: {
      features: any[];
      latitude: number;
      panelLengthM?: number;
      panelWidthM?: number;
      elevationSamples?: Record<string, [number, number, number][]>;
    },
  ): Promise<{ analyses: any[]; latitude: number }> {
    const { data } = await this.withFallback((client) =>
      client.post('/project/generation-shading-analysis', payload, {
        params: { projectId, generationId },
      }),
    );
    return data;
  }

  /**
   * Generate a comprehensive procurement BOM from the generation site layout.
   */
  async calculateGenerationBOM(
    projectId: string,
    generationId: string,
    payload: {
      features: any[];
      siteName: string;
      panelIscA?: number;
      panelVocV?: number;
      panelVmpV?: number;
      panelWp?: number;
      panelModel?: string;
      panelLengthM?: number;
      inverterName?: string;
      inverterAcKw?: number;
      inverterMaxDcV?: number;
      inverterNumMppt?: number;
      inverterStringsPerMppt?: number;
      stringsPerCombiner?: number;
      cableRouting?: any;
      electricalDesign?: GenerationElectricalDesign;
    },
  ): Promise<{ site_name: string; items: any[]; summary: any }> {
    const requestPayload = {
      ...payload,
      electricalDesign: payload.electricalDesign
        ? { design_id: payload.electricalDesign.design_id }
        : undefined,
    };
    const { data } = await this.withFallback((client) =>
      client.post('/project/generation-bom', requestPayload, {
        params: { projectId, generationId },
      }),
    );
    return data;
  }

  /**
   * Download the validated generation-site BOM as a formatted Excel workbook.
   */
  async downloadGenerationBOMExcel(
    projectId: string,
    generationId: string,
    payload: {
      features: any[];
      siteName: string;
      cableRouting?: any;
      electricalDesign?: GenerationElectricalDesign;
    },
  ): Promise<Blob> {
    const requestPayload = {
      ...payload,
      electricalDesign: payload.electricalDesign
        ? { design_id: payload.electricalDesign.design_id }
        : undefined,
    };
    const response = await this.withFallback((client) =>
      client.post('/project/generation-bom', requestPayload, {
        params: { projectId, generationId, format: 'xlsx' },
        responseType: 'blob',
        timeout: 120000,
      }),
    );
    return response.data;
  }

  // =============================================================================
  // TRANSFORMER API
  // =============================================================================

  /**
   * Create a structural pole at an offset from a reference pole.
   * Structural poles provide mechanical support only (e.g., for dual-pole transformer mounting).
   */
  async createStructuralPole(
    projectId: string,
    data: {
      reference_pole_id: string;
      offset_meters?: number;  // Default 1.5m
      bearing_degrees?: number;  // Default 90 (East)
    }
  ): Promise<{
    success: boolean;
    pole_id: string;
    gps_x: number;
    gps_y: number;
    message: string;
  }> {
    const { data: result } = await this.withFallback((client) =>
      client.post('/v5/poles/create-structural', {
        project_id: projectId,
        ...data
      })
    );
    return result;
  }

  /**
   * Create a new transformer linked to a pole or generation site.
   */
  async createTransformer(
    projectId: string,
    data: {
      transformer_type?: 'step_down' | 'step_up' | 'isolation';
      secondary_mode?: '1P' | '3P_1P' | '3P_3P';
      primary_pole_id?: string;  // Electrical connection pole (renamed from pole_id)
      support_pole_id?: string;  // Optional structural support pole for dual-pole mounting
      generation_id?: string;  // For step-up transformers
      status?: 'T' | 'TI' | 'TC';
      serial_number?: string;
      manufacturer?: string;
      kva_rating?: number;
      primary_voltage?: number;
      secondary_voltage?: number;
      commissioning_date?: string;
      primary_protection?: string;
      secondary_protection?: string;
      notes?: string;
    }
  ): Promise<{
    success: boolean;
    transformer_id: string;
    message: string;
  }> {
    const { data: result } = await this.withFallback((client) =>
      client.post('/v5/transformers/create', {
        project_id: projectId,
        ...data
      })
    );
    return result;
  }

  /**
   * Auto-create step-down transformers at all MV-LV interface poles.
   * Creates transformers at MV poles that are root nodes of LV networks.
   */
  async autoCreateTransformers(
    projectId: string,
    defaultKva: number = 50.0
  ): Promise<{
    success: boolean;
    created_count: number;
    transformers: Array<{
      transformer_id: string;
      pole_id: string;
      kva_rating: number;
    }>;
    message: string;
    undo_info?: {
      can_undo: boolean;
      stack_size: number;
      operations: Array<{ operation: string; timestamp: string }>;
    };
  }> {
    const { data: result } = await this.withFallback((client) =>
      client.post('/v5/transformers/auto-create', {
        project_id: projectId,
        default_kva: defaultKva
      })
    );
    return result;
  }

  /**
   * Auto-size LV ABC conductors from downstream current and path voltage drop,
   * then refresh transformer kVA sizing from the same V-drop-aware load model.
   */
  async autoSizeLVConductors(
    projectId: string,
    scenario: '1ph' | '3ph' = '1ph',
    onlyUnsized: boolean = false,
    options?: { maxCableSizeMm2?: number; minVoltageV?: number; phaseMode?: 'abc' | 'a' | 'keep' }
  ): Promise<{
    success: boolean;
    sized_line_count: number;
    resized_transformer_count: number;
    remaining_voltage_violations: number;
    min_voltage_target_v?: number;
    max_cable_size_mm2?: number | null;
    max_cable_fallback_count?: number;
    worst_customer_voltage_v?: number;
    iterations?: number;
    scenario?: string;
    message: string;
    undo_info?: {
      can_undo: boolean;
      stack_size: number;
      operations: Array<{ operation: string; timestamp: string }>;
    };
  }> {
    const { data: result } = await this.withFallback((client) =>
      client.post('/v5/lv-conductors/auto-size', {
        project_id: projectId,
        scenario,
        only_unsized: onlyUnsized,
        phase_mode: onlyUnsized ? 'keep' : (options?.phaseMode || 'keep'),
        ...(options?.maxCableSizeMm2 != null ? { max_cable_size_mm2: options.maxCableSizeMm2 } : {}),
        ...(options?.minVoltageV != null ? { min_voltage_v: options.minVoltageV } : {}),
      })
    );
    return result;
  }

  /**
   * Revert specified LV network lines back to undefined (blank Cable_size).
   * Used to undo a provisional vdrop auto-fill when the user declines to keep it.
   */
  async clearLVConductors(
    projectId: string,
    lines: { node1: string; node2: string }[]
  ): Promise<{
    success: boolean;
    cleared_line_count: number;
    message: string;
    undo_info?: {
      can_undo: boolean;
      stack_size: number;
      operations: Array<{ operation: string; timestamp: string }>;
    };
  }> {
    const { data: result } = await this.withFallback((client) =>
      client.post('/v5/lv-conductors/clear', {
        project_id: projectId,
        lines,
      })
    );
    return result;
  }

  /**
   * Update an existing transformer.
   */
  async updateTransformer(
    projectId: string,
    transformerId: string,
    updates: Record<string, any>
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.put('/v5/transformers/update', {
        project_id: projectId,
        transformer_id: transformerId,
        updates
      })
    );
    return data;
  }

  /**
   * Delete a transformer.
   */
  async deleteTransformer(
    projectId: string,
    transformerId: string
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.delete(`/v5/transformers/${transformerId}`, {
        params: { project_id: projectId }
      })
    );
    return data;
  }

  /**
   * Add a replacement record to a transformer's history.
   */
  async addTransformerReplacement(
    projectId: string,
    transformerId: string,
    replacement: {
      date: string;
      reason?: string;
      old_serial?: string;
      new_serial?: string;
      om_ticket?: string;
      replaced_by?: string;
    }
  ): Promise<{
    success: boolean;
    message: string;
    history: any[];
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/v5/transformers/${transformerId}/replacement`, {
        project_id: projectId,
        transformer_id: transformerId,
        ...replacement
      })
    );
    return data;
  }

  /**
   * Upload a nameplate photo for a transformer.
   */
  async uploadTransformerPhoto(
    projectId: string,
    transformerId: string,
    file: File
  ): Promise<{
    success: boolean;
    filename: string;
    content_type: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const { data } = await this.withFallback((client) =>
      client.post(`/v5/transformers/${transformerId}/photo`, formData, {
        params: { projectId },
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    );
    return data;
  }

  /**
   * Get the URL for a transformer's nameplate photo.
   */
  getTransformerPhotoUrl(projectId: string, transformerId: string): string {
    return `${getApiBaseUrl()}/v5/transformers/${transformerId}/photo?projectId=${projectId}`;
  }

  /**
   * High-res project basemap (drone orthomosaic) metadata.
   * Returns { present: false } when the project has no overlay attached.
   */
  async getHighresBasemap(projectId: string): Promise<{
    present: boolean;
    projectId?: string;
    label?: string;
    bounds?: { west: number; south: number; east: number; north: number };
    center?: { lat: number; lng: number };
    gsd_m?: number;
    source?: string;
    opacity?: number;
    image_url?: string;
    seed_id?: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get('/v5/highres-basemap', { params: { projectId } }),
    );
    return data;
  }

  getHighresBasemapImageUrl(projectId: string): string {
    return `${getApiBaseUrl()}/v5/highres-basemap/image?projectId=${encodeURIComponent(projectId)}`;
  }

  async listHighresBasemapSeeds(): Promise<{
    seeds: Array<{
      seed_id: string;
      label: string;
      site_hint?: string;
      center?: { lat: number; lng: number };
      gsd_m?: number;
    }>;
    count: number;
  }> {
    const { data } = await this.withFallback((client) => client.get('/v5/highres-basemap/seeds'));
    return data;
  }

  /** Attach a packaged demo orthomosaic (e.g. zm_village) to the open project. */
  async installHighresBasemapSeed(projectId: string, seedId: string): Promise<any> {
    const { data } = await this.withFallback((client) =>
      client.post('/v5/highres-basemap/install-seed', null, {
        params: { projectId, seedId },
      }),
    );
    return data;
  }

  /**
   * Upload a web-ready orthomosaic (jpg/png/webp) with WGS84 bounds for any project.
   * Convert GeoTIFF offline first — see docs/SOP_HIGHRES_BASEMAP.md.
   */
  async uploadHighresBasemap(
    projectId: string,
    file: File,
    bounds: { west: number; south: number; east: number; north: number },
    opts?: { label?: string; gsd_m?: number; source?: string },
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await this.withFallback((client) =>
      client.post('/v5/highres-basemap', formData, {
        params: {
          projectId,
          west: bounds.west,
          south: bounds.south,
          east: bounds.east,
          north: bounds.north,
          label: opts?.label || 'High-res aerial',
          ...(opts?.gsd_m != null ? { gsd_m: opts.gsd_m } : {}),
          ...(opts?.source ? { source: opts.source } : {}),
        },
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
    return data;
  }

  async deleteHighresBasemap(projectId: string): Promise<{ status: string; removed: boolean }> {
    const { data } = await this.withFallback((client) =>
      client.delete('/v5/highres-basemap', { params: { projectId } }),
    );
    return data;
  }


  /**
   * Upload an equipment photo for a generation site (multiple allowed).
   */
  async uploadGenerationPhoto(
    projectId: string,
    genId: string,
    file: File,
    caption?: string
  ): Promise<{ success: boolean; photo?: GenerationPhoto }> {
    const formData = new FormData();
    formData.append('file', file);
    if (caption && caption.trim()) formData.append('caption', caption.trim());

    const { data } = await this.withFallback((client) =>
      client.post(`/v5/generations/${encodeURIComponent(genId)}/photos`, formData, {
        params: { projectId },
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    );
    return data;
  }

  /**
   * List equipment photos for a generation site.
   */
  async listGenerationPhotos(projectId: string, genId: string): Promise<GenerationPhoto[]> {
    const { data } = await this.withFallback((client) =>
      client.get(`/v5/generations/${encodeURIComponent(genId)}/photos`, { params: { projectId } })
    );
    return (data?.photos || []) as GenerationPhoto[];
  }

  /**
   * Get the URL for one equipment photo of a generation site.
   */
  getGenerationPhotoUrl(projectId: string, genId: string, filename: string): string {
    return `${getApiBaseUrl()}/v5/generations/${encodeURIComponent(genId)}/photos/${encodeURIComponent(filename)}?projectId=${projectId}`;
  }

  /**
   * Delete one equipment photo from a generation site.
   */
  async deleteGenerationPhoto(projectId: string, genId: string, filename: string): Promise<{ success: boolean }> {
    const { data } = await this.withFallback((client) =>
      client.delete(`/v5/generations/${encodeURIComponent(genId)}/photos/${encodeURIComponent(filename)}`, { params: { projectId } })
    );
    return data;
  }

  /**
   * Fetch a project's construction Gantt: schedule (template + overrides) plus
   * measured work-done drivers for the progress-filled bars.
   */
  async getProjectGantt(projectKey: string): Promise<GanttResponse> {
    const { data } = await this.withFallback((client) =>
      client.get(`/gantt/${encodeURIComponent(projectKey)}`)
    );
    return data as GanttResponse;
  }

  /**
   * Save per-project Gantt settings (start date, duration, milestones).
   * Task rows are managed via the task CRUD methods below.
   */
  async saveProjectGantt(projectKey: string, overrides: GanttOverrides): Promise<GanttSaveResponse> {
    const { data } = await this.withFallback((client) =>
      client.put(`/gantt/${encodeURIComponent(projectKey)}`, overrides)
    );
    return data as GanttSaveResponse;
  }

  /**
   * Fetch a project's canonical/variant status (by composite key or name).
   * Drives the planner's non-canonical banner + write guard.
   */
  async getCanonicalStatus(projectKeyOrName: string): Promise<{
    project: string; name: string; site_role: string; canonical_site_code: string;
    is_canonical: boolean; canonical_project_key?: string | null; canonical_project_name?: string | null;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get(`/projects/${encodeURIComponent(projectKeyOrName)}/canonical-status`)
    );
    return data;
  }

  /**
   * Switch a project to be the canonical (tracked) design for its site.
   * Approver-level only (approve_plans / administer_ugp); the handoff is recorded.
   */
  async makeCanonical(projectKeyOrName: string, reason: string = ''): Promise<{
    ok: boolean; project: string; site_role: string; canonical_site_code: string;
    previous_canonical?: string | null; unchanged: boolean;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/projects/${encodeURIComponent(projectKeyOrName)}/make-canonical`, { reason })
    );
    return data;
  }

  /** Add a work-package row to a project's Gantt. */
  async createGanttTask(projectKey: string, task: Partial<GanttTask>): Promise<{ success: boolean; task: GanttTask }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/gantt/${encodeURIComponent(projectKey)}/tasks`, task)
    );
    return data;
  }

  /** Update a work-package row. progress: null clears a manual override back to auto.
   *  cascade (default true server-side) pushes successors right on start/end moves. */
  async updateGanttTask(projectKey: string, taskId: string, fields: Partial<GanttTask> & { cascade?: boolean }): Promise<{ success: boolean; task: GanttTask; cascaded?: Array<{ id: string; start: number; end: number }> }> {
    const { data } = await this.withFallback((client) =>
      client.put(`/gantt/${encodeURIComponent(projectKey)}/tasks/${encodeURIComponent(taskId)}`, fields)
    );
    return data;
  }

  /** Delete a work-package row. */
  async deleteGanttTask(projectKey: string, taskId: string): Promise<{ success: boolean }> {
    const { data } = await this.withFallback((client) =>
      client.delete(`/gantt/${encodeURIComponent(projectKey)}/tasks/${encodeURIComponent(taskId)}`)
    );
    return data;
  }

  /** Replace the project's work packages from an MS Project XML export. */
  async importGanttMsProject(projectKey: string, xmlText: string): Promise<GanttImportResponse> {
    const { data } = await this.withFallback((client) =>
      client.post(`/gantt/${encodeURIComponent(projectKey)}/import-msproject`, xmlText, {
        headers: { 'Content-Type': 'text/plain' },
      })
    );
    return data;
  }

  /** Download the project's Gantt as MS Project XML or CSV (blob). */
  async exportGantt(projectKey: string, format: 'msproject' | 'csv'): Promise<Blob> {
    const { data } = await this.withFallback((client) =>
      client.get(`/gantt/${encodeURIComponent(projectKey)}/export`, {
        params: { format },
        responseType: 'blob',
      })
    );
    return data as Blob;
  }

  /**
   * List all transformers for a project.
   */
  async listTransformers(projectId: string): Promise<{
    transformers: Array<{
      transformerId: string;
      poleId: string;
      status: string;
      serialNumber?: string;
      manufacturer?: string;
      kvaRating?: number;
      voltageRatio?: string;
      commissioningDate?: string;
      lvProtection?: string;
      mvProtection?: string;
      nameplatePhoto?: string;
      replacementHistory?: string;
      notes?: string;
    }>;
    count: number;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get('/v5/transformers', { params: { projectId } })
    );
    return data;
  }

  // =============================================================================
  // PTB (POLE TOP BOX) API
  // =============================================================================

  /**
   * Create a PTB at a specific pole.
   */
  async createPTB(
    projectId: string,
    poleId: string,
    options?: {
      serialNumber?: string;
      manufacturer?: string;
      model?: string;
      numChannels?: number;
      channelSerials?: string[];
      protectionSpecs?: string;
      installationDate?: string;
      notes?: string;
      addAnother?: boolean;
    }
  ): Promise<{
    success: boolean;
    created_count?: number;
    already_present?: boolean;
    ptb_id: string;
    pole_id: string;
    num_channels: number;
    connected_drops: number;
    ptbs?: Array<{
      ptb_id: string;
      num_channels: number;
      connected_drops: number;
    }>;
    undo_info?: {
      can_undo: boolean;
      stack_size: number;
      operations: Array<{ operation: string; timestamp: string }>;
    };
  }> {
    const { data: result } = await this.withFallback((client) =>
      client.post('/v5/ptb/create', {
        project_id: projectId,
        pole_id: poleId,
        serial_number: options?.serialNumber,
        manufacturer: options?.manufacturer,
        model: options?.model,
        num_channels: options?.numChannels || 0,
        channel_serials: options?.channelSerials || [],
        protection_specs: options?.protectionSpecs,
        installation_date: options?.installationDate,
        notes: options?.notes,
        add_another: options?.addAnother || false,
      })
    );
    return result;
  }

  /**
   * Auto-create PTBs at all terminal LV poles (LV/service drop interface).
   * defaultChannels: 0 = use connected drops count; >0 = use at least this many channels
   */
  async autoCreatePTBs(
    projectId: string,
    defaultChannels: number = 0
  ): Promise<{
    success: boolean;
    created_count: number;
    ptbs: Array<{
      ptb_id: string;
      pole_id: string;
      num_channels: number;
      connected_drops: number;
    }>;
    message: string;
    undo_info?: {
      can_undo: boolean;
      stack_size: number;
      operations: Array<{ operation: string; timestamp: string }>;
    };
  }> {
    const { data: result } = await this.withFallback((client) =>
      client.post('/v5/ptb/auto-create', {
        project_id: projectId,
        default_channels: defaultChannels
      })
    );
    return result;
  }

  /**
   * Auto-generate missing droplines for connections without one.
   * Creates an Airdac service drop from each unserved connection to its nearest
   * LV pole within range (or nearest pole of any type if no LV in range).
   * maxDistance: max meters from connection to pole (default 50)
   */
  async autoGenerateDroplines(
    projectId: string,
    maxDistance: number = 50
  ): Promise<{
    success: boolean;
    created_count: number;
    skipped_count: number;
    total_droplines: number;
    created: Array<{ connection: string; pole: string; distance_m: number }>;
    skipped: string[];
    message: string;
    undo_info: {
      can_undo: boolean;
      stack_size: number;
      operations: Array<{ operation: string; timestamp: string }>;
    };
  }> {
    const { data: result } = await this.withFallback((client) =>
      client.post('/v5/droplines/auto-generate', {
        project_id: projectId,
        max_distance: maxDistance
      })
    );
    return result;
  }

  /**
   * Update an existing PTB.
   */
  async updatePTB(
    projectId: string,
    ptbId: string,
    updates: Record<string, any>
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.put('/v5/ptb/update', {
        project_id: projectId,
        ptb_id: ptbId,
        updates
      })
    );
    return data;
  }

  /**
   * Delete a PTB.
   */
  async deletePTB(
    projectId: string,
    ptbId: string
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.delete(`/v5/ptb/${ptbId}`, {
        params: { project_id: projectId }
      })
    );
    return data;
  }

  /**
   * List all PTBs for a project.
   */
  async listPTBs(projectId: string): Promise<{
    ptbs: Array<{
      ptbId: string;
      poleId: string;
      serialNumber: string;
      manufacturer: string;
      model: string;
      status: string;
      numChannels: number;
      channelSerials: string[];
      protectionSpecs: string;
      installationDate: string;
      commissioningDate: string;
      connectedDrops: number;
      notes: string;
      photoPath: string;
    }>;
    count: number;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get('/v5/ptb', { params: { projectId } })
    );
    return data;
  }

  // =============================================================================
  // BOM API
  // =============================================================================

  /**
   * Generate Bill of Materials for the project.
   */
  /** Design basis of the last BOM download (approved version or latest fallback). */
  lastBomBasis: BomBasis | null = null;

  async generateBOM(
    projectId: string,
    scope: 'full' | 'selection' = 'full',
    elementIds?: string[],
    format: 'json' | 'csv' | 'xlsx' = 'json',
    pricingOverrides?: Record<string, BomPricingOverride>,
    persistOverrides: boolean = false,
  ): Promise<{
    success: boolean;
    scope: string;
    summary: {
      poles: Record<string, number>;
      wire_lengths: Record<string, number>;
      meters: Record<string, number>;
      total_parts: number;
      total_cost_local: number;
      total_cost_usd: number;
      currency: string;
      pricing_overrides_applied?: number;
    };
    items: Array<{
      part_id: string;
      name: string;
      category: string;
      unit: string;
      quantity: number;
      price_local: number;
      price_usd: number;
      currency: string;
      line_cost_local: number;
      line_cost_usd: number;
      price_strategy?: string;
      price_source_label?: string;
      price_is_override?: boolean;
    }>;
    pricing_appendix?: BomPricingAppendixEntry[];
  }> {
    const { data } = await this.withFallback((client) =>
      client.post('/project/bom', {
        projectId, scope, element_ids: elementIds, format,
        pricing_overrides: pricingOverrides,
        persist_overrides: persistOverrides,
      })
    );
    return data;
  }

  /**
   * Download BOM as CSV file.
   */
  async downloadBOMAsCSV(
    projectId: string,
    scope: 'full' | 'selection' = 'full',
    elementIds?: string[],
    pricingOverrides?: Record<string, BomPricingOverride>,
    persistOverrides: boolean = false,
  ): Promise<Blob> {
    const response = await this.withFallback((client) =>
      client.post('/project/bom', 
        {
          projectId, scope, element_ids: elementIds, format: 'csv',
          pricing_overrides: pricingOverrides,
          persist_overrides: persistOverrides,
        },
        { responseType: 'blob' }
      )
    );
    this.lastBomBasis = bomBasisFromHeaders(response.headers);
    return response.data;
  }

  /**
   * Pole-top boxes sized for every customer, with expected filled slots.
   */
  async downloadPtbRequisition(
    projectId: string,
    householdRatePercent: number,
  ): Promise<Blob> {
    const response = await this.withFallback((client) =>
      client.post('/project/ptb-requisition',
        { projectId, householdRatePercent },
        { responseType: 'blob' }
      )
    );
    return response.data;
  }

  /**
   * Download BOM as multi-tab Excel file.
   * Contains: Full BOM, Assembly Summary, and individual assembly BOMs.
   */
  async downloadBOMAsExcel(
    projectId: string,
    scope: 'full' | 'selection' = 'full',
    elementIds?: string[],
    pricingOverrides?: Record<string, BomPricingOverride>,
    persistOverrides: boolean = false,
  ): Promise<Blob> {
    const response = await this.withFallback((client) =>
      client.post('/project/bom', 
        {
          projectId, scope, element_ids: elementIds, format: 'xlsx',
          pricing_overrides: pricingOverrides,
          persist_overrides: persistOverrides,
        },
        { responseType: 'blob' }
      )
    );
    this.lastBomBasis = bomBasisFromHeaders(response.headers);
    return response.data;
  }

  /**
   * Create a purchase request in PR from the design BOM (Brief 07).
   * The BOM is recomputed server-side; the browser only sends the project
   * key, site code and scope. Returns the PR id/number/link or throws
   * with the PR error text.
   */
  async createPurchaseRequestFromBom(
    projectKey: string,
    siteCode: string,
    bomScope: 'full' | 'selection' = 'full',
    requesterEmail?: string,
    designVersion?: string,
  ): Promise<{ ok: true; pr_id: string; pr_number: string; url: string }> {
    const { data } = await this.withFallback((client) =>
      client.post('/api/bom/create-pr', {
        project_key: projectKey,
        site_code: siteCode,
        bom_scope: bomScope,
        requester_email: requesterEmail,
        design_version: designVersion,
      })
    );
    return data;
  }

  /**
   * Import poles + network lines from a utility KMZ/KML/shapefile zip.
   */
  async importUtilityNetwork(
    projectId: string,
    file: File,
  ): Promise<{ ok: true; pole_count: number; line_count: number; source_name: string }> {
    const form = new FormData();
    form.append('project_id', projectId);
    form.append('file', file);
    const { data } = await this.withFallback((client) =>
      client.post('/project/import-utility-network', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    );
    return data;
  }

  // ========== CANONICAL EXPORT (Phase 1 of canonical-gpkg migration) ==========
  //
  // Lets the user pull their project state out of the canonical delta-storage
  // keyframe in any of the supported formats (shp/gpkg/kml/xlsx). Phase 2 will
  // stop writing the cached top-level <timestamp>/ snapshot, so this endpoint
  // becomes the single way for analysts/field teams to get a shapefile bundle
  // off the server.

  /** List the formats the canonical exporter supports. */
  async listExportFormats(): Promise<{
    formats: Array<{
      id: 'shp' | 'gpkg' | 'kml' | 'xlsx';
      label: string;
      extension: string;
      description: string;
    }>;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get('/project/export/formats')
    );
    return data;
  }

  /**
   * Download a canonical export as a zip blob. The backend reconstructs
   * the project from its delta-storage keyframe and emits a format-specific
   * zip; we just stream the bytes through to a browser download.
   *
   * The endpoint is GET (not POST) so a plain `<a href>` would also work,
   * but using axios+blob lets us preserve the auth header and surface
   * server errors as exceptions instead of broken downloads.
   */
  async downloadProjectExport(
    projectId: string,
    format: 'shp' | 'gpkg' | 'kml' | 'xlsx',
    versionId?: string,
  ): Promise<{ blob: Blob; filename: string; layerCounts: Record<string, number> }> {
    const response = await this.withFallback((client) =>
      client.get('/project/export/canonical', {
        params: {
          projectId,
          format,
          ...(versionId ? { versionId } : {}),
        },
        responseType: 'blob',
      })
    );

    // Pull the server-suggested filename out of Content-Disposition. Falls
    // back to a sensible default if the header is stripped by a proxy.
    const cd = (response.headers as any)?.['content-disposition'] || '';
    const m = /filename="?([^";]+)"?/i.exec(cd);
    const filename = m && m[1] ? m[1] : `${projectId}_${format}.zip`;

    let layerCounts: Record<string, number> = {};
    try {
      const raw = (response.headers as any)?.['x-export-layer-counts'];
      if (raw) layerCounts = JSON.parse(raw);
    } catch { /* ignore */ }

    return {
      blob: response.data as Blob,
      filename,
      layerCounts,
    };
  }

  // ========== BOM PRICING PICKER ==========

  /**
   * Get the parts that will appear in the BOM for the given scope. Used by
   * the pricing picker to show only relevant rows. Same scope/selection
   * semantics as generateBOM.
   */
  async previewBomParts(
    projectId: string,
    scope: 'full' | 'selection' = 'full',
    elementIds?: string[]
  ): Promise<{
    parts: BomPreviewPart[];
    currency: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post('/project/bom/preview-parts', {
        projectId,
        scope,
        element_ids: elementIds,
      })
    );
    return data;
  }

  /**
   * For a list of part IDs, get every selectable price option (Average,
   * Lowest, Highest, every individual source/PR record, and the catalog
   * default). Used to populate the per-part dropdown in the picker.
   */
  async getBomPriceOptions(
    projectId: string,
    partIds: string[]
  ): Promise<{
    options: Record<string, BomPartPriceOptions>;
    cost_module_available: boolean;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post('/project/bom/price-options', {
        projectId,
        part_ids: partIds,
      })
    );
    return data;
  }

  /**
   * Read saved per-part pricing overrides for a project.
   */
  async getBomPricingOverrides(
    projectId: string
  ): Promise<{ overrides: Record<string, BomPricingOverride> }> {
    const { data } = await this.withFallback((client) =>
      client.get('/project/bom/pricing-overrides', {
        params: { projectId },
      })
    );
    return data;
  }

  /**
   * Persist per-part pricing overrides for a project (replaces existing).
   * Pass {} to clear.
   */
  async setBomPricingOverrides(
    projectId: string,
    overrides: Record<string, BomPricingOverride>
  ): Promise<{ success: boolean; path: string; count: number }> {
    const { data } = await this.withFallback((client) =>
      client.post('/project/bom/pricing-overrides', {
        projectId,
        overrides,
      })
    );
    return data;
  }

  // =============================================================================
  // MUTATION LOG API
  // =============================================================================

  /**
   * Mutation log entry interface.
   */
  

  /**
   * Get mutation log for a project (all CRUD operations).
   * Returns most recent entries first.
   */
  async getMutationLog(
    projectId: string,
    limit: number = 100,
    filters: { country?: string; org?: string; site?: string; employee?: string } = {},
  ): Promise<{
    status: string;
    projectId: string;
    projectCode: string;
    context: { country: string; org: string; site: string; name: string };
    applied: { country: string; org: string; site: string; employee: string };
    facets: {
      countries: string[];
      orgs: string[];
      sites: Array<{ code: string; name: string }>;
      employees: Array<{ user: string; name: string; hr_url: string | null }>;
    };
    entries: Array<{
      timestamp: string;
      user: string;
      user_name?: string | null;
      hr_url?: string | null;
      action: string;
      element_type: string;
      details: Record<string, any>;
      project_code?: string;
      project_name?: string;
      country?: string;
      org?: string;
    }>;
    count: number;
  }> {
    const params: Record<string, string | number> = { limit };
    if (filters.country) params.country = filters.country;
    if (filters.org) params.org = filters.org;
    if (filters.site) params.site = filters.site;
    if (filters.employee) params.employee = filters.employee;
    const { data } = await this.withFallback((client) =>
      client.get(`/project/mutation-log/${encodeURIComponent(projectId)}`, { params })
    );
    return data;
  }

  // =============================================================================
  // PROJECT MANAGEMENT API
  // =============================================================================

  /**
   * Project summary returned from list endpoint.
   */
  

  /**
   * List all projects with their latest version info.
   *
   * scope:
   *  - 'all'    (default) — published projects (backward-compatible).
   *  - 'shared' — same as 'all'.
   *  - 'mine'   — user's workspace drafts + their owned published projects.
   */
  async listProjects(scope: 'all' | 'shared' | 'mine' | 'archived' = 'all'): Promise<{
    projects: Array<{
      key?: string;
      name: string;
      code?: string;
      project_type?: string;
      portfolio?: string;
      country?: string;
      lv_policy?: string;
      created_at: string;
      version_count: number;
      latest_version: string | null;
      latest_uploaded_at: string | null;
      state?: 'draft' | 'published';
      workspace?: string | null;
      manifest?: {
        schema_version: string;
        project_id: string;
        display_name: string;
        state: 'draft' | 'published';
        workspace?: string | null;
        owner?: string | null;
        published_at?: string | null;
        published_by?: string | null;
        pipeline_state: Record<string, boolean>;
        datasets_present: string[];
        datasets_missing: string[];
      };
    }>;
    scope?: string;
    user?: string | null;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get('/projects', { params: { scope } })
    );
    return data;
  }

  async getMapAnchors(scope: 'all' | 'shared' | 'mine' | 'archived' = 'all'): Promise<{
    anchors: Array<{
      key: string;
      lat: number;
      lng: number;
      source: string;
      boundary?: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
    }>;
    unlocated_keys: string[];
  }> {
    const { data } = await this.withFallback((client) =>
      client.get('/projects/map-anchors', { params: { scope } })
    );
    return data;
  }

  async putProjectBoundary(projectKey: string, body: GeoJSON.Feature | GeoJSON.Geometry): Promise<{ ok: boolean; boundary: GeoJSON.Geometry }> {
    const { data } = await this.withFallback((client) =>
      client.put(`/projects/${encodeURIComponent(projectKey)}/boundary`, body)
    );
    return data;
  }

  async deleteProjectBoundary(projectKey: string): Promise<{ ok: boolean; removed: boolean }> {
    const { data } = await this.withFallback((client) =>
      client.delete(`/projects/${encodeURIComponent(projectKey)}/boundary`)
    );
    return data;
  }

  async importProjectBoundary(projectKey: string, file: File): Promise<{ ok: boolean; boundary: GeoJSON.Geometry; source: string }> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await this.withFallback((client) =>
      client.post(`/projects/${encodeURIComponent(projectKey)}/boundary/import`, form)
    );
    return data;
  }

  /**
   * Publish a workspace draft into the shared registry.
   */
  async publishProject(projectName: string): Promise<{
    success: boolean;
    project_name: string;
    state: 'published';
    path: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/projects/${encodeURIComponent(projectName)}/publish`)
    );
    return data;
  }

  /**
   * Unpublish a published project back to the caller's workspace.
   */
  async unpublishProject(projectName: string): Promise<{
    success: boolean;
    project_name: string;
    state: 'draft';
    path: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/projects/${encodeURIComponent(projectName)}/unpublish`)
    );
    return data;
  }

  /**
   * Fork a published project into the caller's workspace as a new draft.
   */
  async forkProject(
    projectName: string,
    opts?: { new_name?: string; new_code?: string }
  ): Promise<{
    success: boolean;
    project_name: string;
    forked_from: string;
    state: 'draft';
    path: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/projects/${encodeURIComponent(projectName)}/fork`, opts || {})
    );
    return data;
  }

  /**
   * Create a new empty draft directly in the caller's workspace.
   */
  async createDraftProject(metadata: {
    name: string;
    code: string;
    project_type?: string;
    portfolio?: string;
    country?: string;
    lv_policy?: string;
  }): Promise<{
    success: boolean;
    project_name: string;
    state: 'draft';
    path: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post('/projects/create-draft', {
        name: metadata.name,
        code: metadata.code,
        project_type: metadata.project_type || 'minigrid',
        portfolio: metadata.portfolio || '',
        country: metadata.country || '',
        lv_policy: metadata.lv_policy || '',
      })
    );
    return data;
  }

  /**
   * Get the manifest for a project (workspace draft preferred, then published).
   */
  /**
   * Mark a dataset slot in the manifest as present (call after a successful
   * tool run: TMY fetch, sizing optimize, predict simulate, etc.).
   */
  async updateManifestDataset(
    projectName: string,
    datasetType: string,
    opts?: { source?: string; extra?: Record<string, unknown> }
  ): Promise<{
    project_name: string;
    state: 'draft' | 'published';
    dataset_type: string;
    manifest: {
      pipeline_state: Record<string, boolean>;
      [k: string]: unknown;
    };
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(
        `/projects/${encodeURIComponent(projectName)}/manifest/dataset/${encodeURIComponent(datasetType)}`,
        opts || {}
      )
    );
    return data;
  }

  /**
   * Save a JSON dataset payload to a project AND atomically flip the
   * matching manifest slot. This is the canonical "save tool output
   * to project" path for JSON-typed datasets (tmy, demand_8760,
   * sizing, prediction, gensite_perimeter).
   *
   * Closes the historical "Hub shows green but tool comes back
   * empty" gap (RCA 2026-05-01): pre-v1.9.7.19 callers used
   * updateManifestDataset which only flipped the manifest flag;
   * the actual JSON lived in browser state and was lost on
   * navigation. saveProjectDataset persists the payload to disk
   * so a re-mount can call getProjectDataset and rehydrate.
   *
   * Returns the full updated manifest so the caller can refresh
   * any pipeline-state-dependent UI without an extra round trip.
   */
  async saveProjectDataset(
    projectName: string,
    datasetType: string,
    payload: unknown,
    opts?: { source?: string; extra?: Record<string, unknown> }
  ): Promise<{
    project_name: string;
    state: 'draft' | 'published';
    dataset_type: string;
    saved_to: string;
    manifest: { pipeline_state: Record<string, boolean>; [k: string]: unknown };
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(
        `/projects/${encodeURIComponent(projectName)}/datasets/${encodeURIComponent(datasetType)}`,
        { payload, source: opts?.source || null, extra: opts?.extra || null }
      )
    );
    return data;
  }

  /**
   * Read back a previously-saved JSON dataset payload, or
   * ``{ saved: false }`` if the dataset has never been saved.
   *
   * Designed to be called on page mount: every JSON-output tool
   * (DesignPlant, Predict, etc.) should call this before showing
   * an empty form, so a returning user sees their last-saved state
   * rather than a blank slate.
   */
  async listReferenceLayers(): Promise<{ layers: ReferenceLayerInfo[] }> {
    const { data } = await this.withFallback((client) => client.get('/reference-layers'));
    return data;
  }

  async getReferenceLayer(country: string, layerId: string): Promise<any> {
    const { data } = await this.withFallback((client) =>
      client.get(`/reference-layers/${encodeURIComponent(country)}/${encodeURIComponent(layerId)}`)
    );
    return data;
  }

  async getProjectDataset<T = unknown>(
    projectName: string,
    datasetType: string,
  ): Promise<{
    project_name: string;
    state: 'draft' | 'published';
    dataset_type: string;
    saved: boolean;
    saved_at?: string | null;
    saved_by?: string | null;
    source?: string | null;
    payload?: T;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get(
        `/projects/${encodeURIComponent(projectName)}/datasets/${encodeURIComponent(datasetType)}`
      )
    );
    return data;
  }

  /**
   * Promote a NewSite survey draft into the project's canonical
   * Connections layer. Backed by POST /projects/{name}/connections/
   * promote-from-draft which reads datasets/connections_draft.json
   * (saved via saveProjectDataset(name, 'connections_draft', ...))
   * and writes GIS_Files/Connections.shp.
   *
   * Modes:
   *   - 'replace' : overwrite existing Connections layer entirely.
   *                 Best for greenfield sites where the draft IS
   *                 the survey.
   *   - 'append'  : combine draft with existing connections,
   *                 renaming colliding draft IDs (e.g. CONN_001 ->
   *                 CONN_001_draft2). For iteratively refining a
   *                 partially-imported survey.
   *
   * Caller's responsibility: ensure the project has an active
   * session (otherwise the planner won't see the new connections
   * until reload). The backend returns 409 when no session is
   * active to make this explicit.
   */
  async promoteConnectionsDraft(
    projectName: string,
    mode: 'replace' | 'append',
  ): Promise<{
    ok: boolean;
    project_name: string;
    state: 'draft' | 'published';
    mode: 'replace' | 'append';
    count_promoted: number;
    count_total: number;
    saved_to: string;
    manifest: { pipeline_state: Record<string, boolean>; [k: string]: unknown };
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(
        `/projects/${encodeURIComponent(projectName)}/connections/promote-from-draft`,
        { mode, require_session: true },
      )
    );
    return data;
  }

  /**
   * One-call summary of every artifact saved to a project. Used by
   * the Hub's Project Artifacts panel + any "what's in this project"
   * UI. Combines manifest dataset slots with the on-disk 8760 runs
   * directory + GIS layer counts.
   */
  async getProjectArtifacts(projectName: string): Promise<{
    project_name: string;
    project_state: 'draft' | 'published';
    artifacts: Record<string, {
      saved: boolean;
      saved_at?: string | null;
      saved_by?: string | null;
      source?: string | null;
      path?: string | null;
      [k: string]: unknown;
    }>;
    eight_seventy_sixty_runs: Array<{
      run_id: string;
      run_name?: string | null;
      saved_at?: string | null;
      peak_kw?: number | null;
      annual_kwh?: number | null;
      load_factor?: number | null;
      n_customers?: number | null;
      incomplete?: boolean;
    }>;
    manifest_pipeline_state: Record<string, boolean>;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get(`/projects/${encodeURIComponent(projectName)}/artifacts`)
    );
    return data;
  }

  /**
   * Clear a manifest dataset slot (mark as not-yet-created).
   */
  async clearManifestDataset(
    projectName: string,
    datasetType: string
  ): Promise<{
    project_name: string;
    state: 'draft' | 'published';
    dataset_type: string;
    manifest: {
      pipeline_state: Record<string, boolean>;
      [k: string]: unknown;
    };
  }> {
    const { data } = await this.withFallback((client) =>
      client.delete(
        `/projects/${encodeURIComponent(projectName)}/manifest/dataset/${encodeURIComponent(datasetType)}`
      )
    );
    return data;
  }

  /**
   * Save a project's exclusion-zones FeatureCollection as the definitive copy.
   */
  async saveProjectExclusionZones(
    projectName: string,
    geojson: unknown,
    source?: string
  ): Promise<{
    project_name: string;
    state: 'draft' | 'published';
    path: string;
    feature_count: number;
    manifest: Record<string, unknown>;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(
        `/projects/${encodeURIComponent(projectName)}/exclusion-zones`,
        { geojson, source: source || null }
      )
    );
    return data;
  }

  /**
   * Fetch the previously-saved exclusion zones for a project, if any.
   */
  async loadProjectExclusionZones(
    projectName: string
  ): Promise<{
    project_name: string;
    state: 'draft' | 'published';
    geojson: unknown;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get(`/projects/${encodeURIComponent(projectName)}/exclusion-zones`)
    );
    return data;
  }

  async getProjectManifest(projectName: string): Promise<{
    project_name: string;
    state: 'draft' | 'published';
    manifest: {
      schema_version: string;
      project_id: string;
      display_name: string;
      country: string;
      created_by?: string | null;
      created_at: string;
      owner?: string | null;
      state: 'draft' | 'published';
      workspace?: string | null;
      published_at?: string | null;
      published_by?: string | null;
      datasets: Record<
        string,
        {
          type: string;
          path: string;
          format: string;
          depends_on: string[];
          updated_at: string | null;
          updated_by?: string | null;
          source?: string | null;
          count?: number;
          peak_kw?: number;
          annual_kwh?: number;
          poles?: number;
          lines?: number;
          location?: { lat: number; lon: number };
          hours?: number;
          perimeter_m?: number;
          notes?: string;
        }
      >;
      pipeline_state: Record<string, boolean>;
    };
  }> {
    const { data } = await this.withFallback((client) =>
      client.get(`/projects/${encodeURIComponent(projectName)}/manifest`)
    );
    return data;
  }

  async getProjectsPipelineStatus(): Promise<{
    generated_at: string;
    projects: Array<{
      key: string;
      name: string;
      code: string;
      country: string;
      state: 'draft' | 'published';
      pipeline_state: Record<string, boolean>;
      datasets: Record<string, { updated_at: string | null; updated_by?: string | null }>;
      last_update: string | null;
    }>;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get('/projects-pipeline-status')
    );
    return data;
  }

  /**
   * List all versions of a specific project, with their design state (W3).
   */
  async listProjectVersions(projectName: string): Promise<{
    project_name: string;
    created_at: string;
    versions: Array<{
      timestamp: string;
      uploaded_at: string;
      path: string;
      comments?: NoteEntry[];
      modified_by?: string | null;
      modified_by_name?: string | null;
      modified_by_name_source?: string | null;
      modified_by_hr_url?: string | null;
      design_state?: DesignState;
      design_state_history?: DesignStateHistoryEntry[];
      derived_from?: string | null;
    }>;
    approved_version_id?: string | null;
    frozen_version_id?: string | null;
    approved?: { version_id: string; by?: string | null; by_name?: string | null; at?: string | null; note?: string | null } | null;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get(`/projects/${encodeURIComponent(projectName)}/versions`)
    );
    return data;
  }

  /**
   * Freeze, approve or supersede a version. A note is mandatory; requestId
   * makes a retry return the first result; expectedState gives 409 on a stale UI.
   */
  async changeVersionDesignState(
    projectName: string,
    versionId: string,
    action: DesignStateAction,
    body: { note: string; request_id: string; expected_state?: DesignState; supersede_candidate?: boolean; load_basis_override_note?: string }
  ): Promise<{
    success: boolean;
    version_id: string;
    action: DesignStateAction;
    from_state: DesignState;
    design_state: DesignState;
    superseded: Array<{ version_id: string }>;
    approved_version_id: string | null;
    frozen_version_id: string | null;
    idempotent: boolean;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(
        `/projects/${encodeURIComponent(projectName)}/versions/${encodeURIComponent(versionId)}/${action}`,
        body
      )
    );
    return data;
  }

  /** Design-state audit log of a project (applied and refused transitions). */
  async getDesignStateAudit(projectName: string, versionId?: string): Promise<{
    project_id: string;
    count: number;
    entries: DesignStateAuditEntry[];
  }> {
    const { data } = await this.withFallback((client) =>
      client.get(`/projects/${encodeURIComponent(projectName)}/design-state/audit`, {
        params: versionId ? { version_id: versionId } : undefined,
      })
    );
    return data;
  }

  async addVersionComment(
    projectName: string,
    versionId: string,
    content: string
  ): Promise<{ status: string; comment: NoteEntry }> {
    const { data } = await this.withFallback((client) =>
      client.post(
        `/projects/${encodeURIComponent(projectName)}/versions/${encodeURIComponent(versionId)}/comments`,
        { content }
      )
    );
    return data;
  }

  async updateVersionComment(
    projectName: string,
    versionId: string,
    commentId: string,
    content: string
  ): Promise<{ status: string; comment: NoteEntry }> {
    const { data } = await this.withFallback((client) =>
      client.put(
        `/projects/${encodeURIComponent(projectName)}/versions/${encodeURIComponent(versionId)}/comments/${encodeURIComponent(commentId)}`,
        { content }
      )
    );
    return data;
  }

  async deleteVersionComment(
    projectName: string,
    versionId: string,
    commentId: string
  ): Promise<{ status: string; comments_count: number }> {
    const { data } = await this.withFallback((client) =>
      client.delete(
        `/projects/${encodeURIComponent(projectName)}/versions/${encodeURIComponent(versionId)}/comments/${encodeURIComponent(commentId)}`
      )
    );
    return data;
  }

  /**
   * Load a specific version of a project into memory for editing.
   * @param projectName - The project name
   * @param version - Optional version timestamp. If not provided, loads latest version.
   * @returns Project ID and metadata for the loaded version
   */
  async loadProject(projectName: string, version?: string): Promise<{
    projectId: string;
    project_name: string;
    version: string;
    path: string;
    last_modified_by?: string;
    last_modified_at?: string;
    recovery_snapshots?: Array<{
      snapshot_id: string;
      timestamp: string;
      username: string;
      project_name: string;
      summary: string;
      elements: Record<string, { count: number; saved_at: string }>;
    }>;
    design_state?: DesignState;
    design_state_entry?: DesignStateHistoryEntry | null;
    read_only?: boolean;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/projects/${encodeURIComponent(projectName)}/load`, {
        version: version || null
      })
    );
    notifyRestoredUnsaved((data as any)?.restored_unsaved);
    return data;
  }

  /**
   * Create a new empty project (no elements) with the given metadata.
   * Returns a loaded session so the user can immediately start adding elements.
   */
  async createEmptyProject(metadata: {
    name: string;
    code: string;
    project_type?: string;
    portfolio?: string;
    country?: string;
    lv_policy?: string;
    site_role?: 'sandbox' | 'variant' | 'canonical';
    canonical_site_code?: string;
    variant_label?: string;
    bind_confirmed?: boolean;
    proximity_confirmed?: boolean;
  }): Promise<{
    projectId: string;
    project_name: string;
    projectCode: string;
    projectType: string;
    lv_policy?: string;
    site_role?: string;
    canonical_site_code?: string;
    version: string;
    path: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post('/projects/create-empty', {
        name: metadata.name,
        code: metadata.code,
        project_type: metadata.project_type || 'minigrid',
        portfolio: metadata.portfolio || '',
        country: metadata.country || '',
        lv_policy: metadata.lv_policy || '',
        site_role: metadata.site_role || 'sandbox',
        canonical_site_code: metadata.canonical_site_code || '',
        variant_label: metadata.variant_label || '',
        bind_confirmed: metadata.bind_confirmed === true,
        proximity_confirmed: metadata.proximity_confirmed === true,
      })
    );
    return data;
  }

  async createProjectFromNetwork(metadata: {
    name: string;
    code: string;
    project_type?: string;
    portfolio?: string;
    country?: string;
    lv_policy?: string;
    site_role?: 'sandbox' | 'variant' | 'canonical';
    canonical_site_code?: string;
    variant_label?: string;
    snap_m?: number;
    file: File;
  }): Promise<{
    projectId: string;
    project_name: string;
    projectCode: string;
    projectType: string;
    version: string;
    pole_count: number;
    line_count: number;
    source_name?: string;
  }> {
    const form = new FormData();
    form.append('name', metadata.name);
    form.append('code', metadata.code);
    form.append('project_type', metadata.project_type || 'minigrid');
    form.append('portfolio', metadata.portfolio || '');
    form.append('country', metadata.country || '');
    form.append('lv_policy', metadata.lv_policy || '');
    form.append('site_role', metadata.site_role || 'sandbox');
    form.append('canonical_site_code', metadata.canonical_site_code || '');
    form.append('variant_label', metadata.variant_label || '');
    form.append('snap_m', String(metadata.snap_m ?? 25));
    form.append('file', metadata.file, metadata.file.name);
    const { data } = await this.withFallback((client) =>
      client.post('/projects/create-from-network', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      })
    );
    return data;
  }

  /** PR canonical site registry (proxied — the browser never sees the PR API key). */
  async listCanonicalSites(country?: string): Promise<Array<{
    id: string;
    code: string | null;
    name: string;
    countryCode: string | null;
    organizationId: string | null;
    canonicalUgpProjectId: string | null;
  }>> {
    const { data } = await this.withFallback((client) =>
      client.get('/api/canonical-sites', { params: country ? { country } : {} })
    );
    return data.sites || [];
  }

  /** Remediation: attach/detach the canonical site association on an existing design. */
  async setSiteAssociation(
    projectKey: string,
    siteRole: 'sandbox' | 'variant' | 'canonical',
    canonicalSiteCode = '',
    options: { variant_label?: string; bind_confirmed?: boolean; proximity_confirmed?: boolean } = {},
  ): Promise<{
    ok: boolean;
    project: string;
    site_role: string;
    canonical_site_code: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/projects/${encodeURIComponent(projectKey)}/site-association`, {
        site_role: siteRole,
        canonical_site_code: canonicalSiteCode,
        variant_label: options.variant_label || '',
        bind_confirmed: options.bind_confirmed === true,
        proximity_confirmed: options.proximity_confirmed === true,
      })
    );
    return data;
  }

  /**
   * Push Design Grid / New Site connection rows into a project session (shapefile + cache).
   * Required for 8760 and Track Construction after \"Open in uGridPLAN\".
   */
  async importSurveyConnections(
    projectId: string,
    connections: Array<{
      site_number: string;
      connection_type: string;
      latitude: number;
      longitude: number;
      hh_pp?: number;
    }>
  ): Promise<{ ok: boolean; count: number; path?: string }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/projects/${encodeURIComponent(projectId)}/import-survey-connections`, {
        connections: connections.map((c) => ({
          site_number: c.site_number,
          connection_type: c.connection_type,
          latitude: c.latitude,
          longitude: c.longitude,
          hh_pp: c.hh_pp ?? 1,
        })),
      })
    );
    return data;
  }

  /**
   * Merge multiple projects into a new project on the backend.
   * The backend loads each source project's GeoDataFrames, prefixes IDs,
   * combines them, and saves as a new registered project.
   */
  async mergeProjects(request: {
    projects: Array<{ key: string; version: string; name: string }>;
    element_types: { poles: boolean; connections: boolean; lines: boolean; generations: boolean };
    new_project: {
      name: string;
      code: string;
      project_type?: string;
      portfolio?: string;
      country?: string;
      lv_policy?: string;
    };
  }): Promise<{
    projectId: string;
    project_name: string;
    projectCode: string;
    projectType: string;
    version: string;
    path: string;
    counts: Record<string, number>;
    failed: string[];
  }> {
    const { data } = await this.withFallback((client) =>
      client.post('/projects/merge', request)
    );
    return data;
  }

  /**
   * Apply a recovery snapshot to the current session.
   * @param projectName - The project name
   * @param snapshotId - The snapshot ID to recover
   * @param projectId - The current session's project ID
   */
  async applyRecoverySnapshot(projectName: string, snapshotId: string, projectId: string): Promise<{
    status: string;
    message: string;
    recovered_elements: Record<string, number>;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/projects/${encodeURIComponent(projectName)}/recovery/apply`, {
        snapshot_id: snapshotId,
        project_id: projectId
      })
    );
    return data;
  }

  /**
   * Delete a recovery snapshot (discard unsaved changes).
   * @param projectName - The project name
   * @param snapshotId - The snapshot ID to delete
   */
  async deleteRecoverySnapshot(projectName: string, snapshotId: string): Promise<{
    status: string;
    message: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/projects/${encodeURIComponent(projectName)}/recovery/delete`, {
        snapshot_id: snapshotId
      })
    );
    return data;
  }

  /**
   * Stash a recovery snapshot for later.
   * @param projectName - The project name
   * @param snapshotId - The snapshot ID to stash
   */
  async stashRecoverySnapshot(projectName: string, snapshotId: string): Promise<{
    status: string;
    message: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/projects/${encodeURIComponent(projectName)}/recovery/stash`, {
        snapshot_id: snapshotId
      })
    );
    return data;
  }

  /**
   * Delete a specific version of a project.
   * @param projectName - The project name/code
   * @param versionId - The version timestamp to delete
   * @returns Success status and remaining version count
   */
  async archiveVersion(projectName: string, versionId: string): Promise<{
    success: boolean;
    project: string;
    version_id: string;
    unique: boolean;
    reason: string;
    analytics_keep?: boolean;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/projects/${encodeURIComponent(projectName)}/versions/${encodeURIComponent(versionId)}/archive`)
    );
    return data;
  }

  async restoreVersion(projectName: string, versionId: string): Promise<{
    success: boolean;
    project: string;
    version_id: string;
    restored: boolean;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/projects/${encodeURIComponent(projectName)}/versions/${encodeURIComponent(versionId)}/restore`)
    );
    return data;
  }

  async archiveProject(projectName: string): Promise<{
    success: boolean;
    project_key: string;
    archived_versions: number;
    unique_kept_for_analytics: number;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/projects/${encodeURIComponent(projectName)}/archive`)
    );
    return data;
  }

  async restoreProject(projectName: string): Promise<{
    success: boolean;
    project: string;
    restored_versions: number;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/projects/${encodeURIComponent(projectName)}/restore`)
    );
    return data;
  }

  async deleteVersion(projectName: string, versionId: string): Promise<{
    success: boolean;
    deleted_version: string;
    remaining_versions: number;
  }> {
    const { data } = await this.withFallback((client) =>
      client.delete(`/projects/${encodeURIComponent(projectName)}/versions/${encodeURIComponent(versionId)}`)
    );
    return data;
  }

  /**
   * Delete an entire project and all its versions.
   * @param projectName - The project name/code to delete
   * @returns Success status and count of deleted versions
   */
  async deleteProject(projectName: string): Promise<{
    success: boolean;
    deleted_project: string;
    deleted_versions: number;
  }> {
    const { data } = await this.withFallback((client) =>
      client.delete(`/projects/${encodeURIComponent(projectName)}`)
    );
    return data;
  }

  /**
   * Update project metadata (name, code, portfolio, country).
   * Note: If code is changed, the project will be renamed (key and folder).
   * @param projectName - Current project name/code
   * @param updates - Fields to update
   * @returns Updated project info
   */
  async updateProject(projectName: string, updates: {
    name?: string;
    code?: string;
    project_type?: string;
    portfolio?: string;
    country?: string;
    lv_policy?: string;
    target_connections?: number;
  }): Promise<{
    success: boolean;
    project: string;
    old_values: { name: string; code: string; project_type: string; portfolio: string; country: string; lv_policy?: string };
    new_values: { name: string; code: string; project_type: string; portfolio: string; country: string; lv_policy?: string };
  }> {
    const { data } = await this.withFallback((client) =>
      client.put(`/projects/${encodeURIComponent(projectName)}`, updates)
    );
    return data;
  }

  /**
   * Migrate/repair version timestamps by extracting them from nested folder structures,
   * then deduplicate (keep only latest per date, except today).
   * Fixes versions imported with 'baseline_*' names that contain Archive/Updated_* folders.
   * @param projectName - The project name/code to migrate
   * @returns Migration results
   */
  async migrateProjectVersions(projectName: string): Promise<{
    success: boolean;
    project_name: string;
    migrated: Array<{ old_timestamp: string; new_timestamp: string; source_folder: string }>;
    skipped: Array<{ timestamp: string; reason: string }>;
    errors: Array<{ timestamp: string; reason: string }>;
    deleted_duplicates: Array<{ timestamp: string; uploaded_at: string }>;
    summary: { 
      total_migrated: number; 
      total_skipped: number; 
      total_errors: number;
      total_deleted_duplicates: number;
      remaining_versions: number;
    };
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/projects/${encodeURIComponent(projectName)}/migrate-versions`)
    );
    return data;
  }

  /**
   * Deduplicate versions within a project.
   * Keeps all versions for today, but only the latest upload per date for past dates.
   * @param projectName - The project name/code to deduplicate
   * @returns Deduplication results
   */
  async deduplicateProjectVersions(projectName: string): Promise<{
    success: boolean;
    project_name: string;
    original_count: number;
    remaining_count: number;
    deleted_count: number;
    deleted: Array<{ timestamp: string; uploaded_at: string }>;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/projects/${encodeURIComponent(projectName)}/deduplicate`)
    );
    return data;
  }

  /**
   * Export a project version as a ZIP file containing shapefiles and metadata.
   * The ZIP is structured to be re-importable.
   * @param projectName - The project name/code
   * @param version - Optional version timestamp. If not provided, exports latest version.
   */
  async exportProjectZip(projectName: string, version?: string): Promise<void> {
    const params = version ? `?version=${encodeURIComponent(version)}` : '';
    const baseUrl = this.api.defaults.baseURL || API_BASE_URL;
    const url = `${baseUrl}/projects/${encodeURIComponent(projectName)}/export-zip${params}`;
    
    // Use fetch for file download with proper auth cookie handling
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Export failed: ${response.status} ${errorText}`);
    }
    
    // Get filename from Content-Disposition header or generate one
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `${projectName}_export.zip`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) {
        filename = match[1];
      }
    }
    
    // Download the file
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  /**
   * Migrate a project's versions to delta-based storage.
   * Converts full snapshot versions to efficient delta format.
   * @param projectName - The project name/code to migrate
   * @param deleteOriginals - Whether to delete original full snapshots after migration
   * @returns Migration results with storage savings
   */
  async migrateProjectToDeltas(projectName: string, deleteOriginals: boolean = false): Promise<{
    success: boolean;
    project_name: string;
    versions_processed: number;
    keyframes_created: string[];
    deltas_created: string[];
    storage_before: number;
    storage_after: number;
    storage_before_formatted: string;
    storage_after_formatted: string;
    savings_percentage: number;
    deleted_originals: string[];
    errors: Array<{ version: string; error: string }>;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(
        `/projects/${encodeURIComponent(projectName)}/migrate-to-deltas?delete_originals=${deleteOriginals}`,
        {},
        { timeout: 1800000 } // 30 minute timeout for large projects with many versions
      )
    );
    return data;
  }

  /**
   * Get storage statistics for a project.
   * @param projectName - The project name/code
   * @returns Storage stats including delta migration status
   */
  async getProjectStorageStats(projectName: string): Promise<{
    project_name: string;
    version_count: number;
    total_size: number;
    total_size_formatted: string;
    delta_storage_enabled: boolean;
    delta_storage_size: number;
    delta_storage_size_formatted: string;
    keyframe_count: number;
    delta_count: number;
    potential_savings_if_migrated: boolean;
    versions: Array<{ timestamp: string; size: number; size_formatted: string }>;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get(`/projects/${encodeURIComponent(projectName)}/storage-stats`)
    );
    return data;
  }

  /**
   * Upload a new project or version to persistent storage.
   * @param files - Files to upload (folder structure)
   * @param projectName - Optional project name. If not provided, inferred from folder structure.
   * @returns Project ID and metadata for the uploaded version
   */
  async uploadProjectPersistent(
    files: FileList | File[],
    projectName?: string
  ): Promise<{
    projectId: string;
    project_name: string;
    version: string;
    path: string;
  }> {
    const formData = new FormData();
    
    // Add files with their relative paths
    for (let i = 0; i < files.length; i++) {
      const file = files[i] as File & { webkitRelativePath?: string };
      const relativePath = file.webkitRelativePath || file.name;
      formData.append('files', file, relativePath);
    }
    
    const url = projectName 
      ? `/projects/upload?project_name=${encodeURIComponent(projectName)}`
      : '/projects/upload';
    
    const { data } = await this.withFallback((client) =>
      client.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000 // 5 minutes for large uploads
      })
    );
    return data;
  }

  /**
   * Bulk import multiple project folders at once.
   * @param files - Files from multiple project folders
   * @returns Summary of imported projects and any that were skipped
   */
  async bulkImportProjects(files: FileList | File[]): Promise<{
    status: string;
    imported: Array<{
      project_name: string;
      version: string;
      source_folder: string;
    }>;
    skipped: Array<{
      name: string;
      reason: string;
    }>;
    summary: {
      total_imported: number;
      total_skipped: number;
      unique_projects: number;
    };
  }> {
    console.log(`[API] bulkImportProjects: Preparing ${files.length} files`);
    const formData = new FormData();
    
    // Add files with their relative paths
    let totalSize = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i] as File & { webkitRelativePath?: string };
      const relativePath = file.webkitRelativePath || file.name;
      formData.append('files', file, relativePath);
      totalSize += file.size;
    }
    
    console.log(`[API] bulkImportProjects: Total size ${(totalSize / 1024 / 1024).toFixed(2)} MB, sending request...`);
    
    const { data } = await this.withFallback((client) =>
      client.post('/projects/bulk-import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000, // 10 minutes for large bulk imports
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            console.log(`[API] bulkImportProjects: Upload progress ${percent}%`);
          }
        }
      })
    );
    console.log('[API] bulkImportProjects: Complete', data);
    return data;
  }

  // Upload a single snapshot with progress callback
  async uploadSnapshotWithProgress(
    formData: FormData,
    onProgress: (percent: number) => void
  ): Promise<any> {
    const { data } = await this.withFallback((client) =>
      client.post('/projects/bulk-import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000, // 10 minutes per snapshot
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            onProgress(percent);
          }
        }
      })
    );
    return data;
  }

  // Get countries list (public, no auth required for bulk import dropdown)
  async getCountriesPublic(): Promise<Array<{ code: string; name: string }>> {
    try {
      const { data } = await this.withFallback((client) => 
        client.get('/reference-lists/countries')
      );
      return data;
    } catch {
      // Return fallback list if endpoint not available
      return [
        { code: 'SL', name: 'Sierra Leone' },
        { code: 'MW', name: 'Malawi' },
        { code: 'ZM', name: 'Zambia' },
        { code: 'TZ', name: 'Tanzania' },
        { code: 'KE', name: 'Kenya' },
        { code: 'UG', name: 'Uganda' },
        { code: 'RW', name: 'Rwanda' },
        { code: 'CD', name: 'DR Congo' },
        { code: 'ZA', name: 'South Africa' },
        { code: 'MZ', name: 'Mozambique' },
        { code: 'ZW', name: 'Zimbabwe' },
      ];
    }
  }

  // Get portfolios list (public, no auth required for bulk import dropdown)
  async getPortfoliosPublic(): Promise<Array<{ id: string; name: string }>> {
    try {
      const { data } = await this.withFallback((client) => 
        client.get('/reference-lists/portfolios')
      );
      return data;
    } catch {
      // Return fallback list if endpoint not available
      return [
        { id: 'sierra_leone', name: 'Sierra Leone' },
        { id: 'malawi', name: 'Malawi' },
        { id: 'zambia', name: 'Zambia' },
        { id: 'tanzania', name: 'Tanzania' },
        { id: 'kenya', name: 'Kenya' },
        { id: 'uganda', name: 'Uganda' },
        { id: 'rwanda', name: 'Rwanda' },
        { id: 'drc', name: 'DRC' },
        { id: 'other', name: 'Other' },
      ];
    }
  }

  // =============================================================================
  // ANALYTICS METHODS
  // =============================================================================

  /**
   * Get available filter options for analytics dashboard.
   */
  async getAnalyticsFilters(): Promise<AnalyticsFiltersResponse> {
    const { data } = await this.withFallback((client) =>
      client.get('/analytics/filters')
    );
    return data;
  }

  /**
   * Get analytics time series metrics.
   */
  async getAnalyticsTimeSeries(params: {
    projects?: string[];
    portfolio?: string;
    country?: string;
  }): Promise<AnalyticsTimeSeriesResponse> {
    const queryParams = new URLSearchParams();
    if (params.projects && params.projects.length > 0) {
      queryParams.set('projects', params.projects.join(','));
    }
    if (params.portfolio) {
      queryParams.set('portfolio', params.portfolio);
    }
    if (params.country) {
      queryParams.set('country', params.country);
    }
    queryParams.set('metric_type', 'time_series');

    const { data } = await this.withFallback((client) =>
      client.get(`/analytics/metrics?${queryParams.toString()}`, {
        timeout: 300000, // 5 minutes for large datasets
      })
    );
    return data;
  }

  /**
   * Get analytics completion rates (box plot data).
   */
  async getAnalyticsCompletionRates(params: {
    projects?: string[];
    portfolio?: string;
    country?: string;
  }): Promise<AnalyticsCompletionRatesResponse> {
    const queryParams = new URLSearchParams();
    if (params.projects && params.projects.length > 0) {
      queryParams.set('projects', params.projects.join(','));
    }
    if (params.portfolio) {
      queryParams.set('portfolio', params.portfolio);
    }
    if (params.country) {
      queryParams.set('country', params.country);
    }
    queryParams.set('metric_type', 'completion_rates');

    const { data } = await this.withFallback((client) =>
      client.get(`/analytics/metrics?${queryParams.toString()}`, {
        timeout: 300000, // 5 minutes for large datasets
      })
    );
    return data;
  }

  /**
   * Export analytics data as Excel file.
   */
  async exportAnalytics(params: {
    projects?: string[];
    portfolio?: string;
    country?: string;
  }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params.projects && params.projects.length > 0) {
      queryParams.set('projects', params.projects.join(','));
    }
    if (params.portfolio) {
      queryParams.set('portfolio', params.portfolio);
    }
    if (params.country) {
      queryParams.set('country', params.country);
    }

    const { data } = await this.withFallback((client) =>
      client.get(`/analytics/export?${queryParams.toString()}`, {
        responseType: 'blob',
        timeout: 600000, // 10 minutes for export
      })
    );
    return data;
  }

  /**
   * Get energization coverage metrics for selected projects.
   * Returns per-project: total_connections, energizable_connections, percent.
   */
  async getAnalyticsEnergization(params: {
    projects?: string[];
    portfolio?: string;
    country?: string;
    use_target?: boolean;
  }): Promise<{ energization: Array<{
    project: string;
    name: string;
    total_connections: number;
    energizable_connections: number;
    percent: number;
    target_connections?: number;
    denominator?: number;
    error?: string;
  }> }> {
    const queryParams = new URLSearchParams();
    if (params.projects && params.projects.length > 0) {
      queryParams.set('projects', params.projects.join(','));
    }
    if (params.portfolio) {
      queryParams.set('portfolio', params.portfolio);
    }
    if (params.country) {
      queryParams.set('country', params.country);
    }
    if (params.use_target) {
      queryParams.set('use_target', 'true');
    }

    const { data } = await this.withFallback((client) =>
      client.get(`/analytics/energization?${queryParams.toString()}`, {
        timeout: 300000, // 5 minutes
      })
    );
    return data;
  }

  /**
   * Get latest-version aggregate network stats for selected projects.
   */
  async getAnalyticsAggregateStats(params: {
    projects?: string[];
    portfolio?: string;
    country?: string;
  }): Promise<AnalyticsAggregateStatsResponse> {
    const queryParams = new URLSearchParams();
    if (params.projects && params.projects.length > 0) {
      queryParams.set('projects', params.projects.join(','));
    }
    if (params.portfolio) {
      queryParams.set('portfolio', params.portfolio);
    }
    if (params.country) {
      queryParams.set('country', params.country);
    }

    const { data } = await this.withFallback((client) =>
      client.get(`/analytics/aggregate-stats?${queryParams.toString()}`, {
        timeout: 300000,
      })
    );
    return data;
  }

  /**
   * Export grouped BOM data for the selected analytics site set.
   */
  async exportAnalyticsBOM(params: {
    projects?: string[];
    portfolio?: string;
    country?: string;
    format?: 'csv' | 'xlsx';
  }): Promise<Blob> {
    const { data } = await this.withFallback((client) =>
      client.post(
        '/analytics/bom',
        {
          projects: params.projects,
          portfolio: params.portfolio,
          country: params.country,
          format: params.format || 'xlsx',
        },
        {
          responseType: 'blob',
          timeout: 600000,
        }
      )
    );
    return data;
  }

  /**
   * Refresh the server-side analytics cache.
   * This forces a full recomputation of all analytics data.
   * The previous cache is automatically archived as a snapshot.
   */
  async refreshAnalyticsCache(): Promise<{ success: boolean; message: string }> {
    const { data } = await this.withFallback((client) =>
      client.post('/analytics/refresh-cache', {}, {
        timeout: 600000, // 10 minutes for full recomputation
      })
    );
    return data;
  }

  /**
   * Get list of archived analytics cache snapshots.
   */
  async getAnalyticsCacheSnapshots(): Promise<{ snapshots: Array<{ snapshot_id: string; computed_at: string; file_size_kb: number }> }> {
    const { data } = await this.withFallback((client) =>
      client.get('/analytics/cache-snapshots')
    );
    return data;
  }

  /**
   * Load a specific archived cache snapshot.
   */
  async getAnalyticsCachedSnapshot(snapshotId: string): Promise<{
    from_cache: boolean;
    is_snapshot: boolean;
    snapshot_id: string;
    computed_at: string;
    time_series: Record<string, any>;
    completion_rates: Record<string, any>;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get(`/analytics/cached?snapshot=${encodeURIComponent(snapshotId)}`)
    );
    return data;
  }

  /**
   * Delete a specific cache snapshot.
   */
  async deleteAnalyticsCacheSnapshot(snapshotId: string): Promise<{ status: string; snapshot_id: string }> {
    const { data } = await this.withFallback((client) =>
      client.delete(`/analytics/cache-snapshots/${encodeURIComponent(snapshotId)}`)
    );
    return data;
  }

  // =============================================================================
  // 8760 LOAD PROFILE METHODS (stubs for LoadProfile8760Panel)
  // =============================================================================

  async get8760CustomerTypes(): Promise<CustomerTypesResponse> {
    try {
      const { data } = await this.withFallback((client) =>
        client.get('/8760/customer-types')
      );
      return merge8760CustomerTypesResponse(data);
    } catch (err) {
      console.error('8760 customer-types request failed; using canonical fallback', err);
      return {
        supported: FALLBACK_8760_CUSTOMER_TYPES.supported.map((r) => ({ ...r })),
        unsupported: FALLBACK_8760_CUSTOMER_TYPES.unsupported.map((r) => ({ ...r })),
      };
    }
  }

  getCdfTemplateUrl(): string {
    return `${this.api.defaults.baseURL || API_BASE_URL}/8760/cdf-template`;
  }

  async getCdfLibrary(country?: string | null): Promise<CdfLibrary> {
    const { data } = await this.withFallback((client) =>
      client.get('/8760/cdf-library', {
        params: country && String(country).trim() ? { country: String(country).trim() } : {},
      })
    );
    return data;
  }

  async adminListCdfSources(): Promise<AdminCdfRegistryResponse> {
    const { data } = await this.withFallback((client) => client.get('/admin/cdf-sources'));
    return data;
  }

  async adminGetCdfSource(sourceId: string): Promise<{ source: CdfRegistryAdminRow }> {
    const { data } = await this.withFallback((client) =>
      client.get(`/admin/cdf-sources/${encodeURIComponent(sourceId)}`)
    );
    return data;
  }

  async adminCreateCdfSource(body: Record<string, unknown>): Promise<{ ok: boolean; source: CdfRegistryAdminRow }> {
    const { data } = await this.withFallback((client) => client.post('/admin/cdf-sources', body));
    return data;
  }

  async adminUpdateCdfSource(
    sourceId: string,
    body: Record<string, unknown>
  ): Promise<{ ok: boolean; source: CdfRegistryAdminRow }> {
    const { data } = await this.withFallback((client) =>
      client.put(`/admin/cdf-sources/${encodeURIComponent(sourceId)}`, body)
    );
    return data;
  }

  async adminDeleteCdfSource(
    sourceId: string,
    opts?: { force?: boolean }
  ): Promise<{ ok: boolean; deleted_id: string; deleted_file: boolean }> {
    const { data } = await this.withFallback((client) =>
      client.delete(`/admin/cdf-sources/${encodeURIComponent(sourceId)}`, {
        params: opts?.force ? { force: true } : {},
      })
    );
    return data;
  }

  /**
   * Human-readable label for a server project session (site code, country, loaded version date).
   */
  async getProjectSessionDisplay(projectId: string): Promise<{
    session_id: string;
    project_name: string;
    code: string;
    country: string;
    version: string;
    version_display: string;
    display_label: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get('/project/session-display', { params: { projectId } })
    );
    return data;
  }

  /**
   * Connections stored on the server for 8760 (cache + GIS), for UI when map networkData is stale.
   */
  async get8760ConnectionSurvey(projectId: string): Promise<{
    count: number;
    connections: Array<{ id: string; type: string; lat: number; lng: number }>;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get(`/8760/connection-survey/${encodeURIComponent(projectId)}`)
    );
    return data;
  }

  // ---------------------------------------------------------------------
  // 8760 async job API (v1.9.2.0+)
  //
  // The sync run endpoints below are kept for backward compat but the UI
  // should prefer these: POST returns 202 with a job_id in milliseconds,
  // client polls every ~2s, server runs the MC in THREAD_POOL. Survives
  // client disconnects (refresh, mobile sleep) because status.json on the
  // run folder is the fallback source of truth.
  // ---------------------------------------------------------------------

  async startRun8760JobForProject(
    projectId: string,
    connectionIds: string[],
    params: Run8760Params,
    runName?: string,
    adjustments?: Record<string, number>
  ): Promise<{
    job_id: string;
    run_id: string;
    run_name?: string | null;
    scope: 'project';
    project_id: string;
    status: string;
    status_url: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(
        `/8760/run/${projectId}/async`,
        {
          connection_ids: connectionIds,
          params,
          run_name: runName,
          adjustments,
        },
        { timeout: 15000 }
      )
    );
    return data;
  }

  async startRun8760JobStandalone(body: {
    connection_counts: Record<string, number>;
    params: Run8760Params;
    run_name?: string;
    adjustments?: Record<string, number>;
  }): Promise<{
    job_id: string;
    run_id: string;
    run_name?: string | null;
    scope: 'standalone';
    status: string;
    status_url: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(
        `/8760/standalone/run/async`,
        {
          connection_counts: body.connection_counts,
          params: body.params,
          run_name: body.run_name,
          adjustments: body.adjustments,
        },
        { timeout: 15000 }
      )
    );
    return data;
  }

  /** Poll a single 8760 job. Returns the current status snapshot.
   *  Status: queued | running | completed | cancelled | error | interrupted.
   */
  async get8760Job(jobId: string): Promise<{
    job_id: string;
    run_id: string;
    run_dir: string;
    scope: string;
    status: 'queued' | 'running' | 'completed' | 'cancelled' | 'error' | 'interrupted';
    progress: number;
    message: string;
    month: string | null;
    created_at: string;
    started_at: string | null;
    finished_at: string | null;
    stats: Run8760Result['stats'] | null;
    error: string | null;
    user: string | null;
    project_id: string | null;
    run_name: string | null;
    /** Live-chart payload streamed during the MC run. Populated only while
     *  the engine is actually generating samples; absent before / after. */
    live_chart?: {
      avg_by_hour: number[];                    // 24 values (avg kW per hour-of-day)
      /**
       * Per-profile breakdown of avg_by_hour. Keyed by 8760 engine
       * profile (HH / HH1 / HH2 / HH3 / SME / SCH / SCP / CHU / CLI /
       * Other). Each value is a 24-element array. Empty profiles
       * (no customers of that type in the project) are omitted.
       *
       * NB: ``HH`` is the SUM of household tiers (HH+HH1+HH2+HH3),
       * so for a stacked-area chart use either ``HH`` OR the
       * tier breakdown (HH1+HH2+HH3), not both.
       *
       * Optional: only populated for runs from engines built v1.9.7.18+.
       * Older runs in flight return ``avg_by_hour`` only and the
       * frontend should fall back to a single-line chart in that case.
       */
      avg_by_hour_by_type?: Record<string, number[]>;
      year_curve: number[];                     // partial hourly profile downsampled
      year_curve_total_bins: number;            // ceiling for X-axis (200 by default)
      year_curve_bin_size_steps: number;
      year_curve_bin_size_hours: number;
      running_peak_kw: number;
      running_peak_at_hour: number;
      cumulative_kwh: number;
      cumulative_mwh: number;
      current_hour: number;
      granularity_minutes: number;
    };
  }> {
    const { data } = await this.withFallback((client) =>
      client.get(`/8760/jobs/${encodeURIComponent(jobId)}`, { timeout: 15000 })
    );
    return data;
  }

  async cancel8760Job(jobId: string): Promise<{ status: string; job?: unknown }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/8760/jobs/${encodeURIComponent(jobId)}/cancel`, {}, { timeout: 15000 })
    );
    return data;
  }

  async list8760Jobs(): Promise<{
    jobs: Array<{
      job_id: string;
      run_id: string;
      status: string;
      progress: number;
      message: string;
      month?: string | null;
      project_id?: string | null;
      run_name?: string | null;
    }>;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get('/8760/jobs', { timeout: 15000 })
    );
    return data;
  }

  /** Poll a job until it reaches a terminal status. Calls ``onProgress`` on each tick.
   *  Returns the final snapshot. Throws on network errors; caller can retry.
   *  The returned function from ``opts.signal?.addEventListener('abort')`` will
   *  NOT cancel the run server-side — use ``cancel8760Job`` for that.
   */
  async poll8760JobUntilDone(
    jobId: string,
    onProgress?: (snapshot: Awaited<ReturnType<ApiService['get8760Job']>>) => void,
    opts: { intervalMs?: number; maxWaitMs?: number; signal?: AbortSignal } = {}
  ): Promise<Awaited<ReturnType<ApiService['get8760Job']>>> {
    const interval = Math.max(500, opts.intervalMs ?? 2000);
    // Default 60 min: 15-min granularity samples 4× hourly timesteps and
    // large sites that finish in ~8 min at 60 min can need 30+ min at 15 min.
    const maxWait = Math.max(interval, opts.maxWaitMs ?? 60 * 60_000);
    const start = Date.now();
    const terminal = new Set(['completed', 'cancelled', 'error', 'interrupted']);
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (opts.signal?.aborted) {
        throw new DOMException('Polling aborted by caller', 'AbortError');
      }
      const snap = await this.get8760Job(jobId);
      try {
        onProgress?.(snap);
      } catch {
        /* caller's progress handler should not break the loop */
      }
      if (terminal.has(snap.status)) {
        return snap;
      }
      if (Date.now() - start > maxWait) {
        throw new Error(
          `Timed out after ${Math.round(maxWait / 1000)}s waiting for 8760 job ${jobId}`
        );
      }
      await new Promise<void>((resolve, reject) => {
        const t = setTimeout(resolve, interval);
        opts.signal?.addEventListener(
          'abort',
          () => {
            clearTimeout(t);
            reject(new DOMException('Polling aborted by caller', 'AbortError'));
          },
          { once: true }
        );
      });
    }
  }

  async run8760Simulation(
    projectId: string,
    connectionIds: string[],
    params: Run8760Params,
    runName?: string,
    adjustments?: Record<string, number>
  ): Promise<Run8760Result> {
    // 8760 MC runs the inverse-CDF sampler across many timesteps in Python;
    // large sites (1000s of HH) and 15/30-min granularity routinely exceed
    // the default 150s axios timeout. Prefer the async job API; this sync
    // path keeps a 60-minute ceiling. Matching nginx is tuned via the
    // /api/8760 location in deploy/nginx-{,staging-}ugridplan.conf
    // (proxy_pass must keep /8760/).
    const { data } = await this.withFallback((client) =>
      client.post(
        `/8760/run/${projectId}`,
        {
          connection_ids: connectionIds,
          params,
          run_name: runName,
          adjustments,
        },
        { timeout: 3600000 }
      )
    );
    return data;
  }

  /** 8760 run without a project session (stores per authenticated user on server). */
  async run8760Standalone(body: {
    connection_counts: Record<string, number>;
    params: Run8760Params;
    run_name?: string;
    adjustments?: Record<string, number>;
  }): Promise<Run8760Result & { scope?: string }> {
    const { data } = await this.withFallback((client) =>
      client.post(
        `/8760/standalone/run`,
        {
          connection_counts: body.connection_counts,
          params: body.params,
          run_name: body.run_name,
          adjustments: body.adjustments,
        },
        { timeout: 3600000 }
      )
    );
    return data;
  }

  async list8760StandaloneRuns(): Promise<{ runs: Run8760Summary[]; scope?: string }> {
    const { data } = await this.withFallback((client) => client.get(`/8760/standalone/runs`));
    return data;
  }

  async get8760StandaloneRun(runId: string): Promise<Run8760Details> {
    const { data } = await this.withFallback((client) =>
      client.get(`/8760/standalone/run/${encodeURIComponent(runId)}`)
    );
    return data;
  }

  async delete8760StandaloneRun(runId: string): Promise<void> {
    await this.withFallback((client) =>
      client.delete(`/8760/standalone/run/${encodeURIComponent(runId)}`)
    );
  }

  async get8760StandaloneYearLoad(runId: string): Promise<YearLoadData> {
    const { data } = await this.withFallback((client) =>
      client.get(`/8760/standalone/run/${encodeURIComponent(runId)}/year-load`)
    );
    return data;
  }

  get8760StandaloneExcelUrl(runId: string): string {
    return `${API_BASE_URL}/8760/standalone/run/${encodeURIComponent(runId)}/excel`;
  }

  /**
   * Download URL for the FULL 8760 run as a zip bundle. Includes
   * mcsimload.npy, per_type_loads.npz, metadata.json, output.xlsx,
   * and every PNG (daily_avg / 8760_profile / weekly_sample /
   * projection_20yr / monthly/* / box_charts/*). For standalone
   * runs only -- the per-project twin is get8760RunZipUrl.
   */
  get8760StandaloneZipUrl(runId: string): string {
    return `${API_BASE_URL}/8760/standalone/run/${encodeURIComponent(runId)}/zip`;
  }

  get8760StandaloneChartUrl(
    runId: string,
    chartType: string,
    sampleWeek?: number,
    year?: number
  ): string {
    const params = new URLSearchParams();
    params.set('chart_type', chartType);
    if (sampleWeek !== undefined) params.set('sample_week', sampleWeek.toString());
    if (year !== undefined) params.set('year', year.toString());
    return `${API_BASE_URL}/8760/standalone/run/${encodeURIComponent(runId)}/chart?${params.toString()}`;
  }

  async attach8760StandaloneToProject(runId: string, projectId: string): Promise<{
    success: boolean;
    project_id: string;
    run_id: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/8760/standalone/attach`, { run_id: runId, project_id: projectId })
    );
    return data;
  }

  async list8760Runs(projectId: string): Promise<{ runs: Run8760Summary[] }> {
    const { data } = await this.withFallback((client) =>
      client.get(`/8760/runs/${projectId}`)
    );
    return data;
  }

  async get8760Run(projectId: string, runId: string): Promise<Run8760Details> {
    const { data } = await this.withFallback((client) =>
      client.get(`/8760/run/${projectId}/${runId}`)
    );
    return data;
  }

  async delete8760Run(projectId: string, runId: string): Promise<void> {
    await this.withFallback((client) =>
      client.delete(`/8760/run/${projectId}/${runId}`)
    );
  }

  async deleteAll8760Runs(projectId: string): Promise<void> {
    await this.withFallback((client) =>
      client.delete(`/8760/runs/${projectId}`)
    );
  }

  async update8760Run(projectId: string, runId: string, newName: string): Promise<void> {
    await this.withFallback((client) =>
      client.patch(`/8760/run/${projectId}/${runId}`, { run_name: newName })
    );
  }

  async get8760YearLoad(projectId: string, runId: string): Promise<YearLoadData> {
    const { data } = await this.withFallback((client) =>
      client.get(`/8760/run/${projectId}/${runId}/year-load`)
    );
    return data;
  }

  get8760ExcelUrl(projectId: string, runId: string): string {
    return `${API_BASE_URL}/8760/run/${projectId}/${runId}/excel`;
  }

  /** Per-project twin of get8760StandaloneZipUrl -- full run bundle. */
  get8760RunZipUrl(projectId: string, runId: string): string {
    return `${API_BASE_URL}/8760/run/${projectId}/${runId}/zip`;
  }

  get8760ChartUrl(projectId: string, runId: string, chartType: string, sampleWeek?: number, year?: number): string {
    const params = new URLSearchParams();
    params.set('chart_type', chartType);
    if (sampleWeek !== undefined) params.set('sample_week', sampleWeek.toString());
    if (year !== undefined) params.set('year', year.toString());
    return `${API_BASE_URL}/8760/run/${projectId}/${runId}/chart?${params.toString()}`;
  }

  async getCdfSourcePreview(sourceId: string, n: number = 1): Promise<any> {
    const { data } = await this.withFallback((client) =>
      client.get(`/8760/cdf-source/${sourceId}/preview`, { params: { n } })
    );
    return data;
  }

  async getCdfRawScatter(sourceId: string, limit: number = 30000): Promise<any> {
    const { data } = await this.withFallback((client) =>
      client.get(`/8760/cdf-source/${sourceId}/scatter`, { params: { limit } })
    );
    return data;
  }

  async getCdfAnnualProfile(sourceId: string): Promise<any> {
    const { data } = await this.withFallback((client) =>
      client.get(`/8760/cdf-source/${sourceId}/annual-profile`)
    );
    return data;
  }

  async getCdfRawDailyAvg(sourceId: string): Promise<any> {
    const { data } = await this.withFallback((client) =>
      client.get(`/8760/cdf-source/${sourceId}/raw-daily-avg`)
    );
    return data;
  }

  async getCdfDailyAverageDistribution(sourceId: string): Promise<any> {
    const { data } = await this.withFallback((client) =>
      client.get(`/8760/cdf-source/${sourceId}/daily-avg-distribution`)
    );
    return data;
  }

  async getCdfRaw8760(sourceId: string): Promise<any> {
    const { data } = await this.withFallback((client) =>
      client.get(`/8760/cdf-source/${sourceId}/raw-8760`)
    );
    return data;
  }

  async getCdfTenureTrend(sourceId: string): Promise<any> {
    const { data } = await this.withFallback((client) =>
      client.get(`/8760/cdf-source/${sourceId}/tenure-trend`)
    );
    return data;
  }

  /**
   * Refit the tenure trendline over a user-specified bucket-month range.
   *
   * Backed by ``POST /8760/cdf-source/{sourceId}/fit-trend``. Returns the
   * same fields ``derive_tenure_trend_metadata.py`` writes into the
   * library plus a ``predictions`` array (per-half-month kWh/day +
   * kWh/month) so the bubble chart can overlay the fitted curve.
   *
   * Lets the planner explore "what does the trend look like over months
   * 0..48?" without committing -- the response also says whether the
   * resulting fit would be ``has_trend=true`` (medium+ confidence) and
   * what its R^2 / equation are. Only when the planner clicks Save do
   * we persist via saveProjectDataset('tenure_trend_overrides', ...).
   */
  async fitCdfTenureTrend(
    sourceId: string,
    startMonth?: number,
    endMonth?: number,
  ): Promise<any> {
    const body: Record<string, number> = {};
    if (typeof startMonth === 'number') body.start_month = startMonth;
    if (typeof endMonth === 'number') body.end_month = endMonth;
    const { data } = await this.withFallback((client) =>
      client.post(`/8760/cdf-source/${sourceId}/fit-trend`, body)
    );
    return data;
  }

  async uploadCdfData(file: File, metadata: {
    source_name: string;
    source_description: string;
    building_type: string;
    region?: string;
    collection_period?: string;
  }): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    // Backend expects a JSON string 'config' field
    const config = {
      name: metadata.source_name,
      description: metadata.source_description,
      building_types: [metadata.building_type],
      region: metadata.region,
      collection_period: metadata.collection_period,
    };
    formData.append('config', JSON.stringify(config));
    const { data } = await this.withFallback((client) =>
      client.post('/8760/cdf-source/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    );
    return data;
  }

  async deleteCdfSource(sourceId: string): Promise<any> {
    const { data } = await this.withFallback((client) =>
      client.delete(`/8760/cdf-source/${sourceId}`)
    );
    return data;
  }

  // =============================================================================
  // NOTES LEDGER METHODS
  // =============================================================================

  /**
   * Get all notes for an element.
   */
  async getNotes(
    projectId: string,
    elementType: string,
    elementId: string
  ): Promise<{ notes_ledger: NoteEntry[]; legacy_notes: string; total_count: number }> {
    const { data } = await this.withFallback((client) =>
      client.get('/project/note/get', {
        params: { projectId, elementType, elementId }
      })
    );
    return data;
  }

  /**
   * Add a new note to an element.
   */
  async addNote(
    projectId: string,
    elementType: string,
    elementId: string,
    content: string,
    author?: string
  ): Promise<{ status: string; note: NoteEntry; notes_count: number }> {
    const { data } = await this.withFallback((client) =>
      client.post('/project/note/add', {
        projectId,
        elementType,
        elementId,
        content,
        author
      })
    );
    return data;
  }

  /**
   * Update an existing note.
   */
  async updateNote(
    projectId: string,
    elementType: string,
    elementId: string,
    noteId: string,
    content: string
  ): Promise<{ status: string; notes_count: number }> {
    const { data } = await this.withFallback((client) =>
      client.put('/project/note/update', {
        projectId,
        elementType,
        elementId,
        noteId,
        content
      })
    );
    return data;
  }

  /**
   * Delete a note.
   */
  async deleteNote(
    projectId: string,
    elementType: string,
    elementId: string,
    noteId: string
  ): Promise<{ status: string; notes_count: number }> {
    const { data } = await this.withFallback((client) =>
      client.delete('/project/note/delete', {
        data: { projectId, elementType, elementId, noteId }
      })
    );
    return data;
  }

  /**
   * Add a note to multiple elements at once (bulk operation).
   */
  async bulkAddNote(
    projectId: string,
    elementType: string,
    elementIds: string[],
    content: string,
    author?: string
  ): Promise<{ status: string; updated_count: number; not_found: string[] }> {
    const { data } = await this.withFallback((client) =>
      client.post('/project/note/bulk-add', {
        projectId,
        elementType,
        elementIds,
        content,
        author
      })
    );
    return data;
  }

  // ==========================================================================
  // EQUIPMENT MODELS API
  // ==========================================================================

  /**
   * Get all equipment models (optionally filtered by category).
   */
  async getEquipmentModels(category?: string): Promise<{
    categories: Array<{ id: string; name: string; description: string }>;
    models: Array<{
      id: string;
      category: string;
      manufacturer: string;
      model_name: string;
      specs: Record<string, any>;
      is_active: boolean;
    }>;
  }> {
    const url = category 
      ? `/admin/equipment-models?category=${encodeURIComponent(category)}`
      : '/admin/equipment-models';
    const { data } = await this.withFallback((client) => client.get(url));
    return data;
  }

  /**
   * Get equipment models by category.
   */
  async getEquipmentModelsByCategory(category: string): Promise<{
    category: string;
    models: Array<{
      id: string;
      manufacturer: string;
      model_name: string;
      specs: Record<string, any>;
      is_active: boolean;
    }>;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get(`/admin/equipment-models/by-category/${encodeURIComponent(category)}`)
    );
    return data;
  }

  /**
   * Read-only inverter model list for generation property editing.
   * Data is managed via admin equipment models backend.
   */
  async getInverterModels(): Promise<{
    models: Array<{
      id: string;
      manufacturer: string;
      model_name: string;
      category: string;
      specs: Record<string, any>;
    }>;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get('/reference/inverter-models')
    );
    return data;
  }

  // ==========================================================================
  // PTB (POLE TOP BOX) API
  // ==========================================================================

  /**
   * List all PTBs for a project.
   */
  async getPTBs(projectId: string): Promise<{
    ptbs: Array<{
      ptbId: string;
      poleId: string;
      boxSerial: string;
      boxModel: string;
      channelCount: number;
      channelSerials: string;
      protectionSpecs: string;
      installDate: string;
      stCode6: number;
      notes: string;
      gpsX: number;
      gpsY: number;
    }>;
    count: number;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get(`/v5/ptbs?projectId=${encodeURIComponent(projectId)}`)
    );
    return data;
  }

  /**
   * Validate a PTB (check channel count vs connected drops).
   */
  async validatePTB(projectId: string, ptbId: string): Promise<{
    ptb_id: string;
    pole_id: string;
    channel_count: number;
    connected_drops: number;
    energized_drops: number;
    is_valid: boolean;
    validation_message: string;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get(`/v5/ptbs/${encodeURIComponent(ptbId)}/validation?projectId=${encodeURIComponent(projectId)}`)
    );
    return data;
  }

  // ==========================================================================
  // O&M TICKETING API
  // ==========================================================================

  /**
   * Get O&M system metadata (statuses, priorities, etc.).
   */
  async getOMMetadata(): Promise<{
    statuses: string[];
    priorities: Array<{ value: string; label: string; response_hours: number }>;
    equipment_categories: string[];
    ticket_types: string[];
    root_cause_categories: string[];
    sla_targets: Record<string, { response_hours: number; resolution_hours: number }>;
    categories: OMTicketCategory[];
  }> {
    const { data } = await this.withFallback((client) => client.get('/om/metadata'));
    return data;
  }

  /**
   * List O&M tickets with optional filters.
   */
  async getOMTickets(filters?: {
    site_id?: string;
    status?: string;
    priority?: string;
    element_type?: string;
    element_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    tickets: OMTicket[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const params = new URLSearchParams();
    if (filters?.site_id) params.append('site_id', filters.site_id);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.element_type) params.append('element_type', filters.element_type);
    if (filters?.element_id) params.append('element_id', filters.element_id);
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.offset) params.append('offset', String(filters.offset));
    
    const url = `/om/tickets${params.toString() ? '?' + params.toString() : ''}`;
    const { data } = await this.withFallback((client) => client.get(url));
    return data;
  }

  /**
   * Get a specific O&M ticket.
   */
  async getOMTicket(ticketId: string): Promise<OMTicket> {
    const { data } = await this.withFallback((client) =>
      client.get(`/om/tickets/${encodeURIComponent(ticketId)}`)
    );
    return data;
  }

  /**
   * Create a new O&M ticket.
   */
  async createOMTicket(ticket: {
    site_id: string;
    element_type?: string;
    element_id?: string;
    customer_id?: string;
    equipment_category?: string;
    equipment_model?: string;
    fault_description: string;
    services_affected?: string;
    reported_by?: string;
    ticket_type?: string;
    category_id?: string;
    priority?: string;
    root_cause_category?: string;
    downtime_hours?: number;
    customers_affected?: number;
    outsourced?: boolean;
    outsource_cost?: number;
    due_date?: string;
    parts_used?: Array<{ part_name: string; quantity: number; unit_cost: number }>;
    labor_hours?: number;
    warranty_status?: string;
  }): Promise<{ success: boolean; ticket: OMTicket }> {
    const { data } = await this.withFallback((client) =>
      client.post('/om/tickets', ticket)
    );
    return data;
  }

  /**
   * Update an O&M ticket.
   */
  async updateOMTicket(ticketId: string, updates: {
    site_id?: string;
    element_type?: string;
    element_id?: string;
    customer_id?: string;
    equipment_category?: string;
    equipment_model?: string;
    status?: string;
    priority?: string;
    escalated?: boolean;
    escalation_reason?: string;
    reported_at?: string;
    resolved_at?: string;
    closed_at?: string;
    fault_description?: string;
    services_affected?: string;
    troubleshooting_steps?: string;
    cause_of_fault?: string;
    preventive_action?: string;
    assigned_to?: string;
    reported_by?: string;
    updated_by?: string;
    ticket_type?: string;
    category_id?: string;
    root_cause_category?: string;
    downtime_hours?: number;
    customers_affected?: number;
    outsourced?: boolean;
    outsource_cost?: number;
    due_date?: string;
    parts_used?: Array<{ part_name: string; quantity: number; unit_cost: number }>;
    labor_hours?: number;
    warranty_status?: string;
    related_tickets?: string[];
  }): Promise<{ success: boolean; ticket: OMTicket }> {
    const { data } = await this.withFallback((client) =>
      client.put(`/om/tickets/${encodeURIComponent(ticketId)}`, updates)
    );
    return data;
  }

  /**
   * Delete an O&M ticket.
   */
  async deleteOMTicket(ticketId: string): Promise<{ success: boolean; message: string }> {
    const { data } = await this.withFallback((client) =>
      client.delete(`/om/tickets/${encodeURIComponent(ticketId)}`)
    );
    return data;
  }

  /**
   * Perform a workflow action on a ticket.
   */
  async omTicketWorkflow(ticketId: string, action: {
    action: string;
    user?: string;
    notes?: string;
    priority?: string;
    assigned_to?: string;
    cause_of_fault?: string;
    escalation_reason?: string;
    resolution_notes?: string;
  }): Promise<{ success: boolean; ticket: OMTicket }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/om/tickets/${encodeURIComponent(ticketId)}/workflow`, action)
    );
    return data;
  }

  /**
   * Get tickets for a specific element.
   */
  async getOMTicketsByElement(elementType: string, elementId: string): Promise<{
    tickets: OMTicket[];
    count: number;
  }> {
    const { data } = await this.withFallback((client) =>
      client.get(`/om/tickets/by-element/${encodeURIComponent(elementType)}/${encodeURIComponent(elementId)}`)
    );
    return data;
  }

  /**
   * Add a comment to a ticket.
   */
  async addOMComment(ticketId: string, user: string, text: string): Promise<{
    success: boolean;
    comment: OMTicketComment;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(`/om/tickets/${encodeURIComponent(ticketId)}/comments`, { user, text })
    );
    return data;
  }

  /**
   * Get O&M statistics.
   */
  async getOMStatistics(siteId?: string): Promise<{
    total: number;
    open: number;
    closed: number;
    escalated: number;
    by_status: Record<string, number>;
    by_priority: Record<string, number>;
  }> {
    const url = siteId ? `/om/statistics?site_id=${encodeURIComponent(siteId)}` : '/om/statistics';
    const { data } = await this.withFallback((client) => client.get(url));
    return data;
  }

  /**
   * Get comprehensive O&M report (SLA compliance, MTTR, cost summary, trends).
   */
  async getOMReport(siteId?: string): Promise<OMReport> {
    const url = siteId ? `/om/report?site_id=${encodeURIComponent(siteId)}` : '/om/report';
    const { data } = await this.withFallback((client) => client.get(url));
    return data;
  }

  /**
   * Get O&M financial metrics (ARPU, revenue, consumption, customer growth).
   */
  async getOMFinancialMetrics(forceRefresh: boolean = false): Promise<OMFinancialMetrics> {
    const url = forceRefresh ? '/om/financial-metrics?force_refresh=true' : '/om/financial-metrics';
    const { data } = await this.withFallback((client) => client.get(url));
    return data;
  }

  /**
   * Get outsource payout report.
   */
  async getOMPayoutReport(siteId?: string, periodStart?: string, periodEnd?: string): Promise<OMPayoutReport> {
    const params = new URLSearchParams();
    if (siteId) params.set('site_id', siteId);
    if (periodStart) params.set('period_start', periodStart);
    if (periodEnd) params.set('period_end', periodEnd);
    const qs = params.toString();
    const url = qs ? `/om/payout-report?${qs}` : '/om/payout-report';
    const { data } = await this.withFallback((client) => client.get(url));
    return data;
  }

  async getRecurrenceAlerts(siteId?: string, threshold?: number, months?: number): Promise<OMRecurrenceAlertResponse> {
    const params = new URLSearchParams();
    if (siteId) params.set('site_id', siteId);
    if (threshold !== undefined) params.set('threshold', String(threshold));
    if (months !== undefined) params.set('months', String(months));
    const qs = params.toString();
    const url = qs ? `/om/recurrence-alerts?${qs}` : '/om/recurrence-alerts';
    const { data } = await this.withFallback((client) => client.get(url));
    return data;
  }

  /**
   * Bulk-import historical O&M tickets from an Excel directory.
   */
  async importHistoricalOM(directory: string, dryRun: boolean = false): Promise<{
    success: boolean;
    summary: {
      total_files: number;
      total_parsed: number;
      duplicates_skipped: number;
      unique_new: number;
      imported: number;
      import_skipped: number;
      dry_run: boolean;
      by_site: Record<string, number>;
      by_year: Record<string, number>;
      file_details: Array<{ file: string; type: string; tickets_parsed: number }>;
      errors: string[];
      sample_tickets?: Array<Record<string, unknown>>;
    };
  }> {
    const { data } = await this.withFallback((client) =>
      client.post('/om/import-historical', { directory, dry_run: dryRun })
    );
    return data;
  }

  // ===========================================================================
  // O&M NOTIFICATION CONTACTS & ROUTING RULES
  // ===========================================================================

  async getNotificationMetadata(): Promise<{
    roles: string[];
    channels: string[];
    events: string[];
  }> {
    const { data } = await this.withFallback((client) => client.get('/om/notifications/metadata'));
    return data;
  }

  async getNotificationContacts(): Promise<{ contacts: OMNotificationContact[]; count: number }> {
    const { data } = await this.withFallback((client) => client.get('/om/notifications/contacts'));
    return data;
  }

  async createNotificationContact(contact: Omit<OMNotificationContact, 'contact_id' | 'created_at' | 'updated_at'>): Promise<{ success: boolean; contact: OMNotificationContact }> {
    const { data } = await this.withFallback((client) => client.post('/om/notifications/contacts', contact));
    return data;
  }

  async updateNotificationContact(contactId: string, updates: Partial<OMNotificationContact>): Promise<{ success: boolean; contact: OMNotificationContact }> {
    const { data } = await this.withFallback((client) => client.put(`/om/notifications/contacts/${contactId}`, updates));
    return data;
  }

  async deleteNotificationContact(contactId: string): Promise<{ success: boolean }> {
    const { data } = await this.withFallback((client) => client.delete(`/om/notifications/contacts/${contactId}`));
    return data;
  }

  async getNotificationRules(): Promise<{ rules: OMNotificationRule[]; count: number }> {
    const { data } = await this.withFallback((client) => client.get('/om/notifications/rules'));
    return data;
  }

  async createNotificationRule(rule: Omit<OMNotificationRule, 'rule_id' | 'created_at' | 'updated_at'>): Promise<{ success: boolean; rule: OMNotificationRule }> {
    const { data } = await this.withFallback((client) => client.post('/om/notifications/rules', rule));
    return data;
  }

  async updateNotificationRule(ruleId: string, updates: Partial<OMNotificationRule>): Promise<{ success: boolean; rule: OMNotificationRule }> {
    const { data } = await this.withFallback((client) => client.put(`/om/notifications/rules/${ruleId}`, updates));
    return data;
  }

  async deleteNotificationRule(ruleId: string): Promise<{ success: boolean }> {
    const { data } = await this.withFallback((client) => client.delete(`/om/notifications/rules/${ruleId}`));
    return data;
  }

  async getNotificationConfig(): Promise<{
    email_configured: boolean;
    email_host: string;
    email_user: string;
    sms_configured: boolean;
    sms_provider: string;
  }> {
    const { data } = await this.withFallback((client) => client.get('/om/notifications/config'));
    return data;
  }

  async dispatchNotification(event: string, ticketId: string, dryRun: boolean = false): Promise<{
    event: string;
    ticket_id: string;
    rules_matched: number;
    notifications_sent: number;
    notifications_failed: number;
    dry_run: boolean;
    details: Array<{ contact_name: string; channel: string; address: string; status: string; error?: string }>;
  }> {
    const { data } = await this.withFallback((client) => client.post('/om/notifications/dispatch', { event, ticket_id: ticketId, dry_run: dryRun }));
    return data;
  }

  async testNotification(channel: string, address: string): Promise<{ success: boolean; channel: string; address: string }> {
    const { data } = await this.withFallback((client) => client.post('/om/notifications/test', { channel, address }));
    return data;
  }

  async getNotificationLog(limit: number = 50): Promise<{ entries: OMNotificationLogEntry[]; count: number }> {
    const { data } = await this.withFallback((client) => client.get(`/om/notifications/log?limit=${limit}`));
    return data;
  }

  // ===========================================================================
  // O&M SCHEDULED TASKS / DIGESTS
  // ===========================================================================

  async getSLAWarnings(): Promise<{
    warnings: Array<{ ticket_id: string; site_id: string; priority: string; status: string; reported_at: string; deadline: string; elapsed_hours: number; remaining_hours: number; pct_elapsed: number; fault_description: string; alert_type: string }>;
    breaches: Array<{ ticket_id: string; site_id: string; priority: string; status: string; reported_at: string; deadline: string; elapsed_hours: number; remaining_hours: number; pct_elapsed: number; fault_description: string; alert_type: string }>;
    total_warnings: number;
    total_breaches: number;
    checked_at: string;
    warning_threshold_pct: number;
  }> {
    const { data } = await this.withFallback((client) => client.get('/om/sla-warnings'));
    return data;
  }

  async dispatchSLAWarnings(): Promise<any> {
    const { data } = await this.withFallback((client) => client.post('/om/sla-warnings/dispatch'));
    return data;
  }

  async getDailyDigest(siteId?: string): Promise<{
    digest_type: string;
    generated_at: string;
    site_id: string | null;
    total_open: number;
    total_overdue: number;
    total_escalated: number;
    by_priority: Record<string, number>;
    overdue_tickets: Array<{ ticket_id: string; site_id: string; priority: string; due_date: string; fault_description: string }>;
    todays_activity: Array<{ ticket_id: string; action: string; user: string; timestamp: string }>;
    escalated_tickets: Array<{ ticket_id: string; site_id: string; priority: string; escalation_reason: string }>;
  }> {
    const params = siteId ? `?site_id=${siteId}` : '';
    const { data } = await this.withFallback((client) => client.get(`/om/digest/daily${params}`));
    return data;
  }

  async getWeeklySummary(siteId?: string): Promise<{
    digest_type: string;
    generated_at: string;
    site_id: string | null;
    period_start: string;
    period_end: string;
    tickets_created: number;
    tickets_resolved: number;
    current_open: number;
    avg_mttr_hours: number | null;
    sla_met: number;
    sla_breached: number;
    sla_compliance_pct: number | null;
    created_by_category: Record<string, number>;
    created_by_site: Record<string, number>;
  }> {
    const params = siteId ? `?site_id=${siteId}` : '';
    const { data } = await this.withFallback((client) => client.get(`/om/digest/weekly${params}`));
    return data;
  }

  async dispatchWeeklyDigest(): Promise<any> {
    const { data } = await this.withFallback((client) => client.post('/om/digest/weekly/dispatch'));
    return data;
  }

  // ===========================================================================
  // O&M QUALITY METRICS
  // ===========================================================================

  async getSiteQualityMetrics(siteId?: string): Promise<{
    sites: Array<{
      site_id: string; total_tickets: number; open_tickets: number; resolved_tickets: number;
      avg_mttr_hours: number | null; sla_met: number; sla_breached: number; sla_compliance_pct: number | null;
      total_cost: number; total_downtime_hours: number; customers_affected: number; escalated_count: number;
      by_priority: Record<string, number>; by_category: Record<string, number>;
    }>;
    count: number;
  }> {
    const params = siteId ? `?site_id=${siteId}` : '';
    const { data } = await this.withFallback((client) => client.get(`/om/quality/sites${params}`));
    return data;
  }

  async getCustomerMetrics(siteId?: string): Promise<{
    customers: Array<{
      customer_id: string; total_tickets: number; open_tickets: number; resolved_tickets: number;
      avg_mttr_hours: number | null; total_cost: number; total_downtime_hours: number;
      sites: string[]; last_ticket_at: string;
    }>;
    count: number;
  }> {
    const params = siteId ? `?site_id=${siteId}` : '';
    const { data } = await this.withFallback((client) => client.get(`/om/quality/customers${params}`));
    return data;
  }

  async getTechnicianPerformance(siteId?: string): Promise<{
    technicians: Array<{
      technician: string; assigned_tickets: number; resolved_tickets: number; open_tickets: number;
      avg_mttr_hours: number | null; escalated_count: number; by_priority: Record<string, number>;
    }>;
    count: number;
  }> {
    const params = siteId ? `?site_id=${siteId}` : '';
    const { data } = await this.withFallback((client) => client.get(`/om/quality/technicians${params}`));
    return data;
  }

  async getQualityScore(siteId?: string): Promise<{
    score: number | null;
    grade: string;
    components: Record<string, { score: number; weight: number; detail: string }>;
    total_tickets: number;
    site_id: string | null;
  }> {
    const params = siteId ? `?site_id=${siteId}` : '';
    const { data } = await this.withFallback((client) => client.get(`/om/quality/score${params}`));
    return data;
  }

  // ===========================================================================
  // O&M MAINTENANCE TEMPLATES
  // ===========================================================================

  async getMaintenanceTemplateMetadata(): Promise<{ frequencies: string[] }> {
    const { data } = await this.withFallback((client) => client.get('/om/maintenance-templates/metadata'));
    return data;
  }

  async getMaintenanceTemplates(siteId?: string): Promise<{ templates: OMMaintenanceTemplate[]; count: number }> {
    const url = siteId ? `/om/maintenance-templates?site_id=${encodeURIComponent(siteId)}` : '/om/maintenance-templates';
    const { data } = await this.withFallback((client) => client.get(url));
    return data;
  }

  async createMaintenanceTemplate(template: Omit<OMMaintenanceTemplate, 'template_id' | 'created_at' | 'updated_at' | 'last_generated_at' | 'tickets_generated'>): Promise<{ success: boolean; template: OMMaintenanceTemplate }> {
    const { data } = await this.withFallback((client) => client.post('/om/maintenance-templates', template));
    return data;
  }

  async updateMaintenanceTemplate(templateId: string, updates: Partial<OMMaintenanceTemplate>): Promise<{ success: boolean; template: OMMaintenanceTemplate }> {
    const { data } = await this.withFallback((client) => client.put(`/om/maintenance-templates/${templateId}`, updates));
    return data;
  }

  async deleteMaintenanceTemplate(templateId: string): Promise<{ success: boolean }> {
    const { data } = await this.withFallback((client) => client.delete(`/om/maintenance-templates/${templateId}`));
    return data;
  }

  async generateTicketsFromTemplates(dryRun: boolean = true): Promise<{
    templates_checked: number;
    templates_due: number;
    tickets_created: number;
    dry_run: boolean;
    details: Array<{ template_id: string; template_name: string; site_id: string; status: string; ticket_id?: string; error?: string }>;
  }> {
    const { data } = await this.withFallback((client) => client.post(`/om/maintenance-templates/generate?dry_run=${dryRun}`));
    return data;
  }

  // ===========================================================================
  // O&M TECHNICIAN REGISTRY
  // ===========================================================================

  async getTechnicianMetadata(): Promise<{ roles: string[]; specializations: string[] }> {
    const { data } = await this.withFallback((client) => client.get('/om/technicians/metadata'));
    return data;
  }

  async getTechnicians(siteId?: string, role?: string, availableOnly?: boolean): Promise<{ technicians: OMTechnician[]; count: number }> {
    const params = new URLSearchParams();
    if (siteId) params.set('site_id', siteId);
    if (role) params.set('role', role);
    if (availableOnly) params.set('available_only', 'true');
    const qs = params.toString();
    const url = qs ? `/om/technicians?${qs}` : '/om/technicians';
    const { data } = await this.withFallback((client) => client.get(url));
    return data;
  }

  async createTechnician(technician: Omit<OMTechnician, 'technician_id' | 'created_at' | 'updated_at' | 'tickets_assigned' | 'tickets_resolved'>): Promise<{ success: boolean; technician: OMTechnician }> {
    const { data } = await this.withFallback((client) => client.post('/om/technicians', technician));
    return data;
  }

  async updateTechnician(technicianId: string, updates: Partial<OMTechnician>): Promise<{ success: boolean; technician: OMTechnician }> {
    const { data } = await this.withFallback((client) => client.put(`/om/technicians/${technicianId}`, updates));
    return data;
  }

  async deleteTechnician(technicianId: string): Promise<{ success: boolean }> {
    const { data } = await this.withFallback((client) => client.delete(`/om/technicians/${technicianId}`));
    return data;
  }

  // ===========================================================================
  // COST TRACEABILITY API
  // ===========================================================================

  /** List all parts with cost coverage status. */
  async getCostParts(): Promise<CostPartsResponse> {
    const { data } = await this.withFallback((client) => client.get('/cost/parts'));
    return data;
  }

  /** Get computed price for a part with full source traceability. */
  async getPartPrice(
    partId: string,
    strategy: string = 'average',
    country?: string,
    vendorOrigin?: string,
  ): Promise<PartPriceResponse> {
    const params: Record<string, string> = { strategy };
    if (country) params.country = country;
    if (vendorOrigin) params.vendor_origin = vendorOrigin;
    const { data } = await this.withFallback((client) =>
      client.get(`/cost/part/${encodeURIComponent(partId)}/price`, { params })
    );
    return data;
  }

  /** Get full invoice/quote source traceability for a part. */
  async getPartCostSources(partId: string): Promise<PartSourcesResponse> {
    const { data } = await this.withFallback((client) =>
      client.get(`/cost/part/${encodeURIComponent(partId)}/sources`)
    );
    return data;
  }

  /** Get supplier transaction index — all vendors with history. */
  async getCostSuppliers(): Promise<CostSuppliersResponse> {
    const { data } = await this.withFallback((client) => client.get('/cost/suppliers'));
    return data;
  }

  /** Get detailed transaction history for a specific vendor. */
  async getCostSupplierDetail(vendorName: string): Promise<CostSupplierDetail> {
    const { data } = await this.withFallback((client) =>
      client.get(`/cost/suppliers/${encodeURIComponent(vendorName)}`)
    );
    return data;
  }

  /** Get cost data quality summary and coverage statistics. */
  async getCostSummary(): Promise<CostSummaryResponse> {
    const { data } = await this.withFallback((client) => client.get('/cost/summary'));
    return data;
  }

  /** Cost an entire BOM with source traceability. */
  async costBOM(
    items: Array<{ part_id: string; qty: number }>,
    strategy: string = 'average',
    country?: string,
    vendorOrigin?: string,
  ): Promise<BOMCostResponse> {
    const { data } = await this.withFallback((client) =>
      client.post('/cost/bom', {
        items,
        strategy,
        country: country || null,
        vendor_origin: vendorOrigin || null,
      })
    );
    return data;
  }

  /** Get computed cost for a single assembly from its component parts. */
  async getAssemblyCost(assemblyId: string, strategy: string = 'average'): Promise<AssemblyCostResponse> {
    const { data } = await this.withFallback((client) =>
      client.get(`/cost/assembly/${encodeURIComponent(assemblyId)}`, { params: { strategy } })
    );
    return data;
  }

  /** Get computed costs for all assemblies. */
  async getAllAssemblyCosts(strategy: string = 'average'): Promise<AllAssemblyCostsResponse> {
    const { data } = await this.withFallback((client) =>
      client.get('/cost/assemblies', { params: { strategy } })
    );
    return data;
  }

  // ===========================================================================
  // TOPOLOGY BOM COMPARE (scenarios A / B / C1 / C2)
  // ===========================================================================

  /** Default parameters + scenario labels for the topology compare tool. */
  async getTopologyCompareDefaults(): Promise<TopologyCompareDefaultsResponse> {
    const { data } = await this.withFallback((client) =>
      client.get('/topology-compare/defaults')
    );
    return data;
  }

  /** Start a topology comparison background job over one or more projects. */
  async startTopologyCompareRun(
    projects: string[],
    params: TopologyCompareParams,
  ): Promise<{ job_id: string; status: string }> {
    const { data } = await this.withFallback((client) =>
      client.post('/topology-compare/run', { projects, params })
    );
    return data;
  }

  /** Poll a topology comparison job for progress / results. */
  async getTopologyCompareJob(jobId: string): Promise<TopologyCompareJobResponse> {
    const { data } = await this.withFallback((client) =>
      client.get(`/topology-compare/jobs/${encodeURIComponent(jobId)}`)
    );
    return data;
  }

  /** Pre-run health check of the 8760 amps dependency per project. */
  async getTopologyCompareAmpsStatus(projects: string[]): Promise<{ statuses: TopologyAmpsStatus[] }> {
    const { data } = await this.withFallback((client) =>
      client.get('/topology-compare/amps-status', { params: { projects: projects.join(',') } })
    );
    return data;
  }

  /** Download the full comparison report (HTML, XLSX tables, or electrical CSV). */
  async downloadTopologyCompareReport(
    jobId: string,
    format: 'html' | 'xlsx' | 'electrical_csv',
    lang: 'en' | 'fr' = 'en',
  ): Promise<Blob> {
    const { data } = await this.withFallback((client) =>
      client.get(`/topology-compare/jobs/${encodeURIComponent(jobId)}/report`, {
        params: { format, lang },
        responseType: 'blob',
        timeout: 300000,
      })
    );
    return data;
  }

  /** Save imputed transformer placements back to the project as a new version. */
  async applyTopologyCompareTransformers(
    project: string,
    placements: TopologyImputedTransformer[],
  ): Promise<{ project: string; version_id: string; transformer_count: number }> {
    const { data } = await this.withFallback((client) =>
      client.post('/topology-compare/apply-transformers', { project, placements }, { timeout: 120000 })
    );
    return data;
  }

  /** Fork a scenario into a branched, editable project snapshot (remediate topology, then re-compare). */
  async forkTopologyScenario(
    project: string,
    scenario: string,
    params: Record<string, unknown>,
  ): Promise<TopologyForkResult> {
    const { data } = await this.withFallback((client) =>
      client.post('/topology-compare/fork-scenario', { project, scenario, params }, { timeout: 300000 })
    );
    return data;
  }

  /** List saved (persisted) comparison runs, newest first. */
  async listTopologyCompareRuns(): Promise<{ runs: TopologySavedRunMeta[] }> {
    const { data } = await this.withFallback((client) =>
      client.get('/topology-compare/runs')
    );
    return data;
  }

  /** Load a saved comparison run's full result (map payload stripped). */
  async getTopologyCompareRun(
    runId: string,
  ): Promise<{ meta: TopologySavedRunMeta; result: TopologyCompareResult }> {
    const { data } = await this.withFallback((client) =>
      client.get(`/topology-compare/runs/${encodeURIComponent(runId)}`, { timeout: 120000 })
    );
    return data;
  }

  /** Delete a saved comparison run. */
  async deleteTopologyCompareRun(runId: string): Promise<{ deleted: string }> {
    const { data } = await this.withFallback((client) =>
      client.delete(`/topology-compare/runs/${encodeURIComponent(runId)}`)
    );
    return data;
  }

  /** Download the report of a saved run (works after navigation / restarts). */
  async downloadTopologyCompareRunReport(
    runId: string,
    format: 'html' | 'xlsx' | 'electrical_csv',
  ): Promise<Blob> {
    const { data } = await this.withFallback((client) =>
      client.get(`/topology-compare/runs/${encodeURIComponent(runId)}/report`, {
        params: { format },
        responseType: 'blob',
        timeout: 300000,
      })
    );
    return data;
  }

  /** Launch a uGridNET design constrained to a scenario's MV topology (compliant redesign). */
  async startUgridnetRedesign(
    project: string,
    scenario: string,
    params: Record<string, unknown>,
  ): Promise<TopologyRedesignStart> {
    const { data } = await this.withFallback((client) =>
      client.post('/topology-compare/ugridnet-redesign', { project, scenario, params }, { timeout: 120000 })
    );
    return data;
  }

  /** Save the chosen finished designs and store the comparison on the parent. */
  async saveUgridnetComparison(
    project: string,
    scenarios: Array<{ scenario: string; job_id: string; save: boolean; canonical: boolean }>,
    params: Record<string, unknown>,
  ): Promise<UgridnetComparisonRecord> {
    const { data } = await this.withFallback((client) =>
      client.post(
        '/topology-compare/ugridnet-comparison',
        { project, scenarios, params },
        { timeout: 300000 },
      )
    );
    return data;
  }

  async getUgridnetComparison(project: string, comparisonId: string): Promise<UgridnetComparisonRecord> {
    const { data } = await this.withFallback((client) =>
      client.get(`/topology-compare/comparisons/${encodeURIComponent(project)}/${encodeURIComponent(comparisonId)}`)
    );
    return data;
  }

  /** All-LV metrics for a finished D1/D2 job, without writing a project. */
  async previewUgridnetRedesign(
    project: string,
    scenario: string,
    jobId: string,
    params: Record<string, unknown>,
  ): Promise<{
    scenario: string;
    all_lv: boolean;
    feasible: boolean;
    min_voltage_v: number | null;
    num_transformers: number | null;
    transformer_kva: number[];
    design_cost_usd: number | null;
    reason_codes: string[];
    design_message?: string | null;
  }> {
    const { data } = await this.withFallback((client) =>
      client.post(
        '/topology-compare/ugridnet-redesign/preview',
        { project, scenario, job_id: jobId, params },
        { timeout: 180000 },
      )
    );
    return data;
  }

  async applyUgridnetRedesign(
    project: string,
    scenario: string,
    jobId: string,
    params: Record<string, unknown>,
  ): Promise<TopologyRedesignResult> {
    const { data } = await this.withFallback((client) =>
      client.post(
        '/topology-compare/ugridnet-redesign/apply',
        { project, scenario, job_id: jobId, params },
        { timeout: 300000 },
      )
    );
    return data;
  }

  // ===========================================================================
  // PV MODEL (De Soto yield + module library + datasheet ingestion)
  // ===========================================================================

  async listPvModules(): Promise<{ modules: PvModuleRecord[] }> {
    const { data } = await this.withFallback((client) => client.get('/pv/modules'));
    return data;
  }

  async savePvModule(fields: PvModuleSaveRequest): Promise<{ saved: boolean; module: PvModuleRecord }> {
    const { data } = await this.withFallback((client) =>
      client.post('/pv/modules', fields, { timeout: 60000 })
    );
    return data;
  }

  async confirmPvModuleSeriesFuse(
    moduleId: string,
    maxSeriesFuseA: number,
  ): Promise<{ saved: boolean; module: PvModuleRecord }> {
    const { data } = await this.withFallback((client) =>
      client.post(
        `/pv/modules/${encodeURIComponent(moduleId)}/confirm-series-fuse`,
        { max_series_fuse_a: maxSeriesFuseA },
      ),
    );
    return data;
  }

  async deletePvModule(moduleId: string): Promise<{ deleted: boolean; id: string }> {
    const { data } = await this.withFallback((client) =>
      client.delete(`/pv/modules/${encodeURIComponent(moduleId)}`)
    );
    return data;
  }

  async searchCecModules(q: string, limit = 25): Promise<{ query: string; results: CecSearchHit[] }> {
    const { data } = await this.withFallback((client) =>
      client.get('/pv/cec/search', { params: { q, limit } })
    );
    return data;
  }

  async importCecModule(name: string): Promise<{ saved: boolean; module: PvModuleRecord }> {
    const { data } = await this.withFallback((client) =>
      client.post('/pv/modules/import-cec', { name }, { timeout: 60000 })
    );
    return data;
  }

  /** Upload a datasheet PDF and run hybrid extraction (text/OCR/optional LLM). */
  async extractPvDatasheet(file: File, targetPmpW?: number): Promise<PvDatasheetExtraction> {
    const form = new FormData();
    form.append('file', file);
    if (targetPmpW != null && Number.isFinite(targetPmpW)) {
      form.append('target_pmp_w', String(targetPmpW));
    }
    const { data } = await this.withFallback((client) =>
      client.post('/pv/datasheet/extract', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      })
    );
    return data;
  }

  /** Run the hourly De Soto yield simulation. */
  async simulatePvYield(req: PvSimulateRequest): Promise<PvYieldResult> {
    const { data } = await this.withFallback((client) =>
      client.post('/pv/simulate', req, { timeout: 180000 })
    );
    return data;
  }

  /** Compute and store a far + near shading run for a project (edit_plans). */
  async computePvShading(projectId: string, generationId?: string): Promise<{ run_id: string; result: PvShadingRun }> {
    const { data } = await this.withFallback((client) =>
      client.post('/pv/shading', { projectId, generationId: generationId || undefined }, { timeout: 300000 })
    );
    return data;
  }

  /** Latest stored shading run, or null when none exists. */
  async getPvShading(projectId: string, generationId?: string): Promise<PvShadingRun | null> {
    try {
      const { data } = await this.withFallback((client) =>
        client.get('/pv/shading', { params: { projectId, generationId: generationId || undefined } })
      );
      return data;
    } catch (e: any) {
      if (e?.response?.status === 404) return null;
      throw e;
    }
  }

  /**
   * Generate a dated EYA-style yield report (PDF / XLSX / LaTeX-source zip).
   * Provide `req.plant` to add the installed-DC / MEC export-cap + inverter
   * sizing analysis; omit it for a basic per-kWp yield report.
   */
  async generatePvReport(
    req: PvReportRequest,
    format: PvReportFormat = 'pdf',
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await this.withFallback<AxiosResponse<Blob>>((client) =>
      client.post('/pv/report', req, {
        params: { format },
        responseType: 'blob',
        timeout: 300000,
      })
    );
    const cd = String(response.headers?.['content-disposition'] || '');
    const m = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const ext = format === 'latex' ? 'zip' : format;
    const fname = decodeURIComponent(
      (m?.[1] || m?.[2] || `pv_yield_report_${Date.now()}.${ext}`).trim()
    );
    return { blob: response.data, filename: fname };
  }

  // ===========================================================================
  // COMPONENT LIBRARY (inverters + batteries)
  // ===========================================================================

  /** Upload a datasheet PDF and run hybrid extraction for any component type. */
  async extractComponentDatasheet(
    file: File,
    componentType: 'pv' | 'inverter' | 'battery',
    target?: number,
  ): Promise<PvDatasheetExtraction> {
    const form = new FormData();
    form.append('file', file);
    form.append('component_type', componentType);
    if (target != null && Number.isFinite(target)) {
      form.append('target', String(target));
    }
    const { data } = await this.withFallback((client) =>
      client.post('/pv/datasheet/extract', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      })
    );
    return data;
  }

  async listInverters(): Promise<{ inverters: InverterRecord[] }> {
    const { data } = await this.withFallback((client) => client.get('/components/inverters'));
    return data;
  }

  async saveInverter(fields: InverterSaveRequest): Promise<{ saved: boolean; component: InverterRecord }> {
    const { data } = await this.withFallback((client) =>
      client.post('/components/inverters', fields, { timeout: 60000 })
    );
    return data;
  }

  async deleteInverter(id: string): Promise<{ deleted: boolean; id: string }> {
    const { data } = await this.withFallback((client) =>
      client.delete(`/components/inverters/${encodeURIComponent(id)}`)
    );
    return data;
  }

  async listBatteries(): Promise<{ batteries: BatteryRecord[] }> {
    const { data } = await this.withFallback((client) => client.get('/components/batteries'));
    return data;
  }

  async saveBattery(fields: BatterySaveRequest): Promise<{ saved: boolean; component: BatteryRecord }> {
    const { data } = await this.withFallback((client) =>
      client.post('/components/batteries', fields, { timeout: 60000 })
    );
    return data;
  }

  async deleteBattery(id: string): Promise<{ deleted: boolean; id: string }> {
    const { data } = await this.withFallback((client) =>
      client.delete(`/components/batteries/${encodeURIComponent(id)}`)
    );
    return data;
  }
}

// =============================================================================
// O&M NOTIFICATION TYPES
// =============================================================================

export interface OMNotificationContact {
  contact_id: string;
  name: string;
  role: string;
  site_ids: string[];
  email: string;
  phone: string;
  channels: { email: boolean; sms: boolean; whatsapp: boolean };
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  digest_mode: boolean;
  escalation_override: boolean;
  created_at: string;
  updated_at: string;
}

export interface OMNotificationRule {
  rule_id: string;
  event: string;
  conditions: {
    priority: string[];
    site_id: string[];
    ticket_type: string[];
    category_id: string[];
    outsourced: boolean | null;
  };
  recipients: {
    roles: string[];
    contacts: string[];
  };
  channels: string[];
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// O&M TICKET TYPE
// =============================================================================

export interface OMTicketComment {
  id: string;
  user: string;
  text: string;
  created_at: string;
}

export interface OMTicket {
  ticket_id: string;
  site_id: string;
  element_type: string;
  element_id: string;
  customer_id: string;
  equipment_category: string;
  equipment_model: string;
  ticket_type: string;
  category_id: string;
  root_cause_category: string;
  status: string;
  priority: string;
  escalated: boolean;
  escalation_reason: string;
  outsourced: boolean;
  outsource_cost: number;
  downtime_hours: number;
  customers_affected: number;
  reported_at: string;
  triaged_at: string;
  assigned_at: string;
  diagnosed_at: string;
  resolved_at: string;
  closed_at: string;
  due_date: string;
  fault_description: string;
  services_affected: string;
  troubleshooting_steps: string;
  cause_of_fault: string;
  preventive_action: string;
  assigned_to: string;
  reported_by: string;
  comment_thread: OMTicketComment[];
  parts_used: Array<{ part_name: string; quantity: number; unit_cost: number }>;
  labor_hours: number;
  warranty_status: string;
  related_tickets: string[];
  response_time_hours: number | null;
  resolution_time_hours: number | null;
  sla_met: boolean | null;
  history: Array<{
    timestamp: string;
    action: string;
    user: string;
    notes: string;
  }>;
}

export interface OMTicketCategory {
  id: string;
  label_name: string;
  label_color: string;
  outsource: boolean;
}

export interface OMReport {
  sla_compliance: Record<string, { total: number; met: number; breached: number; pending: number }>;
  mttr_by_category: Record<string, { count: number; avg_hours: number; min_hours: number; max_hours: number }>;
  mttr_by_root_cause: Record<string, { count: number; avg_hours: number }>;
  cost_summary: {
    total_outsource_cost: number;
    total_parts_cost: number;
    total_labor_hours: number;
    outsourced_tickets: number;
  };
  monthly_volume: Record<string, number>;
  monthly_resolved: Record<string, number>;
  repeat_elements: Array<{ element_id: string; ticket_count: number }>;
  total_tickets: number;
}

export interface OMFinancialMetrics {
  from_cache: boolean;
  computed_at?: string;
  cached_at?: string;
  error?: string;
  arpu: Array<{
    quarter: string;
    total_revenue: number;
    active_customers: number;
    arpu: number;
    per_site: Record<string, { name: string; revenue: number }>;
  }>;
  revenue: Array<Record<string, any>>; // quarter + site_code keys
  consumption: Array<Record<string, any>>; // quarter + site_code keys
  growth: Array<{
    quarter: string;
    new_customers: number;
    cumulative: number;
  }>;
  cumulative: Array<{
    quarter: string;
    kwh: number;
    lsl: number;
    cumulative_kwh: number;
    cumulative_lsl: number;
  }>;
  avg_consumption: Array<{
    quarter: string;
    customers: number;
    total_kwh: number;
    total_lsl: number;
    avg_daily_kwh_per_customer: number;
    avg_daily_lsl_per_customer: number;
  }>;
  site_codes: string[];
  site_names: Record<string, string>;
  customer_summary: {
    total: number;
    active: number;
    new: number;
  };
}

export interface OMNotificationLogEntry {
  log_id: string;
  timestamp: string;
  event: string;
  channel: string;
  recipient_name: string;
  recipient_address: string;
  ticket_id: string;
  status: string;
  error: string;
}

export interface OMMaintenanceTemplate {
  template_id: string;
  name: string;
  description: string;
  site_ids: string[];
  equipment_category: string;
  element_type: string;
  ticket_type: string;
  priority: string;
  frequency: string;
  estimated_labor_hours: number;
  checklist: string[];
  parts_needed: Array<{ part_name: string; quantity: number; unit_cost: number }>;
  assigned_role: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  last_generated_at: string | null;
  tickets_generated: number;
}

export interface OMRecurrenceAlert {
  element_id: string;
  element_type: string;
  site_ids: string[];
  ticket_count: number;
  open_tickets: number;
  categories: string[];
  root_causes: Record<string, number>;
  latest_ticket_id: string;
  latest_reported_at: string;
  ticket_ids: string[];
}

export interface OMRecurrenceAlertResponse {
  alerts: OMRecurrenceAlert[];
  total_flagged_elements: number;
  threshold: number;
  months: number;
  cutoff_date: string;
}

export interface OMTechnician {
  technician_id: string;
  name: string;
  employee_id: string;
  role: string;
  specializations: string[];
  phone: string;
  email: string;
  site_ids: string[];
  available: boolean;
  notes: string;
  created_at: string;
  updated_at: string;
  tickets_assigned: number;
  tickets_resolved: number;
}

export interface OMPayoutReport {
  total_tickets: number;
  total_outsource_cost: number;
  total_parts_cost: number;
  total_labor_hours: number;
  grand_total: number;
  by_site: Record<string, { count: number; cost: number; parts_cost: number; labor_hours: number }>;
  by_category: Record<string, { count: number; cost: number }>;
  by_month: Record<string, { count: number; cost: number }>;
  line_items: Array<{
    ticket_id: string;
    site_id: string;
    reported_at: string;
    resolved_at: string;
    equipment_category: string;
    fault_description: string;
    outsource_cost: number;
    parts_cost: number;
    labor_hours: number;
    status: string;
    priority: string;
  }>;
  period_start: string | null;
  period_end: string | null;
}

// =============================================================================
// ANALYTICS TYPES
// =============================================================================

export interface AnalyticsProjectInfo {
  code: string;
  name: string;
  portfolio: string;
  country: string;
  version_count: number;
  archived?: boolean;
}

export interface AnalyticsFiltersResponse {
  projects: AnalyticsProjectInfo[];
  portfolios: string[];
  countries: string[];
}

export interface TimeSeriesDataPoint {
  date: string;
  cumulative: number;
  total?: number;  // Total designed count (added for progress tracking)
  project: string;
  version_id: string;
}

export interface AnalyticsTimeSeriesResponse {
  time_series: {
    excavation: TimeSeriesDataPoint[];
    poles: TimeSeriesDataPoint[];
    lines_km: TimeSeriesDataPoint[];
    connections: TimeSeriesDataPoint[];
  };
}

export interface BoxPlotStats {
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  outliers: number[];
  raw_data: number[];
}

export interface AnalyticsCompletionRatesResponse {
  completion_rates: {
    excavation_per_day: BoxPlotStats;
    poles_per_day: BoxPlotStats;
    lines_km_per_day: BoxPlotStats;
    connections_per_day: BoxPlotStats;
  };
}

// mv_km / lv_km are network conductor only and drive the PREDICT BOM split, which
// is why service drops are reported separately in drop_km rather than folded into
// LV. The *_conductor_km fields are the all-in totals (network + drops) used for
// lender milestone reporting, where "conductor placed" is not voltage-qualified.
// excluded_spans / excluded_km report records rejected as corrupt geometry, which in
// practice means a node whose coordinates are in the wrong projection; a non-zero
// count means the site's length figures are understated and the node needs repair.
export interface AnalyticsAggregateStatsMetrics {
  total_poles: number;
  planted_poles: number;
  total_connections: number;
  energized_connections: number;
  mv_km: number;
  lv_km: number;
  energized_mv_km: number;
  energized_lv_km: number;
  strung_line_km: number;
  strung_mv_km: number;
  strung_lv_km: number;
  drop_km: number;
  strung_drop_km: number;
  energized_drop_km: number;
  total_conductor_km: number;
  strung_conductor_km: number;
  energized_conductor_km: number;
  service_drops: number;
  excluded_spans: number;
  excluded_km: number;
  transformers: number;
  generations: number;
}

export interface AnalyticsAggregateStatsSite extends AnalyticsAggregateStatsMetrics {
  project: string;
  name: string;
  latest_version: string | null;
  error?: string;
}

export interface AnalyticsAggregateStatsResponse {
  aggregate: AnalyticsAggregateStatsMetrics & { site_count: number };
  sites: AnalyticsAggregateStatsSite[];
}

// =============================================================================
// COST TRACEABILITY TYPES
// =============================================================================

export interface CostPartEntry {
  id: string;
  name: string | null;
  category: string | null;
  unit: string | null;
  has_cost_data: boolean;
  cost_record_count: number;
}

export interface CostPartsResponse {
  total_parts: number;
  parts_with_costs: number;
  parts: CostPartEntry[];
}

export interface CostRecordSummary {
  id: string;
  unit_cost_usd: number;
  unit_cost: number | null;
  currency: string | null;
  country: string | null;
  vendor: string | null;
  vendor_origin: string | null;
  date: string | null;
  source_type: string;
  source_id: string | null;
  incoterm: string | null;
  quantity: number | null;
  description: string | null;
  mapping_confidence: number | null;
  supply_country: string | null;
}

export interface PartPriceResponse {
  part_id: string;
  price_usd: number | null;
  strategy: string;
  datapoints: number;
  datapoints_used?: number;
  cost_range: {
    min_usd: number;
    max_usd: number;
    median_usd: number;
  } | null;
  records_summary: CostRecordSummary[];
  message?: string;
}

export interface CostSourceGroup {
  source_id: string;
  source_type: string;
  source_date: string | null;
  pr_system_link: string | null;
  vendors: string[];
  countries: string[];
  record_count: number;
  cost_range_usd: { min: number; max: number };
  records: CostRecordSummary[];
}

export interface PartSourcesResponse {
  part_id: string;
  part_name: string | null;
  total_sources: number;
  sources: CostSourceGroup[];
}

export interface CostSupplierDetail {
  vendor_name: string;
  transaction_count: number;
  parts_supplied: string[];
  countries: string[];
  date_range: { earliest: string; latest: string } | null;
  total_value_usd: number;
  records: CostRecordSummary[];
}

export interface CostSuppliersResponse {
  total_vendors: number;
  vendors: CostSupplierDetail[];
}

export interface CostSummaryResponse {
  total_records: number;
  total_parts: number;
  parts_with_costs: number;
  coverage_pct: number;
  records_by_source_type: Record<string, number>;
  records_by_country: Record<string, number>;
  top_vendors: Array<{ vendor: string; count: number }>;
}

export interface BOMCostLineItem {
  part_id: string;
  name: string | null;
  qty: number;
  unit_price_usd: number | null;
  line_cost_usd: number | null;
  datapoints: number;
  strategy: string;
  has_cost_data: boolean;
}

export interface BOMCostResponse {
  total_cost_usd: number | null;
  items_costed: number;
  items_total: number;
  coverage_pct: number;
  strategy: string;
  line_items: BOMCostLineItem[];
}

export interface AssemblyCostItem {
  part_id: string;
  quantity: number;
  notes: string | null;
  unit_price_usd: number | null;
  line_cost_usd: number | null;
  datapoints: number;
}

export interface AssemblyCostResponse {
  assembly_id: string;
  assembly_name: string | null;
  assembly_type: string | null;
  total_cost_usd: number | null;
  parts_total: number;
  parts_costed: number;
  parts_missing: number;
  coverage_pct: number;
  strategy: string;
  items: AssemblyCostItem[];
  error?: string;
}

export interface AllAssemblyCostsResponse {
  total_assemblies: number;
  fully_costed: number;
  partially_costed: number;
  not_costed: number;
  strategy: string;
  assemblies: AssemblyCostResponse[];
}

// =============================================================================
// TOPOLOGY BOM COMPARE TYPES
// =============================================================================

export interface TopologyCompareParams {
  target_min_voltage: number;
  max_cable_size_mm2?: number;
  fos: number;
  nominal_voltage: number;
  converter_cost_usd: number;
  /** Share of SME connections that need a converter on C1/C2. 0–100. School and clinic are excluded. */
  shop_converter_pct?: number;
  power_factor: number;
  amps_voltage_v: number;
  amps_basis: 'system_peak' | 'type_peak';
  use_8760: boolean;
  /** manual | system_peak | type_peak | system_peak_sf. Absent on older saved runs. */
  amps_source?: 'manual' | 'system_peak' | 'type_peak' | 'system_peak_sf' | null;
  /** Applied only when amps_source is system_peak_sf. Default 1.2. */
  amps_safety_factor?: number;
  single_phase_types: string[];
  manual_type_amps: Record<string, number>;
  default_amps: number;
  abc_backbone_span_m: number;
  lv_backbone_max_parallel: number;
  phase_balance_enabled: boolean;
  inverter_cost_per_kw: number;
  stepup_cost_per_kva: number;
  mv_conductor_usd_per_m: number;
  inverter_lv_voltage_ll: number;
  phase_balance_max_units: number;
  /** Maximum MV pole span (m) by topology. Required before a fresh design of that topology. */
  mv_span_m_by_topology?: Record<string, number>;
}

// Phase-balance TEA (MV lateral phasing vs inverter capacity stranding).
export interface TopologyPhaseBalanceUnit {
  root: string;
  load_amps: number;
  load_kw: number;
  length_m: number;
  assigned: string; // 'ABC' (3-wire) or a phase pair 'AB' | 'BC' | 'CA' (2-wire)
}

export interface TopologyPhaseBalancePlan {
  allocation: string[];
  phase_current: Record<string, number>;
  i_max: number;
  imbalance_pct: number;
  required_ac_kw: number;
  min_ac_kw: number;
  stranded_kw: number;
  stepup_kva?: number;
  inverter_cost_usd: number;
  stepup_cost_usd?: number;
  conductor_cost_usd: number;
  total_usd: number;
}

export interface TopologyPhaseBalance {
  units: TopologyPhaseBalanceUnit[];
  background_kw: number;
  total_kw: number;
  optimal: TopologyPhaseBalancePlan;
  reference_round_robin: TopologyPhaseBalancePlan;
  reference_all_3ph: TopologyPhaseBalancePlan;
  enumerated: boolean;
  params: {
    inverter_cost_per_kw: number;
    stepup_cost_per_kva?: number;
    mv_conductor_usd_per_m: number;
    inverter_lv_voltage_ll: number;
  };
}

export interface TopologyCompareDefaultsResponse {
  params: TopologyCompareParams;
  scenario_labels: Record<string, string>;
}

export interface TopologyScenarioBreakdown {
  mv_wire: number;
  lv_wire: number;
  drop_wire: number;
  transformers: number;
  converters: number;
  backbone_poles: number;
  other: number;
}

export interface TopologyBackboneInfo {
  circuits_max: number;
  min_voltage: number | null;
  feasible: boolean;
  intermediate_poles: number;
  intermediate_pole_unit_usd: number;
  intermediate_pole_cost_usd: number;
  span_m: number;
}

export interface TopologyScenarioResult {
  label: string;
  total_usd: number;
  total_usd_with_converters: number;
  converter_count: number;
  converter_unit_cost_usd: number;
  converter_cost_usd: number;
  breakdown: TopologyScenarioBreakdown;
  mv_conductor_count: number;
  transformer_phase: string | null;
  transformer_count: number;
  transformers_imputed: boolean;
  backbone?: TopologyBackboneInfo;
  achieved_min_voltage: number | null;
  voltage_threshold: number;
  feasible: boolean;
  sizing_iterations: number;
  wire_meters_by_part: Record<string, number>;
  warnings: string[];
  diagnostics?: {
    status?: string;
    failure_stages?: string[];
    reason_codes?: string[];
    primary_stage?: string | null;
    voltage_margin_v?: number | null;
    distribution?: { voltage_pass?: boolean; ampacity_pass?: boolean; reason_codes?: string[] };
    backbone?: { voltage_pass?: boolean; ampacity_pass?: boolean; empty_backbone?: boolean; reason_codes?: string[] };
  };
}

export interface TopologyImputedTransformer {
  pole_id: string;
  attach_mv_pole: string | null;
  gps_x: number | null;
  gps_y: number | null;
  load_amps: number;
  kva_raw: number;
  kva: number;
}

export type TopologyAmpsStatusCode = 'ok' | 'stale_run' | 'legacy_run' | 'no_run' | 'unknown_project';

export interface TopologyAmpsStatus {
  project: string;
  status: TopologyAmpsStatusCode;
  run_id: string | null;
  run_timestamp: string | null;
  latest_version: string | null;
}

export interface TopologyProjectResult {
  project: string;
  name: string;
  code: string;
  portfolio: string | null;
  version_id: string | null;
  amps_source: string;
  amps_status: TopologyAmpsStatus;
  type_amps: Record<string, number>;
  counts: {
    poles: number;
    lv_edges: number;
    connections: number;
    non_single_phase_connections: number;
    shop_connections?: number;
    shop_converter_pct?: number;
    flagged_lines: number;
  };
  transformers_imputed: boolean;
  imputed_transformers: TopologyImputedTransformer[];
  warnings: string[];
  scenarios: Record<string, TopologyScenarioResult>;
  phase_balance?: TopologyPhaseBalance | null;
  transformer_consolidation?: {
    sweep: Array<{ count: number; total_usd: number }>;
    merges: number;
    fixed_cost_usd: number;
    max_span_m: number;
    optimal_count: number;
    initial_count: number;
    link_cost_usd: number;
  } | null;
  type_coverage?: {
    total_connections: number;
    covered_connections: number;
    empty_type_connections: number;
    uncovered_type_connections: number;
    uncovered_types: Record<string, number>;
    fallback_amps: number;
  } | null;
}

export interface TopologyPortfolioScenarioTotals {
  total_usd: number;
  total_usd_with_converters: number;
  converter_cost_usd: number;
  breakdown: TopologyScenarioBreakdown;
  infeasible_projects: string[];
}

export interface TopologyCompareResult {
  params: TopologyCompareParams;
  projects: TopologyProjectResult[];
  errors: Array<{ project: string; error: string }>;
  portfolio: Record<string, TopologyPortfolioScenarioTotals>;
}

export interface TopologyCompareJobResponse {
  job_id: string;
  status: 'running' | 'completed' | 'failed';
  progress: { current: number; total: number; project: string | null } | null;
  error?: string | null;
  result?: TopologyCompareResult | null;
}

export interface TopologyForkResult {
  project: string;
  name: string;
  version_id: string;
  scenario: string;
  scenario_label: string;
  feasible: boolean;
  min_voltage: number | null;
  voltage_threshold: number | null;
  transformers_imputed: boolean;
  forked_from: { project: string; version_id: string | null };
}

export interface TopologySavedRunMeta {
  run_id: string;
  label: string;
  created_at: string;
  created_by: string | null;
  projects: string[];
  project_count: number;
  error_count: number;
}

export interface UgridnetComparisonScenario {
  scenario: string;
  scenario_label: string;
  min_voltage_v: number | null;
  headroom_v?: number | null;
  target_v?: number | null;
  pole_count?: number | null;
  pole_cost_usd?: number | null;
  network_usd: number | null;
  converter_count: number;
  converter_usd: number;
  total_usd: number | null;
  lv_cable_id?: string | null;
  num_transformers?: number | null;
  transformer_kva?: number[];
  feasible?: boolean;
  saved_project: string | null;
  saved_name?: string | null;
  version_id: string | null;
  canonical: boolean;
}

export interface UgridnetComparisonRecord {
  id: string;
  parent: string;
  created_at: string;
  created_by?: string | null;
  canonical_project: string | null;
  amps_source?: string;
  type_amps?: Record<string, number>;
  scenarios: UgridnetComparisonScenario[];
}

export interface TopologyRedesignStart {
  job_id: string;
  project: string;
  scenario: string;
  mv_topology: string;
  connection_count: number;
  generation: { lat: number; lon: number };
  min_lv_voltage: number;
  hemisphere: string;
  amps_source: string;
  amps_warnings?: string[];
  type_amps?: Record<string, number>;
  backbone_conversion?: boolean;
}

export interface TopologyRedesignResult {
  project: string;
  name: string;
  version_id: string;
  scenario: string;
  scenario_label: string;
  design_status: string;
  design_message: string | null;
  feasible: boolean;
  min_voltage_v: number | null;
  num_transformers: number | null;
  design_cost_usd: number | null;
  transformer_kva?: number[];
  mv_km?: number | null;
  pole_count?: number | null;
  mv_span_m?: number | null;
  lv_phase_family?: string | null;
  binding?: {
    target_v?: number;
    min_voltage_v?: number;
    headroom_v?: number;
    transformer_count?: number;
    k_star?: number;
    transformer_kva?: number[];
    rejected_neighbor?: {
      num_transformers?: number;
      min_voltage_v?: number | null;
      passed?: boolean;
      limit?: 'voltage' | 'ampacity' | null;
    } | null;
    lv_cable_id?: string | null;
    lv_cable_mm2?: number | null;
    next_smaller_cable_id?: string | null;
    next_smaller_holds?: boolean | null;
    layout_mode?: string | null;
    layout_stretch?: number | null;
    pole_count?: number | null;
    pole_cost_usd?: number | null;
    layout_neighbor?: {
      layout_mode?: string | null;
      min_voltage_v?: number | null;
      pole_count?: number | null;
      passed?: boolean;
    } | null;
  } | null;
  diagnostics?: Record<string, unknown> | null;
  warnings?: string[];
  forked_from: { project: string; method: string; job_id: string };
}

// =============================================================================
// PV MODEL TYPES (De Soto yield + module library + datasheet ingestion)
// =============================================================================

export interface PvModuleRecord {
  id: string;
  manufacturer: string;
  model: string;
  source: 'datasheet' | 'cec';
  technology?: string;
  stc: { pmp_w: number; voc_v: number; isc_a: number; vmp_v: number; imp_a: number };
  temp_coeff: {
    alpha_sc_pct_per_c: number | null;
    beta_voc_pct_per_c: number | null;
    gamma_pmp_pct_per_c: number | null;
  };
  cells_in_series: number;
  noct_c: number | null;
  bifacial: boolean;
  bifaciality: number | null;
  max_series_fuse_a?: number | null;
  max_series_fuse_confirmed?: boolean;
  max_series_fuse_source?: string;
  max_series_fuse_confirmed_at?: string | null;
  max_series_fuse_confirmed_by?: string | null;
  dimensions_mm?: { length: number | null; width: number | null } | null;
  desoto?: Record<string, number>;
  cec?: Record<string, number | null>;
  datasheet_file?: string | null;
  readonly?: boolean;
  notes?: string;
  created_at?: string;
  created_by?: string;
}

export interface PvModuleSaveRequest {
  manufacturer: string;
  model: string;
  id?: string;
  technology?: string;
  pmp_w?: number | null;
  voc_v: number;
  isc_a: number;
  vmp_v: number;
  imp_a: number;
  alpha_sc_pct_per_c: number;
  beta_voc_pct_per_c: number;
  gamma_pmp_pct_per_c?: number | null;
  max_series_fuse_a?: number | null;
  max_series_fuse_confirmed?: boolean;
  cells_in_series: number;
  noct_c?: number | null;
  bifacial?: boolean;
  bifaciality?: number | null;
  length_mm?: number | null;
  width_mm?: number | null;
  datasheet_file?: string | null;
  notes?: string;
}

export interface CecSearchHit {
  name: string;
  technology: string;
  stc_w: number | null;
  bifacial: boolean;
  cells_in_series: number;
}

// =============================================================================
// COMPONENT LIBRARY TYPES (inverters + batteries)
// =============================================================================

export interface InverterRecord {
  id: string;
  manufacturer: string;
  model: string;
  source?: string;
  ac_kw: number;
  ac_voltage_v?: number | null;
  phases?: number | null;
  max_dc_voltage_v: number;
  mppt_min_v?: number | null;
  mppt_max_v?: number | null;
  start_voltage_v?: number | null;
  num_mppt?: number | null;
  strings_per_mppt?: number | null;
  max_input_current_per_mppt_a?: number | null;
  max_isc_per_mppt_a?: number | null;
  max_pv_input_w?: number | null;
  battery_voltage_min_v?: number | null;
  battery_voltage_max_v?: number | null;
  max_battery_charge_a?: number | null;
  max_battery_discharge_a?: number | null;
  max_efficiency_pct?: number | null;
  datasheet_file?: string | null;
  readonly?: boolean;
  notes?: string;
  created_at?: string;
  created_by?: string;
  electrical_ready?: boolean;
  electrical_missing_fields?: string[];
  curated_fallback_fields?: string[];
  datasheet_url?: string | null;
  datasheet_rev?: string | null;
  tbc_fields?: string[] | null;
  install_status?: 'new' | 'existing';
  design_support?: 'design' | 'catalog_only';
  electrical_ready_reason?: string | null;
  aliases?: string[] | null;
  part_id?: string | null;
}

export interface GenerationElectricalDesign {
  schema_version: number;
  design_id: string;
  status: 'validated' | 'stale' | 'invalid';
  stale_reason?: string;
  created_at: string;
  layout_hash: string;
  module: {
    id: string;
    manufacturer: string;
    model: string;
    pmp_w: number;
    voc_v: number;
    vmp_v: number;
    isc_a: number;
    imp_a: number;
    source?: string;
    beta_vmp_pct_per_c?: number | null;
    max_series_fuse_a?: number | null;
    max_series_fuse_confirmed?: boolean;
    max_series_fuse_source?: string;
  };
  inverter: InverterRecord;
  assumptions: {
    min_design_temp_c: number;
    max_cell_temp_c: number;
    vmp_temperature_coefficient_pct_per_c: number;
    vmp_temperature_coefficient_source: string;
    isc_design_factor: number;
    required_string_fuse_a?: number | null;
    string_fuse_rule?: 'iec62548' | 'nec_1p56';
    strings_in_parallel_max?: number;
    string_fuse_required?: boolean;
    fuse_rating_if_fitted_a?: number | null;
    fit_string_fuses?: boolean;
    voltage_margin_pct?: number;
    string_length_policy?: 'fewest_strings' | 'array_aligned';
  };
  limits: {
    voc_cold_per_module_v: number;
    vmp_cold_per_module_v: number;
    vmp_hot_per_module_v: number;
    minimum_modules_per_string: number;
    maximum_modules_per_string: number;
    maximum_modules_by_voc: number;
    maximum_modules_by_mppt: number;
    parallel_strings_per_mppt: number;
  };
  array_inventory: Array<{
    array_id: string;
    array_name: string;
    modules_count: number;
  }>;
  strings: Array<{
    string_id: string;
    modules_count: number;
    segments: Array<{
      array_id: string;
      array_name: string;
      modules_count: number;
    }>;
    inverter_index: number;
    mppt_index: number;
    assignment_id: string;
    voc_cold_v: number;
    vmp_cold_v: number;
    vmp_hot_v: number;
    vmp_stc_v: number;
  }>;
  mppt_assignments: Array<{
    assignment_id: string;
    inverter_index: number;
    mppt_index: number;
    modules_per_string: number;
    string_ids: string[];
    parallel_strings: number;
    dc_power_kw: number;
    operating_current_a: number;
    design_short_circuit_current_a: number;
  }>;
  checks: Array<{ code: string; status: string; message: string }>;
  warnings: string[];
  summary: {
    total_modules: number;
    total_dc_kw: number;
    total_strings: number;
    inverter_count: number;
    mppt_used: number;
    string_lengths: number[];
  };
}

export type InverterSaveRequest = Omit<InverterRecord, 'id' | 'readonly' | 'source' | 'created_at' | 'created_by'> & {
  id?: string;
};

export interface BatteryRecord {
  id: string;
  manufacturer: string;
  model: string;
  source?: string;
  chemistry?: string | null;
  module_nominal_voltage_v: number;
  module_kwh: number;
  module_capacity_ah?: number | null;
  module_voltage_min_v?: number | null;
  module_voltage_max_v?: number | null;
  modules_per_rack_min: number;
  modules_per_rack_max: number;
  bmu_model?: string | null;
  bmus_per_rack?: number;
  max_racks_parallel?: number | null;
  max_charge_current_a?: number | null;
  max_discharge_current_a?: number | null;
  dod_pct?: number | null;
  datasheet_file?: string | null;
  readonly?: boolean;
  notes?: string;
  created_at?: string;
  created_by?: string;
  datasheet_url?: string | null;
  datasheet_rev?: string | null;
  tbc_fields?: string[] | null;
  install_status?: 'new' | 'existing';
  design_support?: 'design' | 'catalog_only';
  electrical_ready_reason?: string | null;
  aliases?: string[] | null;
  part_id?: string | null;
  series?: string | null;
}

export type BatterySaveRequest = Omit<BatteryRecord, 'id' | 'readonly' | 'source' | 'created_at' | 'created_by'> & {
  id?: string;
};

export interface PvDatasheetExtraction {
  fields: Record<string, number | boolean | string | null>;
  snippets: Record<string, string>;
  confidence: Record<string, 'high' | 'low'>;
  variants: number[];
  variant_index: number | null;
  conflicts: Record<string, { heuristic: unknown; llm: unknown }>;
  llm_used: boolean;
  text_chars: number;
  warnings: string[];
  datasheet_file: string;
  uploaded_filename: string;
}

export interface PvRackingParams {
  kind: 'fixed' | 'sat';
  tilt_deg?: number;
  azimuth_deg?: number;
  axis_azimuth_deg?: number;
  max_angle_deg?: number;
  backtrack?: boolean;
  gcr?: number;
  /** True when gcr/tilt come from a real layout: near shading is then modelled. */
  geometry_known?: boolean;
}

export interface PvBifacialParams {
  enabled: boolean;
  bifaciality?: number;
  albedo?: number;
  row_height_m?: number;
  collector_width_m?: number;
}

export interface PvLossParams {
  soiling?: number;
  shading?: number;
  mismatch?: number;
  wiring_dc?: number;
  connections?: number;
  lid?: number;
  nameplate?: number;
  availability?: number;
  wiring_ac?: number;
}

export interface PvSystemParams {
  dc_ac_ratio?: number;
  inverter_eta_nom?: number;
}

export interface PvSimulateRequest {
  latitude: number;
  longitude: number;
  module_id: string;
  tmy_hourly: Array<Record<string, unknown>>;
  racking?: PvRackingParams;
  bifacial?: PvBifacialParams;
  losses?: PvLossParams;
  system?: PvSystemParams;
  include_hourly?: boolean;
  /** Stored shading run (GET /pv/shading) and the stored TMY's metadata. */
  shading?: PvShadingRun | null;
  tmy_metadata?: Record<string, unknown> | null;
}

/** result.json of a stored shading run (datasets/shading/<run>/). */
export interface PvShadingRun {
  schema: string;
  run_id: string;
  computed_at: string;
  project: {
    project_id: string; generation_id: string | null; generation_id_requested?: string | null;
    project_version_id: string | null;
  };
  inputs?: { generation_source?: string; [key: string]: unknown };
  results: {
    annual: {
      unshaded_kwh_per_kwp: number; far_shaded_kwh_per_kwp: number; near_shaded_kwh_per_kwp: number;
      far_loss_pct: number; near_loss_pct: number; total_loss_pct: number;
    };
    /** geometry_status: geometry | no_generation_selected | generation_has_no_layout |
     *  layout_has_no_solar_array_rows | row_geometry_incomplete */
    near_shading: { mode: string; label: string; geometry_status?: string; generation_id?: string | null };
  };
  /** horizon_free | pvgis_horizon_applied | unknown (absent on runs stored before v1.89.4) */
  tmy_horizon_mode?: string;
  far_shading_applied?: boolean;
  far_shading_basis?: string;
  double_count_risk: boolean;
  verified?: boolean;
  [key: string]: unknown;
}

export type PvReportFormat = 'pdf' | 'xlsx' | 'latex';

export interface PvReportMeta {
  project?: string;
  doc_id?: string;
  author?: string;
  site?: string;
  altitude_m?: number;
  timezone?: string;
  degradation_pct_yr?: number;
  plant_life_years?: number;
  unc_resource?: number;
  unc_transposition?: number;
  unc_modelling?: number;
}

export interface PvPlantParams {
  dc_mw: number;
  mec_mw: number;
  inverter_model?: string;
  inverter_kw?: number;
  inverters_operational?: number;
  inverters_spare?: number;
  modules_per_string?: number;
}

export interface PvReportRequest extends PvSimulateRequest {
  report?: PvReportMeta;
  plant?: PvPlantParams;
}

export interface PvYieldResult {
  specific_yield_kwh_per_kwp: number;
  performance_ratio: number;
  poa_annual_kwh_m2: number;
  poa_effective_annual_kwh_m2: number;
  bifacial_rear_gain_pct: number;
  inverter_clipping_kwh_per_kwp: number;
  hours: number;
  monthly: Array<{ month: number; energy_kwh_per_kwp: number; poa_kwh_m2: number }>;
  loss_waterfall: Array<{ stage: string; kwh_per_kwp: number }>;
  hourly_ac_kw_per_kwp?: number[];
  config: Record<string, unknown>;
  warnings: string[];
  generated_at?: string;
}

export const apiService = new ApiService();
